import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import MediaAdminSection from "@/components/admin/MediaAdminSection";
import BodyDetectionSection from "@/components/admin/BodyDetectionSection";
import TaxReportSection from "@/components/admin/TaxReportSection";
import ContentModerationSection from "@/components/admin/ContentModerationSection";
import RescueKitsAdminSection from "@/components/admin/RescueKitsAdminSection";
// iter242 · Admin-Detektor für Paid-Media-Kopien (Variante B §17).
import AdminFingerprintPanel from "@/components/admin/AdminFingerprintPanel";
import RescueBagIntakePanel from "@/components/admin/RescueBagIntakePanel";
import ServiceFeesSection from "@/components/admin/ServiceFeesSection";
import UserFeeOverridesPanel from "@/components/admin/UserFeeOverridesPanel";
import AdminPermissionsPanel from "@/components/admin/AdminPermissionsPanel";
import CompanyAvatarUploader from "@/components/admin/CompanyAvatarUploader";
import AdminCreatorHelperPanel from "@/components/admin/AdminCreatorHelperPanel";
import GhostCheckSection from "@/components/admin/GhostCheckSection";
import DocumentTemplatesSection from "@/components/admin/DocumentTemplatesSection";
import BrandBarSettings from "@/components/admin/BrandBarSettings";
import { ORDER_STATUS_LABELS, orderStatusColor, orderStatusLabel, paymentStatusLabel, adminStatusLabel } from "@/components/admin/orderStatusUtils";
// iter162 · Artikel-/Beitrags-Verwaltung
import { ListingsAdminSection } from "@/pages/admin/ContentManagementSections";
import RoleChangeModal from "@/components/admin/RoleChangeModal";
import RichTextEditor from "@/components/common/RichTextEditor";
import StaticContentAdminSection from "@/components/admin/StaticContentAdminSection";
import NewsletterSection from "@/components/admin/NewsletterSection";
import AiLabSection from "@/components/admin/AiLabSection";
import { useAdminLangGuard } from "@/components/common/LanguageSwitcher";
import {
  Shield, Users, Settings, CreditCard, Building2, Mail, Truck, Database,
  Search, ChevronLeft, ChevronRight, LogIn, Filter, X, Check, Ban, UserCheck,
  Save, Eye, EyeOff, Plus, Trash2, BarChart3, FileText, Landmark, ScrollText,
  Flag, AlertTriangle, Send, MessageSquare, Package, ScanLine, Clock, Image as ImageIcon,
  Boxes, RefreshCw, ExternalLink, Warehouse, Camera, Wallet, Percent, Columns, Sparkles, BadgeCheck, Ghost, Wand2, Music2,
} from "lucide-react";

// ─── Sidebar Nav ────────────────────────────────────────
const NAV_SECTIONS = [
  { key: "dashboard", label: "Dashboard", icon: BarChart3 },
  { key: "users", label: "User-Verwaltung", icon: Users },
  { key: "listings_admin", label: "Artikel-Verwaltung", icon: Package },
  { key: "message_reports", label: "Gemeldete Nachrichten", icon: Flag },
  { key: "modul_d_policy", label: "Modul D · Policy", icon: Shield },
  { key: "hub_intake", label: "Hub-Eingang", icon: ScanLine },
  { key: "shipments", label: "Versand / Labels", icon: Truck },
  { key: "hub_queue", label: "Hub / Warehouse", icon: Database },
  { key: "physical_labels", label: "Eingesandte Labels", icon: FileText },
  { key: "orders_admin", label: "Bestellungen", icon: CreditCard },
  { key: "shipping_queue", label: "Lieferzeiten-Queue", icon: Clock },
  { key: "media_admin", label: "Medienverwaltung", icon: ImageIcon },
  { key: "ai_lab", label: "KI-Labor (Test)", icon: Sparkles },
  { key: "content_mod", label: "Content-Moderation", icon: Flag },
  { key: "rescue_kits", label: "Rescue-Kits", icon: Boxes },
  { key: "stx_shop_config", label: "STX Konfiguration", icon: Package },
  { key: "reports_moderation", label: "Meldungen (Moderation)", icon: Flag },
  { key: "platform_post", label: "Platform-Beitrag", icon: BadgeCheck, external: "/admin/platform-post" },
  { key: "static_content", label: "News · FAQ · Rechtliches", icon: ScrollText },
  { key: "newsletter", label: "Newsletter", icon: Send },
  { key: "inactivity", label: "Inaktivitäts-Sweep", icon: AlertTriangle },
  { key: "ghost_check", label: "Ghost-Check", icon: Ghost },
  { key: "avs_requests", label: "AVS-Anträge", icon: Shield },
  { key: "reconciliation", label: "Zahlungseingänge", icon: Landmark },
  { key: "payouts", label: "Gutschriften", icon: Wallet },
  { key: "tax_report", label: "Steuerrechtliche Auswertung", icon: FileText },
  { key: "audit", label: "Audit-Log", icon: ScrollText },
  { divider: true, label: "Einstellungen" },
  { key: "service_fees", label: "Servicegebühren", icon: Percent },
  { key: "company", label: "Unternehmen", icon: Building2 },
  { key: "hub_address", label: "Hubadresse", icon: Warehouse },
  { key: "maintenance", label: "Wartungsmodus", icon: AlertTriangle },
  { key: "payment_methods", label: "Zahlungsarten", icon: CreditCard },
  { key: "paypal", label: "PayPal", icon: CreditCard },
  { key: "bank", label: "Bankverbindung", icon: Building2 },
  { key: "smtp", label: "E-Mail / SMTP", icon: Mail },
  { key: "email_roles", label: "E-Mail-Rollen", icon: FileText },
  { key: "email_templates", label: "E-Mail-Vorlagen", icon: FileText },
  { key: "document_templates", label: "Dokumentenvorlagen", icon: ScrollText },
  { key: "shipping", label: "Versand / DHL", icon: Truck },
  { key: "finapi", label: "finAPI (Bank)", icon: Database },
  { key: "avs_provider", label: "AVS-Provider", icon: Shield },
  { key: "music_slot", label: "Musik-Slot", icon: Music2 },
  { key: "permissions", label: "Rollen & Rechte", icon: Shield },
  { key: "creator_helper", label: "Creator-Helper", icon: Sparkles },
];

const CAP_LABELS = {
  ST_USER: "ST User", ST_CREATOR: "Creator", MP_BUYER: "MP Buyer",
  MP_SELLER: "MP Seller", STX_BUYER: "STX Buyer", STX_SELLER: "STX Seller",
};
const CAP_LIST = Object.keys(CAP_LABELS);

export default function AdminDashboardPage() {
  useAdminLangGuard();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  // iter242 · Rückwärts-Kompat: Deep-Links aus AdminFingerprintPanel liefern
  // `?tab=orders` (Legacy). Wir mappen das auf die interne section-Konvention.
  const legacyTab = params.get("tab");
  const legacyTabMap = { orders: "orders_admin", users: "users",
                          listings: "listings_admin" };
  const section = params.get("section")
                  || legacyTabMap[legacyTab]
                  || "dashboard";

  const setSection = (s) => setParams({ section: s });

  useEffect(() => {
    if (authLoading) return;
    if (!user || user === false) { navigate("/login"); return; }
    if (!["owner", "admin"].includes(user.role)) { navigate("/"); return; }
  }, [user, authLoading, navigate]);

  if (authLoading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAFAFA] flex flex-col">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white shrink-0">
        <div className="flex items-center justify-between h-14 px-6">
          <div className="flex items-center gap-3">
            <Shield size={18} className="text-[#C0C0C0]" />
            <span className="font-bold text-sm tracking-tight">stilltouched Admin</span>
          </div>
          <button data-testid="admin-to-frontend" onClick={() => navigate("/")}
            className="text-xs text-[#888] hover:text-white transition-colors">Zum Frontend</button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-56 bg-white border-r border-[#E5E5E5] shrink-0 overflow-y-auto py-4">
          {NAV_SECTIONS.map((item, i) => {
            if (item.divider) return (
              <p key={i} className="px-4 pt-4 pb-1 text-[9px] font-bold text-[#999] uppercase tracking-[0.15em]">{item.label}</p>
            );
            const active = section === item.key;
            const onClick = item.external
              ? () => navigate(item.external)
              : () => setSection(item.key);
            return (
              <button key={item.key} data-testid={`admin-nav-${item.key}`} onClick={onClick}
                className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm text-left transition-colors ${active ? "bg-[#0A0A0A] text-white" : "text-[#666] hover:bg-[#F5F5F5]"}`}>
                <item.icon size={15} /> {item.label}
              </button>
            );
          })}
        </aside>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {section === "dashboard" && <DashboardSection />}
          {section === "users" && <UsersSection />}
          {section === "listings_admin" && <ListingsAdminSection />}
          {section === "message_reports" && <MessageReportsSection />}
          {section === "modul_d_policy" && <ModulDPolicySection />}
          {section === "hub_intake" && <HubIntakeSection />}
          {section === "shipments" && <ShipmentsSection />}
          {section === "hub_queue" && <HubQueueSection />}
          {section === "physical_labels" && <PhysicalLabelsSection />}
          {section === "orders_admin" && <OrdersAdminSection />}
          {section === "shipping_queue" && <ShippingQueueSection />}
          {section === "media_admin" && <MediaAdminSection />}
          {section === "ai_lab" && <AiLabSection />}
          {section === "static_content" && <StaticContentAdminSection />}
          {section === "newsletter" && <NewsletterSection />}
          {section === "body_detection" && <BodyDetectionSection />}
          {section === "content_mod" && <ContentModerationSection />}
          {section === "rescue_kits" && <RescueKitsAdminSection />}
          {section === "body_detection" && <BodyDetectionSection />}
          {section === "stx_shop_config" && <STXKonfigurationSection />}
          {section === "reports_moderation" && <ReportsModerationSection />}
          {section === "inactivity" && <InactivitySection />}
          {section === "ghost_check" && <GhostCheckSection />}
          {section === "avs_requests" && <AVSRequestsSection />}
          {section === "reconciliation" && <ReconciliationSection />}
          {section === "payouts" && <PayoutsSection />}
          {section === "audit" && <AuditSection />}
          {section === "service_fees" && <ServiceFeesSection />}
          {/* iter174c · Steuerrechtliche Auswertung */}
          {section === "tax_report" && <TaxReportSection />}
          {section === "company" && <SettingSection settingKey="company" />}
          {section === "hub_address" && <SettingSection settingKey="hub_address" />}
          {section === "maintenance" && <MaintenanceModeSection />}
          {section === "payment_methods" && <SettingSection settingKey="payment_methods" />}
          {section === "paypal" && <SettingSection settingKey="paypal" />}
          {section === "bank" && <SettingSection settingKey="bank" />}
          {section === "smtp" && <SMTPSection />}
          {section === "email_roles" && <EmailRolesSection />}
          {section === "email_templates" && <EmailTemplatesSection />}
          {section === "document_templates" && <DocumentTemplatesSection />}
          {section === "shipping" && <ShippingSection />}
          {section === "finapi" && <SettingSection settingKey="finapi" />}
          {section === "avs_provider" && <AVSProviderSettings />}
          {section === "music_slot" && <MusicSlotSection />}
          {section === "permissions" && <AdminPermissionsPanel />}
          {section === "creator_helper" && <AdminCreatorHelperPanel />}
        </main>
      </div>
    </div>
  );
}

// ─── Dashboard with Analytics ───────────────────────────
function DashboardSection() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([api.get("/admin/stats"), api.get(`/admin/analytics?period=${period}`)]);
      setStats(s.data);
      setAnalytics(a.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  if (loading || !stats) return <Spinner />;

  return (
    <div data-testid="admin-dashboard-kpis">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-[#0A0A0A]">Dashboard</h1>
        <div className="flex gap-1">
          {[["7d", "7 Tage"], ["30d", "30 Tage"], ["90d", "90 Tage"], ["365d", "1 Jahr"]].map(([v, l]) => (
            <button key={v} onClick={() => setPeriod(v)} className={`h-8 px-3 text-xs font-medium border transition-colors ${period === v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5]"}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard label="Umsatz" value={`${analytics?.revenue?.total?.toFixed(2) || 0} EUR`} sub={`${analytics?.revenue?.paid_orders || 0} bezahlte Bestellungen`} color="text-green-600" />
        <KPICard label="Bestellungen" value={analytics?.orders?.total || 0} sub={`${analytics?.orders?.guest || 0} Gast / ${analytics?.orders?.registered || 0} registriert`} />
        <KPICard label="Neue User" value={analytics?.growth?.new_users || 0} sub={`im Zeitraum ${period}`} color="text-blue-600" />
        <KPICard label="Neue Listings" value={analytics?.growth?.new_listings || 0} sub={`im Zeitraum ${period}`} />
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPICard label="User gesamt" value={stats.total_users} />
        <KPICard label="Aktive Listings" value={stats.published_listings} color="text-green-600" />
        <KPICard label="Ø Bestellwert" value={`${analytics?.revenue?.average_order_value?.toFixed(2) || 0} EUR`} />
        <KPICard label="Ausstehend" value={analytics?.revenue?.pending_orders || 0} sub="offene Zahlungen" color="text-amber-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Payment Method Distribution */}
        <div className="bg-white border border-[#E5E5E5] p-4">
          <h3 className="text-sm font-bold text-[#0A0A0A] mb-3">Zahlungsarten</h3>
          {analytics?.orders?.by_payment_method && <div className="space-y-2">
            <BarRow label="PayPal" value={analytics.orders.by_payment_method.paypal} total={analytics.orders.total} color="bg-blue-500" />
            <BarRow label="Überweisung" value={analytics.orders.by_payment_method.bank_transfer} total={analytics.orders.total} color="bg-green-500" />
            <BarRow label="Alt/Sonstige" value={analytics.orders.by_payment_method.legacy} total={analytics.orders.total} color="bg-[#CCC]" />
          </div>}
        </div>

        {/* Area Distribution */}
        <div className="bg-white border border-[#E5E5E5] p-4">
          <h3 className="text-sm font-bold text-[#0A0A0A] mb-3">Bereiche</h3>
          {analytics?.orders?.by_area && <div className="space-y-2">
            <BarRow label="Marketplace" value={analytics.orders.by_area.marketplace} total={analytics.orders.total} color="bg-[#0A0A0A]" />
            <BarRow label="stilltouchedX" value={analytics.orders.by_area.stilltouchedx} total={analytics.orders.total} color="bg-[#666]" />
          </div>}
        </div>
      </div>

      {/* Top Sellers */}
      {analytics?.top_sellers?.length > 0 && (
        <div className="bg-white border border-[#E5E5E5] p-4 mb-6">
          <h3 className="text-sm font-bold text-[#0A0A0A] mb-3">Top Seller</h3>
          <div className="space-y-2">
            {analytics.top_sellers.map((s, i) => (
              <div key={s.seller_id} className="flex items-center gap-3 p-2 bg-[#FAFAFA] border border-[#F0F0F0]">
                <span className="w-6 h-6 bg-[#0A0A0A] text-white text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0A0A0A] truncate">{s.display_name} <span className="text-[#999]">@{s.handle}</span></p>
                </div>
                <span className="text-xs text-[#666]">{s.orders} Bestellungen</span>
                <span className="text-sm font-bold text-[#0A0A0A]">{s.revenue.toFixed(2)} EUR</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      {analytics?.timeline?.length > 0 && (
        <div className="bg-white border border-[#E5E5E5] p-4 mb-6">
          <h3 className="text-sm font-bold text-[#0A0A0A] mb-3">Zeitverlauf</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-[#FAFAFA]">
                <th className="text-left px-2 py-1.5 text-[10px] text-[#999] uppercase">Datum</th>
                <th className="text-right px-2 py-1.5 text-[10px] text-[#999] uppercase">Bestellungen</th>
                <th className="text-right px-2 py-1.5 text-[10px] text-[#999] uppercase">Umsatz</th>
                <th className="text-right px-2 py-1.5 text-[10px] text-[#999] uppercase">Neue User</th>
              </tr></thead>
              <tbody className="divide-y divide-[#F0F0F0]">
                {analytics.timeline.slice(-14).reverse().map((d) => (
                  <tr key={d.date}>
                    <td className="px-2 py-1.5 text-xs">{new Date(d.date).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}</td>
                    <td className="px-2 py-1.5 text-right text-xs font-medium">{d.orders}</td>
                    <td className="px-2 py-1.5 text-right text-xs font-medium">{d.revenue.toFixed(2)} EUR</td>
                    <td className="px-2 py-1.5 text-right text-xs">{d.users}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Engagement */}
      <div className="grid grid-cols-3 gap-4">
        <KPICard label="Watchlist-Einträge" value={analytics?.engagement?.watches || 0} />
        <KPICard label="Pre-Sale Anfragen" value={analytics?.engagement?.presale_requests || 0} />
        <KPICard label="Chat-Nachrichten" value={analytics?.engagement?.chat_messages || 0} />
      </div>
    </div>
  );
}

function KPICard({ label, value, sub, color }) {
  return (
    <div className="bg-white border border-[#E5E5E5] p-4">
      <p className="text-[10px] text-[#999] uppercase tracking-wider font-medium">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color || "text-[#0A0A0A]"}`}>{value}</p>
      {sub && <p className="text-[10px] text-[#CCC] mt-0.5">{sub}</p>}
    </div>
  );
}

function BarRow({ label, value, total, color }) {
  const pct = total > 0 ? (value / total * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-[#666] w-24">{label}</span>
      <div className="flex-1 h-3 bg-[#F0F0F0]"><div className={`h-full ${color}`} style={{ width: `${pct}%` }} /></div>
      <span className="text-xs font-medium text-[#0A0A0A] w-8 text-right">{value}</span>
    </div>
  );
}

// ─── Users Section ──────────────────────────────────────
const USER_TABLE_COLS = [
  { key: "user", label: "Benutzer", required: true },
  { key: "sex", label: "Geschlecht" },
  { key: "user_id", label: "Kundennr." },
  { key: "stx_alias", label: "STX-Alias" },
  { key: "email", label: "E-Mail" },
  { key: "listings", label: "Artikel" },
  { key: "posts", label: "Beiträge" },
  { key: "verification", label: "Verifizierung" },
  { key: "capabilities", label: "Freigaben" },
  { key: "status", label: "Status" },
];
const USER_TABLE_COLS_DEFAULT = USER_TABLE_COLS.map((c) => c.key);
const USER_TABLE_COLS_LS_KEY = "admin_user_table_cols_v1";

function UsersSection() {
  const { user: authUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [capFilter, setCapFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showColPicker, setShowColPicker] = useState(false);
  // Variante B: Rollen-Änderungs-Modal
  const [roleChangeUser, setRoleChangeUser] = useState(null);
  const isOwner = authUser?.role === "owner";
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(USER_TABLE_COLS_LS_KEY) || "null");
      if (Array.isArray(stored) && stored.length > 0) return new Set(stored);
    } catch { /* ignore */ }
    return new Set(USER_TABLE_COLS_DEFAULT);
  });
  const toggleCol = (key) => {
    setVisibleCols((prev) => {
      const nx = new Set(prev);
      if (nx.has(key)) nx.delete(key); else nx.add(key);
      try { localStorage.setItem(USER_TABLE_COLS_LS_KEY, JSON.stringify([...nx])); } catch { /* ignore */ }
      return nx;
    });
  };
  const resetCols = () => {
    setVisibleCols(new Set(USER_TABLE_COLS_DEFAULT));
    try { localStorage.removeItem(USER_TABLE_COLS_LS_KEY); } catch { /* ignore */ }
  };
  const isCol = (k) => visibleCols.has(k);
  // Total visible cols incl. checkbox (1) + actions (1)
  const totalCols = 3 + USER_TABLE_COLS.filter((c) => c.required || isCol(c.key)).length;
  const [impersonating, setImpersonating] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // {user_id, email}
  const [deleteError, setDeleteError] = useState(null);
  const [deleteMode, setDeleteMode] = useState("soft"); // "soft" | "hard"
  const [hardDeleteConfirmText, setHardDeleteConfirmText] = useState("");
  const [suspendModal, setSuspendModal] = useState(null); // {user_id, email, display_name}
  const [suspendDays, setSuspendDays] = useState(7);
  const [suspendReason, setSuspendReason] = useState("");
  const [suspendError, setSuspendError] = useState(null);
  const [suspending, setSuspending] = useState(false);
  const [detailUserId, setDetailUserId] = useState(null);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  // iter107b (Msg 804): Bulk-Delete State
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const toggleRow = (uid) => setSelected((prev) => {
    const nx = new Set(prev);
    if (nx.has(uid)) nx.delete(uid); else nx.add(uid);
    return nx;
  });
  const toggleAll = () => setSelected((prev) => {
    const eligibleIds = users.filter((u) => u.role !== "admin" && u.role !== "owner").map((u) => u.user_id);
    const allSelected = eligibleIds.every((id) => prev.has(id));
    if (allSelected) return new Set();
    return new Set(eligibleIds);
  });
  const runBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Wirklich ${ids.length} User HARD löschen? Diese Aktion kann NICHT rückgängig gemacht werden.`)) return;
    setBulkDeleting(true);
    setBulkResult(null);
    try {
      const { data } = await api.post(`/admin/users/bulk-delete`, {
        user_ids: ids, hard_delete: true, reason: "Admin Bulk-Delete (Test-Cleanup)",
      });
      setBulkResult(data);
      setSelected(new Set());
      loadUsers();
    } catch (e) {
      window.alert("Bulk-Delete fehlgeschlagen: " + (e?.response?.data?.detail?.message || e?.response?.data?.detail || "Unbekannter Fehler"));
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
    setPage(1);
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/admin/users?page=${page}&limit=25&sort_by=${sortBy}&sort_dir=${sortDir}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (capFilter) url += `&capability=${capFilter}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      const { data } = await api.get(url);
      setUsers(data.users || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch { setUsers([]); }
    finally { setLoading(false); }
  }, [page, search, capFilter, statusFilter, sortBy, sortDir]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // iter110e · User-Gruppen für Dropdown-Spalte laden
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/user-groups");
        setGroups(Array.isArray(data) ? data : (data?.groups || []));
      } catch { setGroups([]); }
    })();
  }, []);

  const assignGroup = async (userId, groupId) => {
    try {
      await api.put(`/admin/users/${userId}/group`, { group_id: groupId || null });
      // Optimistic UI: patche local ohne Reload
      setUsers((us) => us.map((u) => u.user_id === userId ? { ...u, group_id: groupId || null } : u));
    } catch { loadUsers(); }
  };

  const impersonate = async (userId) => {
    setImpersonating(userId);
    try {
      const { data } = await api.post(`/admin/users/${userId}/impersonate`);
      window.open(`/impersonate?token=${data.impersonation_token}`, "_blank");
    } catch { /* ignore */ }
    finally { setImpersonating(null); }
  };

  const updateStatus = async (userId, newStatus) => {
    try { await api.post(`/admin/users/${userId}/status`, { account_status: newStatus }); loadUsers(); } catch { /* ignore */ }
  };

  const openSuspendModal = (u) => {
    setSuspendModal({ user_id: u.user_id, email: u.email, display_name: u.display_name });
    setSuspendDays(7);
    setSuspendReason("");
    setSuspendError(null);
  };

  const doSuspendUser = async () => {
    if (!suspendModal) return;
    if (!Number.isFinite(suspendDays) || suspendDays < 1 || suspendDays > 3650) {
      setSuspendError("Dauer muss zwischen 1 und 3650 Tagen liegen.");
      return;
    }
    setSuspending(true);
    setSuspendError(null);
    try {
      await api.post(`/admin/users/${suspendModal.user_id}/status`, {
        account_status: "SUSPENDED",
        duration_days: Math.floor(suspendDays),
        reason: suspendReason.trim() || "Verstoß gegen Plattform-Richtlinien",
      });
      setSuspendModal(null);
      loadUsers();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setSuspendError(typeof d === "string" ? d : (d?.message || "Fehler beim Sperren"));
    } finally {
      setSuspending(false);
    }
  };

  const updateVerification = async (userId, field, value) => {
    try {
      const body = {};
      body[field] = value;
      await api.post(`/admin/users/${userId}/verification`, body);
      loadUsers();
    } catch { /* ignore */ }
  };

  const doDeleteUser = async () => {
    if (!confirmDelete) return;
    const isHard = deleteMode === "hard";
    if (isHard && hardDeleteConfirmText.trim() !== "HARD DELETE") {
      setDeleteError('Bitte gib genau "HARD DELETE" ein, um die endgültige Löschung zu bestätigen.');
      return;
    }
    setDeletingId(confirmDelete.user_id);
    setDeleteError(null);
    try {
      await api.delete(`/admin/users/${confirmDelete.user_id}`, {
        data: {
          reason: isHard ? "Administrative Hard-Delete (DSGVO)" : "Administrative Löschung",
          hard_delete: isHard,
        },
      });
      setConfirmDelete(null);
      setDeleteMode("soft");
      setHardDeleteConfirmText("");
      loadUsers();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setDeleteError(typeof d === "string" ? d : (d?.message || "Fehler beim Löschen"));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div data-testid="admin-users-section">
      <h1 className="text-xl font-bold text-[#0A0A0A] mb-1 flex items-center gap-2"><Users size={20} /> User-Verwaltung</h1>
      <p className="text-sm text-[#999] mb-4">{total} Benutzer</p>

      <div className="flex gap-2 mb-4">
        <form onSubmit={(e) => { e.preventDefault(); setSearch(searchInput); setPage(1); }} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
            <input data-testid="admin-search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              className="w-full h-10 pl-9 pr-4 border border-[#E5E5E5] bg-white text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
              placeholder="Name, E-Mail, Handle..." />
          </div>
          <button type="submit" className="h-10 px-4 bg-[#0A0A0A] text-white text-sm font-medium">Suchen</button>
        </form>
        <button onClick={() => setShowFilters(!showFilters)} className={`h-10 px-3 border text-sm flex items-center gap-1.5 ${showFilters ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5]"}`}>
          <Filter size={14} /> Filter
        </button>
        <button
          data-testid="admin-users-col-picker-toggle"
          onClick={() => setShowColPicker(!showColPicker)}
          className={`h-10 px-3 border text-sm flex items-center gap-1.5 ${showColPicker ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5]"}`}
          title="Sichtbare Spalten"
        >
          <Columns size={14} /> Spalten
        </button>
      </div>

      {showColPicker && (
        <div data-testid="admin-users-col-picker" className="mb-4 p-3 bg-white border border-[#E5E5E5]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-[#999] uppercase tracking-wider">Sichtbare Spalten</span>
            <button
              onClick={resetCols}
              data-testid="admin-users-col-reset"
              className="text-[10px] text-[#666] underline hover:text-[#0A0A0A]"
            >
              Alle anzeigen
            </button>
          </div>
          <div className="flex flex-wrap gap-3">
            {USER_TABLE_COLS.map((c) => (
              <label
                key={c.key}
                className={`flex items-center gap-1.5 text-xs cursor-pointer select-none ${c.required ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <input
                  type="checkbox"
                  data-testid={`admin-users-col-${c.key}`}
                  checked={c.required || isCol(c.key)}
                  disabled={c.required}
                  onChange={() => toggleCol(c.key)}
                  className="accent-[#0A0A0A]"
                />
                <span className="text-[#666]">{c.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {showFilters && (
        <div className="flex gap-3 mb-4 flex-wrap p-3 bg-white border border-[#E5E5E5]">
          <div>
            <label className="text-[10px] text-[#999] uppercase tracking-wider font-medium">Capability</label>
            <select value={capFilter} onChange={(e) => { setCapFilter(e.target.value); setPage(1); }} className="mt-1 block h-9 px-2 border border-[#E5E5E5] text-sm bg-white">
              <option value="">Alle</option>
              {CAP_LIST.map((c) => <option key={c} value={c}>{CAP_LABELS[c]}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-[#999] uppercase tracking-wider font-medium">Status</label>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="mt-1 block h-9 px-2 border border-[#E5E5E5] text-sm bg-white">
              <option value="">Alle</option><option value="ACTIVE">Aktiv</option><option value="SUSPENDED">Gesperrt</option><option value="BANNED">Gebannt</option><option value="DELETED">Gelöscht (Soft)</option>
            </select>
          </div>
          {(capFilter || statusFilter || search) && (
            <button onClick={() => { setCapFilter(""); setStatusFilter(""); setSearch(""); setSearchInput(""); setPage(1); }}
              className="self-end h-9 px-3 border border-[#E5E5E5] text-xs text-[#666] flex items-center gap-1"><X size={12} /> Reset</button>
          )}
        </div>
      )}

      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        {/* iter107b: Bulk-Delete Action-Bar */}
        {selected.size > 0 && (
          <div data-testid="admin-users-bulk-bar" className="px-3 py-2 bg-red-50 border-b border-red-200 flex items-center justify-between">
            <span className="text-sm text-red-800 font-medium">{selected.size} User ausgewählt</span>
            <div className="flex gap-2">
              <button
                onClick={() => setSelected(new Set())}
                className="h-8 px-3 bg-white border border-[#E5E5E5] text-xs text-[#666]">
                Auswahl aufheben
              </button>
              <button
                data-testid="admin-users-bulk-delete"
                onClick={runBulkDelete}
                disabled={bulkDeleting}
                className="h-8 px-3 bg-red-700 text-white text-xs font-semibold flex items-center gap-1.5 hover:bg-red-800 disabled:opacity-40">
                <Trash2 size={12} /> {bulkDeleting ? "Wird gelöscht…" : `${selected.size} User hard löschen`}
              </button>
            </div>
          </div>
        )}
        {bulkResult && (
          <div data-testid="admin-users-bulk-result" className="px-3 py-2 bg-green-50 border-b border-green-200 text-xs text-green-900 flex items-center justify-between">
            <span>Bulk-Delete: {bulkResult.deleted} gelöscht · {bulkResult.skipped?.length || 0} übersprungen · {bulkResult.errors?.length || 0} Fehler</span>
            <button onClick={() => setBulkResult(null)} className="text-green-700 hover:text-green-900 text-lg leading-none">&times;</button>
          </div>
        )}
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead>
            <tr className="border-b border-[#E5E5E5] bg-[#FAFAFA]">
              <th className="px-2 py-2.5 w-8">
                <input
                  type="checkbox"
                  data-testid="admin-users-select-all"
                  onChange={toggleAll}
                  checked={users.filter((u) => u.role !== "admin" && u.role !== "owner").length > 0 && users.filter((u) => u.role !== "admin" && u.role !== "owner").every((u) => selected.has(u.user_id))}
                  className="accent-red-700"
                />
              </th>
              <SortableTh id="display_name" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} align="left">Benutzer</SortableTh>
              <SortableTh id="stx_username" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} align="left">stilltouchedX-Name</SortableTh>
              {isCol("sex") && <SortableTh id="sex" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} align="left">Geschlecht</SortableTh>}
              {isCol("user_id") && <SortableTh id="user_id" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} align="left">Kundennr.</SortableTh>}
              {isCol("stx_alias") && <SortableTh id="stx_alias" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} align="left">STX-Alias</SortableTh>}
              {isCol("email") && <SortableTh id="email" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} align="left">E-Mail</SortableTh>}
              {isCol("listings") && <SortableTh id="active_listings" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} align="center">Artikel</SortableTh>}
              {isCol("posts") && <SortableTh id="active_posts" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} align="center">Beiträge</SortableTh>}
              {isCol("verification") && <SortableTh id="email_verified" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} align="left">Verifizierung</SortableTh>}
              {isCol("capabilities") && <SortableTh id="capabilities" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} align="left">Freigaben</SortableTh>}
              {isCol("status") && <SortableTh id="account_status" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} align="left">Status</SortableTh>}
              <SortableTh id="created_at" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} align="left">Registriert</SortableTh>
              <SortableTh id="last_login_at" sortBy={sortBy} sortDir={sortDir} onToggle={toggleSort} align="left">Letzter Login</SortableTh>
              <th className="text-right px-3 py-2.5 text-[10px] font-bold text-[#999] uppercase tracking-wider">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F0F0F0]">
            {loading ? <tr><td colSpan={totalCols} className="py-8 text-center"><Spinner /></td></tr> :
             users.length === 0 ? <tr><td colSpan={totalCols} className="py-8 text-center text-[#999]">Keine Benutzer.</td></tr> :
             users.map((u) => (
              <tr key={u.user_id} className="hover:bg-[#FAFAFA]">
                <td className="px-2 py-2.5 w-8">
                  {u.role !== "admin" && u.role !== "owner" ? (
                    <input
                      type="checkbox"
                      data-testid={`admin-users-select-${u.user_id}`}
                      checked={selected.has(u.user_id)}
                      onChange={() => toggleRow(u.user_id)}
                      className="accent-red-700"
                    />
                  ) : <span className="text-[#CCC] text-[10px]">—</span>}
                </td>
                <td className="px-3 py-2.5">
                  <button
                    data-testid={`user-open-${u.user_id}`}
                    onClick={() => setDetailUserId(u.user_id)}
                    className="flex items-center gap-2 text-left hover:opacity-70 min-w-0 w-full"
                    title="Benutzer-Details öffnen"
                  >
                    <div className="w-7 h-7 rounded-full bg-[#E5E5E5] flex items-center justify-center text-[10px] font-bold text-[#999] shrink-0 overflow-hidden">
                      {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover"/> : (u.display_name || u.email || "?")[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#0A0A0A] truncate underline underline-offset-2 decoration-transparent hover:decoration-current">{u.display_name || "—"}</p>
                      {u.handle && <p className="text-[10px] text-[#999]">@{u.handle}</p>}
                    </div>
                  </button>
                </td>
                <td className="px-3 py-2.5" data-testid={`user-stx-username-${u.user_id}`}>
                  {u.stx_username
                    ? <span className="text-sm text-[#0A0A0A]">{u.stx_username}</span>
                    : <span className="text-[10px] text-[#CCC]">—</span>}
                </td>
                {isCol("sex") && <td className="px-3 py-2.5">
                  {u.sex === "FEMALE" ? (
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-pink-50 text-pink-700 border border-pink-200">♀ Weiblich</span>
                  ) : u.sex === "MALE" ? (
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200">♂ Männlich</span>
                  ) : (
                    <span className="text-[10px] text-[#CCC]">—</span>
                  )}
                </td>}
                {isCol("user_id") && <td className="px-3 py-2.5"><span className="text-[11px] font-mono text-[#666]">{u.user_id.slice(0, 8)}</span></td>}
                {isCol("stx_alias") && <td className="px-3 py-2.5" data-testid={`user-stx-alias-${u.user_id}`}>
                  {u.stx_alias ? (
                    <span className="px-1.5 py-0.5 text-[10px] font-mono font-medium bg-[#F5F5F5] text-[#0A0A0A] border border-[#E5E5E5]">{u.stx_alias}</span>
                  ) : (
                    <span className="text-[10px] text-[#CCC]">—</span>
                  )}
                </td>}
                {isCol("email") && <td className="px-3 py-2.5"><span className="text-sm">{u.email}</span>{u.email_verified && <Check size={10} className="inline ml-1 text-green-500" />}</td>}
                {isCol("listings") && <td className="px-3 py-2.5 text-center">
                  <span data-testid={`user-active-listings-${u.user_id}`} className={`text-sm font-medium ${u.active_listings ? "text-[#0A0A0A]" : "text-[#CCC]"}`}>{u.active_listings ?? 0}</span>
                </td>}
                {isCol("posts") && <td className="px-3 py-2.5 text-center">
                  <span data-testid={`user-active-posts-${u.user_id}`} className={`text-sm font-medium ${u.active_posts ? "text-[#0A0A0A]" : "text-[#CCC]"}`}>{u.active_posts ?? 0}</span>
                </td>}
                {isCol("verification") && <td className="px-3 py-2.5">
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] text-[#999] w-10">E-Mail:</span>
                      {u.email_verified ? (
                        <span className="text-[9px] text-green-600 font-medium">Verifiziert</span>
                      ) : (
                        <button onClick={() => updateVerification(u.user_id, "email_verified", true)} className="text-[9px] text-amber-600 font-medium hover:underline">Nicht verifiziert – Klick zum Setzen</button>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] text-[#999] w-10">AVS:</span>
                      {u.age_verification_status === "VERIFIED" ? (
                        <span className="text-[9px] text-green-600 font-medium">Verifiziert</span>
                      ) : (
                        <select value={u.age_verification_status || "NOT_STARTED"} onChange={(e) => updateVerification(u.user_id, "age_verification_status", e.target.value)}
                          className="h-5 px-1 text-[9px] border border-[#E5E5E5] bg-white">
                          <option value="NOT_STARTED">Nicht gestartet</option>
                          <option value="PENDING">Ausstehend</option>
                          <option value="VERIFIED">Verifiziert</option>
                          <option value="REJECTED">Abgelehnt</option>
                          <option value="EXPIRED">Abgelaufen</option>
                        </select>
                      )}
                    </div>
                  </div>
                </td>}
                {isCol("capabilities") && <td className="px-3 py-2.5"><div className="flex gap-1 flex-wrap">{u.capabilities.length === 0 ? <span className="text-[10px] text-[#CCC]">—</span> : u.capabilities.map((c) => <span key={c} className="px-1.5 py-0.5 bg-[#F0F0F0] text-[9px] font-medium text-[#666] border border-[#E5E5E5]">{CAP_LABELS[c] || c}</span>)}</div></td>}
                {isCol("status") && <td className="px-3 py-2.5">
                  <div className="flex flex-col gap-1 items-start">
                    <span className={`px-2 py-0.5 text-[10px] font-medium border ${u.account_status === "ACTIVE" ? "text-green-600 border-green-200 bg-green-50" : u.account_status === "SUSPENDED" ? "text-amber-600 border-amber-200 bg-amber-50" : "text-red-600 border-red-200 bg-red-50"}`}>{u.account_status}</span>
                    {u.role && (
                      <span
                        data-testid={`admin-user-role-badge-${u.user_id}`}
                        className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider border rounded-full ${
                          u.role === "owner" ? "text-amber-700 border-amber-300 bg-amber-50" :
                          u.role === "admin" ? "text-purple-700 border-purple-300 bg-purple-50" :
                          u.role === "moderator" ? "text-emerald-700 border-emerald-300 bg-emerald-50" :
                          u.role === "support" ? "text-blue-700 border-blue-300 bg-blue-50" :
                          "text-[#666] border-[#E5E5E5] bg-[#F5F5F5]"
                        }`}
                      >{u.role}</span>
                    )}
                  </div>
                </td>}
                <td className="px-3 py-2.5" data-testid={`admin-users-created-${u.user_id}`}>
                  <span className="text-[11px] text-[#666]">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"}
                  </span>
                </td>
                <td className="px-3 py-2.5" data-testid={`admin-users-last-login-${u.user_id}`}>
                  <span className="text-[11px] text-[#666]">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Nie"}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right">
                  <div className="flex gap-1 justify-end flex-wrap">
                    {isOwner && u.role !== "owner" && (
                      <button
                        data-testid={`admin-change-role-${u.user_id}`}
                        onClick={() => setRoleChangeUser(u)}
                        className="h-7 px-2 border border-[#0A0A0A] text-[#0A0A0A] text-[10px] flex items-center gap-1 rounded-full hover:bg-[#0A0A0A] hover:text-white transition-colors"
                        title="Rolle ändern (nur Owner)"
                      >
                        <Shield size={10} /> Rolle
                      </button>
                    )}
                    <button onClick={() => impersonate(u.user_id)} disabled={impersonating === u.user_id} className="h-7 px-2 bg-[#0A0A0A] text-white text-[10px] font-medium flex items-center gap-1 disabled:opacity-40" title="Als User einloggen"><LogIn size={10} /></button>
                    {u.account_status === "ACTIVE" && u.role !== "admin" && u.role !== "owner" && <button data-testid={`admin-suspend-user-${u.user_id}`} onClick={() => openSuspendModal(u)} className="h-7 px-2 border border-amber-300 text-amber-600 text-[10px] flex items-center gap-1" title="Sperren mit Dauer"><Ban size={10} /></button>}
                    {u.account_status === "SUSPENDED" && <button onClick={() => updateStatus(u.user_id, "ACTIVE")} className="h-7 px-2 border border-green-300 text-green-600 text-[10px] flex items-center gap-1" title="Entsperren"><UserCheck size={10} /></button>}
                    {u.role !== "admin" && u.role !== "owner" && u.account_status === "ACTIVE" && (
                      <button
                        data-testid={`admin-reset-2fa-${u.user_id}`}
                        onClick={async () => {
                          if (!window.confirm(`2FA für ${u.email} zurücksetzen? Der User muss 2FA dann neu einrichten.`)) return;
                          try {
                            const { data } = await api.post(`/admin/users/${u.user_id}/reset-2fa`);
                            window.alert(data.was_enabled ? "2FA wurde deaktiviert." : "2FA war bei diesem User nicht aktiv.");
                            loadUsers();
                          } catch (e) {
                            window.alert("Fehler: " + (e?.response?.data?.detail?.message || "Reset fehlgeschlagen"));
                          }
                        }}
                        className="h-7 px-2 border border-purple-300 text-purple-600 text-[10px] flex items-center gap-1"
                        title="2FA zurücksetzen (Notfall)"
                      >
                        2FA↺
                      </button>
                    )}
                    {u.role !== "admin" && u.role !== "owner" && u.account_status !== "DELETED" && (
                      <button
                        data-testid={`admin-delete-user-${u.user_id}`}
                        onClick={() => { setDeleteMode("soft"); setHardDeleteConfirmText(""); setConfirmDelete({ user_id: u.user_id, email: u.email, display_name: u.display_name, current_status: u.account_status }); }}
                        disabled={deletingId === u.user_id}
                        className="h-7 px-2 border border-red-300 text-red-700 text-[10px] flex items-center gap-1 hover:bg-red-50 disabled:opacity-40"
                        title="User löschen (Soft-Delete)"
                      >
                        <Trash2 size={10} />
                      </button>
                    )}
                    {u.role !== "admin" && u.role !== "owner" && u.account_status === "DELETED" && (
                      <button
                        data-testid={`admin-hard-delete-user-${u.user_id}`}
                        onClick={() => { setDeleteMode("hard"); setHardDeleteConfirmText(""); setConfirmDelete({ user_id: u.user_id, email: u.email, display_name: u.display_name, current_status: u.account_status }); }}
                        disabled={deletingId === u.user_id}
                        className="h-7 px-2 bg-red-700 text-white text-[10px] flex items-center gap-1 hover:bg-red-800 disabled:opacity-40"
                        title="Hard-Delete (endgültig)"
                      >
                        <Trash2 size={10} /> Endgültig
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-[#999]">Seite {page}/{totalPages} ({total})</p>
          <div className="flex gap-1">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="h-8 px-3 border border-[#E5E5E5] bg-white text-sm disabled:opacity-30 flex items-center gap-1"><ChevronLeft size={14} /></button>
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className="h-8 px-3 border border-[#E5E5E5] bg-white text-sm disabled:opacity-30 flex items-center gap-1"><ChevronRight size={14} /></button>
          </div>
        </div>
      )}

      {/* Sub-Batch UX-4: Delete-Confirmation-Modal (Soft & Hard) */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => { setConfirmDelete(null); setHardDeleteConfirmText(""); }}>
          <div className="w-full max-w-md bg-white border border-[#E5E5E5] shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
              <h3 className="text-sm font-bold text-red-700 flex items-center gap-1.5">
                <Trash2 size={14} /> {deleteMode === "hard" ? "User endgültig löschen?" : "User löschen?"}
              </h3>
              <button onClick={() => { setConfirmDelete(null); setHardDeleteConfirmText(""); }} className="text-[#999]"><X size={16} /></button>
            </div>
            <div className="px-4 py-3 space-y-3">
              <p className="text-sm text-[#0A0A0A]">
                Möchtest du den User <strong>{confirmDelete.display_name || confirmDelete.email}</strong> {deleteMode === "hard" ? "wirklich ENDGÜLTIG löschen?" : "wirklich löschen?"}
              </p>
              {deleteMode === "soft" ? (
                <div className="p-3 bg-amber-50 border border-amber-200">
                  <p className="text-[11px] text-amber-900 leading-relaxed font-medium mb-1">Soft-Delete (Standard):</p>
                  <ul className="text-[10px] text-amber-800 space-y-0.5 list-disc list-inside">
                    <li>Account wird auf <code>DELETED</code> gesetzt, Login unmöglich</li>
                    <li>Profil-Daten werden anonymisiert (Name → &bdquo;Gelöschter Nutzer&ldquo;, Adresse leer)</li>
                    <li>Aktive Listings werden auf <code>DELETED</code> gesetzt</li>
                    <li>Verkaufte Bestellungen + Audit-Trail bleiben erhalten (Compliance)</li>
                    <li className="font-medium">Hard-Delete (endgültige Entfernung) ist nach Soft-Delete möglich.</li>
                  </ul>
                </div>
              ) : (
                <>
                  <div className="p-3 bg-red-50 border border-red-300">
                    <p className="text-[11px] text-red-900 leading-relaxed font-bold mb-1">⚠ Hard-Delete (endgültig, nicht widerrufbar):</p>
                    <ul className="text-[10px] text-red-800 space-y-0.5 list-disc list-inside">
                      <li>User-Datensatz wird <strong>vollständig aus der Datenbank entfernt</strong></li>
                      <li>Profil, Capabilities und nicht-verkaufte Listings werden gelöscht</li>
                      <li>Verkaufte Bestellungen + Audit-Logs bleiben für Compliance erhalten</li>
                      <li>Diese Aktion kann <strong>NICHT</strong> rückgängig gemacht werden (DSGVO Art. 17)</li>
                    </ul>
                  </div>
                  <div>
                    <label className="text-[10px] text-[#999] uppercase tracking-wider font-medium block mb-1">
                      Tippe <code className="bg-[#F0F0F0] px-1">HARD DELETE</code> zur Bestätigung
                    </label>
                    <input
                      data-testid="admin-hard-delete-confirm-input"
                      type="text"
                      value={hardDeleteConfirmText}
                      onChange={(e) => setHardDeleteConfirmText(e.target.value)}
                      placeholder="HARD DELETE"
                      className="w-full h-10 px-3 border border-red-300 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-red-600"
                    />
                  </div>
                </>
              )}
              {deleteError && (
                <div className="p-2 bg-red-50 border border-red-200">
                  <p className="text-[11px] text-red-700">{deleteError}</p>
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => { setConfirmDelete(null); setHardDeleteConfirmText(""); }} className="flex-1 h-10 border border-[#E5E5E5] text-[#0A0A0A] text-sm">Abbrechen</button>
                <button
                  data-testid={deleteMode === "hard" ? "admin-hard-delete-confirm" : "admin-delete-confirm"}
                  onClick={doDeleteUser}
                  disabled={!!deletingId || (deleteMode === "hard" && hardDeleteConfirmText.trim() !== "HARD DELETE")}
                  className={`flex-1 h-10 text-white text-sm font-medium disabled:opacity-50 ${deleteMode === "hard" ? "bg-red-700 hover:bg-red-800" : "bg-red-600 hover:bg-red-500"}`}
                >
                  {deletingId ? "Lösche..." : (deleteMode === "hard" ? "Endgültig löschen" : "User löschen")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Suspend-Modal (Dauer + Grund) */}
      {suspendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setSuspendModal(null)}>
          <div className="w-full max-w-md bg-white border border-[#E5E5E5] shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
              <h3 className="text-sm font-bold text-amber-700 flex items-center gap-1.5"><Ban size={14} /> User sperren</h3>
              <button onClick={() => setSuspendModal(null)} className="text-[#999]"><X size={16} /></button>
            </div>
            <div className="px-4 py-3 space-y-3">
              <p className="text-sm text-[#0A0A0A]">
                Sperre für <strong>{suspendModal.display_name || suspendModal.email}</strong>:
              </p>
              <div>
                <label className="text-[10px] text-[#999] uppercase tracking-wider font-medium block mb-1">Dauer (Tage)</label>
                <div className="flex gap-2 mb-1">
                  {[1, 3, 7, 14, 30].map((d) => (
                    <button key={d} type="button" onClick={() => setSuspendDays(d)}
                      className={`h-8 px-3 text-xs border ${suspendDays === d ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5]"}`}>{d}</button>
                  ))}
                </div>
                <input
                  data-testid="admin-suspend-days"
                  type="number"
                  min={1}
                  max={3650}
                  value={suspendDays}
                  onChange={(e) => setSuspendDays(parseInt(e.target.value, 10) || 0)}
                  className="w-full h-10 px-3 border border-[#E5E5E5] bg-white text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                />
              </div>
              <div>
                <label className="text-[10px] text-[#999] uppercase tracking-wider font-medium block mb-1">Grund (optional)</label>
                <textarea
                  data-testid="admin-suspend-reason"
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Verstoß gegen Plattform-Richtlinien"
                  className="w-full px-3 py-2 border border-[#E5E5E5] bg-white text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
                />
              </div>
              <div className="p-2 bg-blue-50 border border-blue-200">
                <p className="text-[10px] text-blue-800 leading-relaxed">
                  Der User erhält eine <strong>E-Mail</strong> und eine <strong>System-Nachricht im Postfach</strong> mit Sperrgrund und Enddatum. Nach Ablauf wird das Konto automatisch reaktiviert.
                </p>
              </div>
              {suspendError && (
                <div className="p-2 bg-red-50 border border-red-200">
                  <p className="text-[11px] text-red-700">{suspendError}</p>
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => setSuspendModal(null)} className="flex-1 h-10 border border-[#E5E5E5] text-[#0A0A0A] text-sm">Abbrechen</button>
                <button data-testid="admin-suspend-confirm" onClick={doSuspendUser} disabled={suspending}
                  className="flex-1 h-10 bg-amber-600 text-white text-sm font-medium hover:bg-amber-500 disabled:opacity-50">
                  {suspending ? "Sperre..." : "Jetzt sperren"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Detail Modal (Owner Msg 2026-02: alle Details klickbar) */}
      {detailUserId && (
        <AdminUserDetailModal userId={detailUserId} onClose={() => setDetailUserId(null)} />
      )}
      {/* Variante B: Rollen-Änderungs-Modal (nur Owner sichtbar) */}
      {roleChangeUser && (
        <RoleChangeModal
          open={!!roleChangeUser}
          user={roleChangeUser}
          onClose={() => setRoleChangeUser(null)}
          onDone={() => { setRoleChangeUser(null); loadUsers(); }}
        />
      )}
    </div>
  );
}

function SortableTh({ id, sortBy, sortDir, onToggle, align, children }) {
  const active = sortBy === id;
  const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "▲▼";
  const alignCls = align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";
  return (
    <th className={`${alignCls} px-3 py-2.5`}>
      <button
        data-testid={`sort-th-${id}`}
        onClick={() => onToggle(id)}
        className={`text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 ${active ? "text-[#0A0A0A]" : "text-[#999] hover:text-[#0A0A0A]"}`}
        title={active ? `Sortiert ${sortDir === "asc" ? "aufsteigend" : "absteigend"}` : "Zum Sortieren klicken"}
      >
        {children}
        <span className={`text-[8px] ${active ? "text-[#0A0A0A]" : "text-[#CCC]"}`}>{arrow}</span>
      </button>
    </th>
  );
}

// ─── Admin User Detail Modal ────────────────────────────
function RestoreBanner({ userId, daysRemaining, onRestored }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const doRestore = async () => {
    if (!window.confirm(`User wirklich wiederherstellen? Aktive Artikel/Beiträge bleiben gelöscht — nur Konto + Profil werden restauriert.`)) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/users/${userId}/restore`);
      onRestored?.();
    } catch (e) {
      setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Wiederherstellung fehlgeschlagen.");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-3 border border-amber-300 bg-amber-50 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-amber-800">User ist gelöscht</p>
        <p className="text-[11px] text-amber-800 mt-0.5">
          Noch <strong>{daysRemaining} Tag(e)</strong> bis zur endgültigen Löschung. Danach ist keine Wiederherstellung mehr möglich.
        </p>
        {err && <p className="text-[11px] text-red-700 mt-1">{err}</p>}
      </div>
      <button
        data-testid="user-restore-btn"
        onClick={doRestore}
        disabled={busy}
        className="h-9 px-3 bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-50 whitespace-nowrap"
      >
        {busy ? "..." : "Wiederherstellen"}
      </button>
    </div>
  );
}

function AdminUserDetailModal({ userId, onClose }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [nestedOrderId, setNestedOrderId] = useState(null);
  const purchasesRef = useRef(null);
  const salesRef = useRef(null);
  const listingsRef = useRef(null);
  const postsRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/users/${userId}`);
        setD(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Konnte User-Details nicht laden.");
      } finally { setLoading(false); }
    })();
  }, [userId]);

  const scrollTo = (ref) => ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const p = d?.profile || {};
  const c = d?.contact || {};
  const s = d?.stats || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-white flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-3 min-w-0">
            {p.avatar_url && <img src={p.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />}
            <div className="min-w-0">
              <p className="text-base font-bold text-[#0A0A0A] truncate">{p.display_name || d?.email || "—"}</p>
              <p className="text-[10px] text-[#999] font-mono truncate">{d?.user_id}</p>
            </div>
          </div>
          <button data-testid="user-detail-close" onClick={onClose} className="text-[#666] hover:text-[#0A0A0A]"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="py-12 text-center"><Spinner /></div>
        ) : err ? (
          <div className="p-5 text-sm text-red-600">{String(err)}</div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Restore-Banner bei gelöschten Usern innerhalb Grace */}
            {d?.account_status === "DELETED" && d?.restore_info?.restorable && (
              <RestoreBanner userId={d.user_id} daysRemaining={d.restore_info.days_remaining} onRestored={onClose} />
            )}
            {d?.account_status === "DELETED" && d?.restore_info && !d.restore_info.restorable && (
              <div className="p-3 border border-red-200 bg-red-50 text-xs text-red-700">
                Dieser User ist gelöscht. Die 30-Tage-Wiederherstellungsfrist ist abgelaufen — beim nächsten Auto-Purge wird er endgültig entfernt.
              </div>
            )}

            {/* Kontaktdaten */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-2">Kontakt & Adresse</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <DL label="E-Mail" value={c.email} />
                <DL label="Bestätigt" value={d.email_verified ? "Ja" : "Nein"} />
                <DL label="Vorname" value={c.first_name} />
                <DL label="Nachname" value={c.last_name} />
                <DL label="Straße" value={[c.address_street, c.address_house_number].filter(Boolean).join(" ") || "—"} />
                <DL label="PLZ / Stadt" value={[c.address_zip, c.address_city].filter(Boolean).join(" ") || "—"} />
                <DL label="Land" value={c.address_country || "—"} />
                <DL label="Packstation" value={c.packstation_number || "—"} />
                <DL label="Bio" value={p.bio || "—"} className="sm:col-span-2" />
              </div>
            </div>

            {/* Stats */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-2">Zahlen</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Käufe offen" value={s.buyer_orders_open} sub={`von ${s.buyer_orders_total} gesamt`}
                  onClick={s.buyer_orders_total ? () => scrollTo(purchasesRef) : null} />
                <Stat label="Verkäufe offen" value={s.seller_orders_open} sub={`von ${s.seller_orders_total} gesamt`}
                  onClick={s.seller_orders_total ? () => scrollTo(salesRef) : null} />
                <Stat label="Aktive Artikel" value={s.active_listings} sub={`von ${s.total_listings} gesamt`}
                  onClick={s.active_listings ? () => scrollTo(listingsRef) : null} />
                <Stat label="Aktive Beiträge" value={s.active_posts} sub={`von ${s.total_posts} gesamt`}
                  onClick={s.active_posts ? () => scrollTo(postsRef) : null} />
              </div>
            </div>

            {/* Käufe */}
            {d.recent_purchases?.length > 0 && (
              <div ref={purchasesRef}><MiniList title="Letzte Käufe" items={d.recent_purchases} onOrderClick={setNestedOrderId} /></div>
            )}
            {/* Verkäufe */}
            {d.recent_sales?.length > 0 && (
              <div ref={salesRef}><MiniList title="Letzte Verkäufe" items={d.recent_sales} onOrderClick={setNestedOrderId} /></div>
            )}
            {/* Aktive Artikel */}
            {d.active_listings_sample?.length > 0 && (
              <div ref={listingsRef}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-2">Aktive Artikel (bis 20)</p>
                <div className="space-y-1">
                  {d.active_listings_sample.map((l) => (
                    <div key={l.listing_id} className="flex items-center justify-between px-3 py-2 border border-[#E5E5E5] text-xs">
                      <div className="min-w-0 flex-1">
                        <a href={l.area_scope === "STILLTOUCHEDX" ? `/stilltouchedx/${l.listing_id}` : `/listing/${l.listing_id}`}
                          target="_blank" rel="noopener noreferrer"
                          className="font-medium text-[#0A0A0A] hover:underline inline-flex items-center gap-1">
                          {l.title} <ExternalLink size={10} className="text-[#999]" />
                        </a>
                        <p className="text-[10px] text-[#999]">{l.listing_status} · {new Date(l.created_at).toLocaleDateString("de-DE")}</p>
                      </div>
                      <span className="tabular-nums text-[#0A0A0A]">{Number(l.price || 0).toFixed(2)} {l.currency || "EUR"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Aktive Beiträge */}
            {d.active_posts_sample?.length > 0 && (
              <div ref={postsRef}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-2">Aktive Beiträge (bis 20)</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {d.active_posts_sample.map((post) => {
                    const path = post.kind === "reel" ? `/reels/${post.id}` : post.kind === "watch" ? `/watch/${post.id}` : `/post/${post.id}`;
                    return (
                      <a
                        key={`${post.kind}-${post.id}`}
                        href={path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="border border-[#E5E5E5] hover:border-[#0A0A0A] text-xs group"
                      >
                        {post.thumbnail_url && (
                          <div className="aspect-square bg-[#F5F5F5] overflow-hidden">
                            <img src={post.thumbnail_url} alt="" className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div className="p-2">
                          <p className="text-[10px] text-[#666] uppercase tracking-wider">{post.kind}</p>
                          <p className="text-[11px] font-medium text-[#0A0A0A] line-clamp-2 group-hover:underline">{post.title || "—"}</p>
                          <p className="text-[9px] text-[#999] mt-0.5">{new Date(post.created_at).toLocaleDateString("de-DE")}</p>
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Capabilities */}
            {d.capabilities?.length > 0 && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-2">Freigaben (Capabilities)</p>
                <div className="flex flex-wrap gap-1">
                  {d.capabilities.map((cap) => (
                    <span key={cap.code || cap.capability_code} className="px-2 py-0.5 border border-[#E5E5E5] text-[10px] text-[#666]">
                      {cap.code || cap.capability_code}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* iter110 · Service-Fee Sonderkonditionen */}
            <UserFeeOverridesPanel userId={d.user_id} />

            {/* iter170 · DSGVO Art. 15 Datenauszug */}
            <DSGVOExportPanel userId={d.user_id} email={c.email || d.email} />
          </div>
        )}
      </div>
      {nestedOrderId && (
        <OrderDetailModal
          orderId={nestedOrderId}
          onClose={() => setNestedOrderId(null)}
        />
      )}
    </div>
  );
}

function DL({ label, value, className = "" }) {
  return (
    <div className={`p-2 border border-[#E5E5E5] ${className}`}>
      <p className="text-[9px] uppercase tracking-wider text-[#999]">{label}</p>
      <p className="text-[11px] text-[#0A0A0A] break-words">{value || "—"}</p>
    </div>
  );
}

// iter170 · DSGVO Art. 15 Datenauszug (Admin bearbeitet Anfragen per E-Mail)
function DSGVOExportPanel({ userId, email }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null); // {records}

  const doExport = async () => {
    setLoading(true);
    setErr("");
    setSuccess(null);
    try {
      const resp = await api.get(`/admin/users/${userId}/dsgvo-export`, {
        responseType: "blob",
      });
      const totalRecords = resp.headers?.["x-dsgvo-records"] || "?";
      // Download-Trigger
      const blob = new Blob([resp.data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dsgvo-export_${userId}_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setSuccess({ records: totalRecords });
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="dsgvo-export-panel"
      className="rounded-2xl border border-[#E5E5E5] bg-[#FAFAFA] p-4"
    >
      <div className="flex items-start gap-3">
        <ScrollText size={18} className="text-[#0A0A0A] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[#0A0A0A]">DSGVO Art. 15 · Datenauszug</p>
          <p className="text-[11px] text-[#666] mt-1 mb-3">
            Erstellt einen vollständigen JSON-Auszug aller zum User gespeicherten
            Daten (Profil, Käufe, Verkäufe, Nachrichten, Follows, Benachrichtigungen …).
            Die Datei anschließend per E-Mail an{" "}
            <span className="font-mono text-[#0A0A0A]">{email || "die Registrierungsadresse"}</span>{" "}
            versenden. Jede Anfrage wird im Audit-Log protokolliert.
          </p>
          {err && (
            <div
              data-testid="dsgvo-export-error"
              className="mb-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            >
              {err}
            </div>
          )}
          {success && (
            <div
              data-testid="dsgvo-export-success"
              className="mb-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
            >
              Export erstellt · {success.records} Datensätze heruntergeladen.
            </div>
          )}
          <button
            data-testid="dsgvo-export-btn"
            onClick={doExport}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A0A0A] text-white text-xs font-medium hover:bg-[#222] transition disabled:opacity-50"
          >
            {loading ? (
              <>
                <RefreshCw size={12} className="animate-spin" /> Wird erstellt …
              </>
            ) : (
              <>
                <FileText size={12} /> DSGVO-Export herunterladen
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, onClick }) {
  const Cmp = onClick ? "button" : "div";
  return (
    <Cmp
      onClick={onClick || undefined}
      className={`p-3 border border-[#E5E5E5] bg-[#FAFAFA] text-center ${onClick ? "cursor-pointer hover:border-[#0A0A0A] hover:bg-white transition-colors" : ""}`}
    >
      <p className="text-2xl font-bold text-[#0A0A0A] tabular-nums">{value ?? 0}</p>
      <p className="text-[10px] text-[#666] uppercase tracking-wider">{label}</p>
      {sub && <p className="text-[9px] text-[#999] mt-0.5">{sub}</p>}
    </Cmp>
  );
}

function MiniList({ title, items, onOrderClick }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#999] mb-2">{title}</p>
      <div className="space-y-1">
        {items.map((o) => (
          <button
            key={o.order_id}
            type="button"
            onClick={() => onOrderClick && onOrderClick(o.order_id)}
            data-testid={`admin-user-order-row-${o.order_id}`}
            className="w-full flex items-center justify-between px-3 py-2 border border-[#E5E5E5] hover:border-[#0A0A0A] text-xs group text-left cursor-pointer"
          >
            <div className="min-w-0 flex-1">
              <p className="font-medium text-[#0A0A0A] truncate group-hover:underline">{o.title || "—"}</p>
              <p className="text-[10px] text-[#999] font-mono">{o.order_id?.slice(0, 8)} · {o.area_scope} · {o.status}</p>
            </div>
            <span className="tabular-nums text-[#0A0A0A]">{Number(o.price || 0).toFixed(2)} {o.currency || "EUR"}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Bank Reconciliation ────────────────────────────────
function ReconciliationSection() {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ amount: "", sender_name: "", reference_text: "" });
  const [matchOrderId, setMatchOrderId] = useState({});

  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/bank-transactions?limit=50"); setTxs(data.transactions || []); } catch { /* ignore */ } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const addTx = async () => {
    if (!form.amount) return;
    try { await api.post("/admin/bank-transactions", { ...form, amount: parseFloat(form.amount) }); setForm({ amount: "", sender_name: "", reference_text: "" }); load(); } catch { /* ignore */ }
  };

  const manualMatch = async (txId) => {
    const orderId = matchOrderId[txId];
    if (!orderId) return;
    try { await api.post(`/admin/bank-transactions/${txId}/match`, { order_id: orderId }); load(); } catch { /* ignore */ }
  };

  return (
    <div data-testid="admin-reconciliation">
      <h1 className="text-xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><Landmark size={20} /> Zahlungseingänge</h1>
      <div className="bg-white border border-[#E5E5E5] p-4 mb-4 space-y-3">
        <p className="text-xs font-medium text-[#666] uppercase tracking-wider">Neue Transaktion erfassen</p>
        <div className="flex gap-2 flex-wrap">
          <input value={form.amount} onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))} type="number" step="0.01" placeholder="Betrag" className="h-9 w-32 px-3 border border-[#E5E5E5] text-sm" />
          <input value={form.sender_name} onChange={(e) => setForm(p => ({ ...p, sender_name: e.target.value }))} placeholder="Absender" className="h-9 w-48 px-3 border border-[#E5E5E5] text-sm" />
          <input value={form.reference_text} onChange={(e) => setForm(p => ({ ...p, reference_text: e.target.value }))} placeholder="Verwendungszweck" className="h-9 flex-1 px-3 border border-[#E5E5E5] text-sm" />
          <button onClick={addTx} className="h-9 px-4 bg-[#0A0A0A] text-white text-xs font-medium flex items-center gap-1"><Plus size={12} /> Erfassen</button>
        </div>
      </div>
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-[#FAFAFA]">
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Datum</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Betrag</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Absender</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Verwendungszweck</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Status</th>
            <th className="text-right px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Aktion</th>
          </tr></thead>
          <tbody className="divide-y divide-[#F0F0F0]">
            {loading ? <tr><td colSpan={6} className="py-8 text-center"><Spinner /></td></tr> :
             txs.length === 0 ? <tr><td colSpan={6} className="py-8 text-center text-[#999]">Keine Transaktionen.</td></tr> :
             txs.map((t) => (
              <tr key={t.transaction_id} className="hover:bg-[#FAFAFA]">
                <td className="px-3 py-2 text-xs text-[#666]">{new Date(t.created_at).toLocaleDateString("de-DE")}</td>
                <td className="px-3 py-2 font-medium">{t.amount?.toFixed(2)} {t.currency}</td>
                <td className="px-3 py-2 text-[#666]">{t.sender_name || "—"}</td>
                <td className="px-3 py-2 text-xs font-mono text-[#666]">{t.reference_text || "—"}</td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 text-[10px] font-medium border ${t.match_status === "AUTO_MATCHED" || t.match_status === "MANUAL_MATCHED" ? "text-green-600 border-green-200 bg-green-50" : "text-amber-600 border-amber-200 bg-amber-50"}`}>{t.match_status}</span></td>
                <td className="px-3 py-2 text-right">
                  {t.match_status === "UNMATCHED" ? (
                    <div className="flex gap-1 justify-end">
                      <input value={matchOrderId[t.transaction_id] || ""} onChange={(e) => setMatchOrderId(p => ({ ...p, [t.transaction_id]: e.target.value }))} placeholder="Order-ID" className="h-7 w-32 px-2 border border-[#E5E5E5] text-[10px]" />
                      <button onClick={() => manualMatch(t.transaction_id)} className="h-7 px-2 bg-[#0A0A0A] text-white text-[10px]">Zuordnen</button>
                    </div>
                  ) : t.matched_order_id ? <span className="text-[10px] text-green-600 font-mono">{t.matched_order_id.slice(0, 8)}</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Audit Log ──────────────────────────────────────────
function AuditSection() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/audit-log?page=${page}&limit=30${filter ? `&event=${filter}` : ""}`); setLogs(data.logs || []); setTotal(data.total || 0); } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [page, filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-audit">
      <h1 className="text-xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><ScrollText size={20} /> Audit-Log</h1>
      <div className="flex gap-2 mb-4">
        <input value={filter} onChange={(e) => { setFilter(e.target.value); setPage(1); }} placeholder="Event filtern..." className="h-10 flex-1 px-3 border border-[#E5E5E5] text-sm bg-white" />
        <p className="text-xs text-[#999] self-center">{total} Events</p>
      </div>
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-[#FAFAFA]">
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Zeitpunkt</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Event</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Details</th>
          </tr></thead>
          <tbody className="divide-y divide-[#F0F0F0]">
            {loading ? <tr><td colSpan={3} className="py-8 text-center"><Spinner /></td></tr> :
             logs.map((l, i) => (
              <tr key={i} className="hover:bg-[#FAFAFA]">
                <td className="px-3 py-2 text-xs text-[#666] whitespace-nowrap">{new Date(l.created_at).toLocaleString("de-DE")}</td>
                <td className="px-3 py-2"><span className="px-2 py-0.5 bg-[#F0F0F0] text-[10px] font-mono font-medium text-[#0A0A0A] border border-[#E5E5E5]">{l.event}</span></td>
                <td className="px-3 py-2 text-xs text-[#666] max-w-md truncate">
                  {l.order_id ? `Order: ${l.order_id.slice(0,8)} ` : ""}
                  {l.admin_id ? `Admin: ${l.admin_id.slice(0,8)} ` : ""}
                  {l.file_id ? `Datei: ${l.file_id.slice(0,8)} ` : ""}
                  {typeof l.force === "boolean" ? (l.force ? "⚠ FORCE " : "") : ""}
                  {typeof l.usage_count === "number" ? `Verwendungen: ${l.usage_count} ` : ""}
                  {typeof l.deleted === "number" ? `gelöscht: ${l.deleted} ` : ""}
                  {typeof l.skipped === "number" ? `übersprungen: ${l.skipped} ` : ""}
                  {l.key || ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > 30 && <div className="flex justify-center gap-2 mt-4">
        <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="h-8 px-3 border text-sm disabled:opacity-30"><ChevronLeft size={14} /></button>
        <span className="text-xs text-[#999] self-center">Seite {page}</span>
        <button onClick={() => setPage(page + 1)} className="h-8 px-3 border text-sm"><ChevronRight size={14} /></button>
      </div>}
    </div>
  );
}

// ─── Generic Setting Section ────────────────────────────
function MaintenanceModeSection() {
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [estimatedEnd, setEstimatedEnd] = useState("");
  const [sitePin, setSitePin] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings/maintenance");
        const v = data?.value || {};
        setEnabled(!!v.enabled);
        setMessage(v.message || "");
        setEstimatedEnd(v.estimated_end || "");
        setSitePin(v.site_pin || "");
      } catch { /* leeres Setting */ }
    })();
  }, []);

  const save = async (nextEnabled) => {
    const target = nextEnabled ?? enabled;
    setSaving(true); setError("");
    try {
      await api.post("/admin/settings/maintenance", {
        value: {
          enabled: !!target,
          message: message.trim(),
          estimated_end: estimatedEnd.trim(),
          site_pin: sitePin.trim(),
        },
      });
      setEnabled(!!target);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="maintenance-mode-section" className="bg-white border border-[#E5E5E5] p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-[#0A0A0A]">Wartungsmodus</h3>
          <p className="text-xs text-[#666] mt-0.5">Blockiert das Frontend für alle Nicht-Admin-User. Admin-Dashboard bleibt erreichbar.</p>
        </div>
        <div className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider border ${enabled ? "bg-red-50 border-red-300 text-red-700" : "bg-green-50 border-green-300 text-green-700"}`}>
          {enabled ? "AKTIV" : "AUS"}
        </div>
      </div>

      <div>
        <label className="text-[10px] font-bold text-[#666] uppercase tracking-wider block mb-1">Nachricht an Nutzer (optional)</label>
        <textarea
          data-testid="maintenance-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          placeholder="Wir arbeiten gerade an der Plattform. Bitte hab ein paar Minuten Geduld — wir sind gleich wieder für dich da."
          className="w-full px-3 py-2 border border-[#E5E5E5] text-sm resize-none"
        />
      </div>

      <div>
        <label className="text-[10px] font-bold text-[#666] uppercase tracking-wider block mb-1">Voraussichtliches Ende (optional, z. B. &bdquo;ca. 18:00 Uhr&ldquo;)</label>
        <input
          data-testid="maintenance-eta"
          value={estimatedEnd}
          onChange={(e) => setEstimatedEnd(e.target.value)}
          placeholder="ca. 18:00 Uhr"
          className="w-full h-10 px-3 border border-[#E5E5E5] text-sm"
        />
      </div>

      <div>
        <label className="text-[10px] font-bold text-[#666] uppercase tracking-wider block mb-1">Zugangs-PIN (optional)</label>
        <input
          data-testid="maintenance-site-pin"
          value={sitePin}
          onChange={(e) => setSitePin(e.target.value.slice(0, 12))}
          maxLength={12}
          placeholder="z. B. Team2026"
          className="w-full h-10 px-3 border border-[#E5E5E5] text-sm font-mono"
        />
        <p className="text-[11px] text-[#999] mt-1">
          Ausgewählte User können mit diesem PIN die Seite trotz aktivem Wartungsmodus für ihre Session nutzen (4–12 Zeichen). Leer lassen = kein PIN-Zugang. Nach dem Ändern verfallen alte Freigaben automatisch.
        </p>
      </div>

      {error && <div className="p-2 bg-red-50 border border-red-200 text-xs text-red-700">{String(error)}</div>}
      {saved && <div className="p-2 bg-green-50 border border-green-200 text-xs text-green-700">Gespeichert.</div>}

      <div className="flex flex-wrap gap-2 pt-2 border-t border-[#F0F0F0]">
        {!enabled ? (
          <button
            data-testid="maintenance-enable-btn"
            onClick={() => save(true)}
            disabled={saving}
            className="h-10 px-4 bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <AlertTriangle size={14} /> Wartungsmodus aktivieren
          </button>
        ) : (
          <button
            data-testid="maintenance-disable-btn"
            onClick={() => save(false)}
            disabled={saving}
            className="h-10 px-4 bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Check size={14} /> Wartungsmodus deaktivieren
          </button>
        )}
        <button
          data-testid="maintenance-save-msg-btn"
          onClick={() => save()}
          disabled={saving}
          className="h-10 px-4 border border-[#E5E5E5] text-sm text-[#666] hover:bg-[#F5F5F5] disabled:opacity-50"
        >
          {saving ? "..." : "Nachricht speichern"}
        </button>
      </div>

      {/* iter107e (Msg 809): Zombie-Cleanup als Admin-Aktion */}
      <ZombieCleanupPanel />
    </div>
  );
}

function ZombieCleanupPanel() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const run = async () => {
    if (!window.confirm("Zombie-Cleanup startet: entfernt alle DB-Referenzen auf bereits gelöschte User (follows, mutes, aliases, watches, search-alerts, notifications, carts). Fortfahren?")) return;
    setRunning(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/admin/maintenance/zombie-cleanup", {});
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message || "Unbekannter Fehler");
    } finally {
      setRunning(false);
    }
  };
  return (
    <div data-testid="zombie-cleanup-panel" className="border-t border-[#E5E5E5] pt-4 mt-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-bold text-[#0A0A0A]">Zombie-Cleanup (Ghost-Data entfernen)</h4>
          <p className="text-[11px] text-[#666] mt-0.5">Entfernt Datenbank-Einträge (follows, mutes, watches, aliases, …), die auf nicht mehr existierende User verweisen. Idempotent — sicher wiederholbar.</p>
        </div>
        <button
          data-testid="admin-zombie-cleanup-btn"
          onClick={run}
          disabled={running}
          className="h-9 px-3 border border-[#0A0A0A] text-xs font-semibold text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-40"
        >
          {running ? "Läuft…" : "Cleanup starten"}
        </button>
      </div>
      {result && (
        <div data-testid="zombie-cleanup-result" className="mt-3 p-3 bg-green-50 border border-green-200 text-[11px] text-green-900">
          <p className="font-semibold mb-1">Ergebnis · gesamt {result.total_removed} Einträge entfernt:</p>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono">
            {Object.entries(result).filter(([k]) => k.endsWith("_removed")).map(([k, v]) => (
              <li key={k}>{k.replace("_removed", "")}: <b>{v}</b></li>
            ))}
          </ul>
        </div>
      )}
      {error && (
        <div data-testid="zombie-cleanup-error" className="mt-3 p-2 bg-red-50 border border-red-200 text-[11px] text-red-800">
          Fehler: {error}
        </div>
      )}
    </div>
  );
}

function SettingSection({ settingKey }) {
  const [value, setValue] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");

  useEffect(() => { (async () => { try { const { data } = await api.get(`/admin/settings/${settingKey}`); setValue(data.value || {}); } catch { /* ignore */ } finally { setLoading(false); } })(); }, [settingKey]);

  const save = async () => { setSaving(true); setSuccess(""); try { await api.post(`/admin/settings/${settingKey}`, { value }); setSuccess("Gespeichert"); setTimeout(() => setSuccess(""), 3000); } catch { /* ignore */ } finally { setSaving(false); } };

  if (loading) return <Spinner />;

  const FIELD_DEFS = {
    company: [
      { key: "company_name", label: "Firmenname", ph: "stilltouched GmbH" },
      { key: "legal_name", label: "Rechtlicher Name" },
      { key: "address", label: "Adresse" },
      { key: "support_email", label: "Support-E-Mail", type: "email" },
      { key: "system_email", label: "System-Absender-E-Mail", type: "email" },
      { key: "signature", label: "Kommunikationssignatur" },
    ],
    hub_address: [
      { key: "recipient_name", label: "Empfänger-Name", ph: "stilltouched Hub · Wareneingang" },
      { key: "street", label: "Straße", ph: "Musterstraße" },
      { key: "house_number", label: "Hausnummer", ph: "12a" },
      { key: "zip", label: "PLZ", ph: "12345" },
      { key: "city", label: "Stadt", ph: "Musterstadt" },
      { key: "country", label: "Land", ph: "Deutschland" },
      { key: "contact_email", label: "Kontakt-E-Mail Hub", type: "email", ph: "hub@stilltouched.com" },
    ],
    paypal: [
      { key: "mode", label: "Modus", type: "select", options: [["sandbox", "Sandbox (Test)"], ["live", "Live (Produktion)"]] },
      { key: "client_id", label: "Client ID" },
      { key: "client_secret", label: "Client Secret", secret: true },
      { key: "webhook_id", label: "Webhook ID" },
    ],
    bank: [
      { key: "bank_name", label: "Bankname", ph: "Deutsche Bank" },
      { key: "account_holder", label: "Kontoinhaber" },
      { key: "iban", label: "IBAN" },
      { key: "bic", label: "BIC" },
      { key: "payment_deadline_days", label: "Zahlungsfrist (Tage)", type: "number" },
      { key: "payment_note", label: "Zahlungshinweis" },
    ],
    payment_methods: [
      { key: "stripe_enabled", label: "Stripe (Kreditkarte) aktiviert", type: "checkbox" },
      { key: "paypal_enabled", label: "PayPal aktiviert", type: "checkbox" },
      { key: "bank_transfer_enabled", label: "Überweisung / Vorkasse aktiviert", type: "checkbox" },
    ],
    finapi: [
      { key: "client_id", label: "Client ID" },
      { key: "client_secret", label: "Client Secret", secret: true },
      { key: "api_url", label: "API URL", ph: "https://sandbox.finapi.io" },
      { key: "enabled", label: "Aktiviert", type: "checkbox" },
    ],
  };

  const fields = FIELD_DEFS[settingKey] || [];
  const titles = { company: "Unternehmensdaten", hub_address: "Hub-Empfängeradresse (für Versandetiketten)", paypal: "PayPal Konfiguration", bank: "Bankverbindung / Vorkasse", payment_methods: "Aktive Zahlungsarten", finapi: "finAPI (Bank-Reconciliation)" };

  return (
    <div data-testid={`admin-setting-${settingKey}`} className="bg-white border border-[#E5E5E5] p-6 space-y-4">
      <h2 className="text-base font-bold text-[#0A0A0A]">{titles[settingKey] || settingKey}</h2>
      {success && <div className="px-3 py-2 bg-green-50 border border-green-200 text-green-600 text-sm">{success}</div>}
      {/* iter155 · Unternehmens-Avatar (Logo) für Platform-Posts */}
      {settingKey === "company" && (
        <CompanyAvatarUploader value={value.avatar_url || ""} onChange={(url) => setValue((p) => ({ ...p, avatar_url: url }))} />
      )}
      {/* iter272 · Dokument-/E-Mail-Logo (Belege + Mail-Kopf). Leer = Unternehmens-Avatar wird genutzt. */}
      {settingKey === "company" && (
        <div className="border border-[#E5E5E5] rounded-lg p-3" data-testid="company-document-logo">
          <p className="text-xs font-semibold text-[#0A0A0A] mb-1">Dokument- & E-Mail-Logo</p>
          <p className="text-[11px] text-[#888] mb-2">Wird auf PDF-Belegen und im E-Mail-Kopf verwendet (auf schwarzem Balken). Wenn leer, greift automatisch der Unternehmens-Avatar.</p>
          <CompanyAvatarUploader title="Dokument- & E-Mail-Logo" aspect={3} darkBg pngOutput value={value.document_logo_url || ""} onChange={(url) => setValue((p) => ({ ...p, document_logo_url: url }))} />
        </div>
      )}
      {fields.map((f) => {
        if (f.type === "checkbox") return (
          <label key={f.key} className="flex items-center gap-3 p-3 border border-[#E5E5E5] cursor-pointer hover:bg-[#FAFAFA]">
            <input type="checkbox" checked={value[f.key] || false} onChange={(e) => setValue(p => ({ ...p, [f.key]: e.target.checked }))} className="w-4 h-4 accent-[#0A0A0A]" />
            <span className="text-sm text-[#0A0A0A]">{f.label}</span>
          </label>
        );
        if (f.type === "select") return (
          <div key={f.key}>
            <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{f.label}</label>
            <select value={value[f.key] || f.options[0][0]} onChange={(e) => setValue(p => ({ ...p, [f.key]: e.target.value }))} className="mt-1 w-full h-10 px-3 border border-[#E5E5E5] text-sm bg-white">
              {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        );
        return <FieldInput key={f.key} label={f.label} value={value[f.key] || ""} onChange={(v) => setValue(p => ({ ...p, [f.key]: v }))} type={f.type || "text"} placeholder={f.ph || ""} secret={f.secret} />;
      })}
      <button onClick={save} disabled={saving} className="h-10 px-6 bg-[#0A0A0A] text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"><Save size={14} /> {saving ? "..." : "Speichern"}</button>
    </div>
  );
}

// ─── SMTP Section ───────────────────────────────────────
function SMTPSection() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/smtp-accounts"); setAccounts(data.accounts || []); } catch { /* ignore */ } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const save = async () => { setSaving(true); try { await api.post("/admin/smtp-accounts", editing === "new" ? form : { ...form, account_id: editing }); setEditing(null); load(); } catch { /* ignore */ } finally { setSaving(false); } };
  const remove = async (id) => { try { await api.delete(`/admin/smtp-accounts/${id}`); load(); } catch { /* ignore */ } };
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));

  if (loading) return <Spinner />;

  return (
    <div data-testid="admin-smtp">
      <div className="bg-white border border-[#E5E5E5] p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-[#0A0A0A]">E-Mail / SMTP Konten</h2>
          <button onClick={() => { setForm({ label: "", host: "", port: 587, username: "", password: "", from_email: "", from_name: "stilltouched", use_tls: true, is_default: false }); setEditing("new"); }}
            className="h-8 px-3 bg-[#0A0A0A] text-white text-xs font-medium flex items-center gap-1"><Plus size={12} /> Neues Konto</button>
        </div>
        {accounts.map((a) => (
          <div key={a.account_id} className="flex items-center gap-3 p-3 border border-[#E5E5E5] mb-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{a.label} {a.is_default && <span className="text-[10px] text-green-600">(Standard)</span>}</p>
              <p className="text-xs text-[#999]">{a.from_email} via {a.host}:{a.port}</p>
            </div>
            <button onClick={() => { setForm(a); setEditing(a.account_id); }} className="text-xs px-2 py-1 border border-[#E5E5E5]">Bearbeiten</button>
            <button onClick={() => remove(a.account_id)} className="text-xs text-red-500 px-2 py-1 border border-red-200"><Trash2 size={12} /></button>
          </div>
        ))}
        {accounts.length === 0 && !editing && <p className="text-sm text-[#999]">Keine SMTP-Konten konfiguriert.</p>}
      </div>
      {editing && (
        <div className="bg-white border border-[#E5E5E5] p-6 mt-4 space-y-3">
          <h3 className="text-sm font-bold">{editing === "new" ? "Neues SMTP-Konto" : "Bearbeiten"}</h3>
          <FieldInput label="Bezeichnung" value={form.label || ""} onChange={(v) => u("label", v)} />
          <FieldInput label="SMTP Host" value={form.host || ""} onChange={(v) => u("host", v)} />
          <FieldInput label="Port" value={form.port || 587} onChange={(v) => u("port", parseInt(v) || 587)} type="number" />
          <FieldInput label="Benutzername" value={form.username || ""} onChange={(v) => u("username", v)} />
          <FieldInput label="Passwort" value={form.password || ""} onChange={(v) => u("password", v)} secret />
          <FieldInput label="Absender-E-Mail" value={form.from_email || ""} onChange={(v) => u("from_email", v)} />
          <FieldInput label="Absender-Name" value={form.from_name || ""} onChange={(v) => u("from_name", v)} />
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.use_tls || false} onChange={(e) => u("use_tls", e.target.checked)} className="accent-[#0A0A0A]" /><span className="text-sm text-[#666]">TLS</span></label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_default || false} onChange={(e) => u("is_default", e.target.checked)} className="accent-[#0A0A0A]" /><span className="text-sm text-[#666]">Standard</span></label>
          <div className="flex gap-2">
            <button onClick={() => setEditing(null)} className="h-9 px-4 border text-xs text-[#666]">Abbrechen</button>
            <button onClick={save} disabled={saving} className="h-9 px-4 bg-[#0A0A0A] text-white text-xs font-medium disabled:opacity-50"><Save size={12} /> Speichern</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Shipping Section ───────────────────────────────────
function ShippingSection() {
  const [dhl, setDhl] = useState({});
  const [dp, setDp] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");

  useEffect(() => { (async () => { try { const [d1, d2] = await Promise.all([api.get("/admin/settings/shipping_dhl"), api.get("/admin/settings/shipping_deutschepost")]); setDhl(d1.data.value || {}); setDp(d2.data.value || {}); } catch { /* ignore */ } finally { setLoading(false); } })(); }, []);

  const saveDHL = async () => { setSaving(true); try { await api.post("/admin/settings/shipping_dhl", { value: dhl }); setSuccess("DHL gespeichert"); setTimeout(() => setSuccess(""), 3000); } catch { /* ignore */ } finally { setSaving(false); } };
  const saveDP = async () => { setSaving(true); try { await api.post("/admin/settings/shipping_deutschepost", { value: dp }); setSuccess("Deutsche Post gespeichert"); setTimeout(() => setSuccess(""), 3000); } catch { /* ignore */ } finally { setSaving(false); } };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      {success && <div className="px-3 py-2 bg-green-50 border border-green-200 text-green-600 text-sm">{success}</div>}
      <div className="bg-white border border-[#E5E5E5] p-6 space-y-4">
        <h2 className="text-base font-bold text-[#0A0A0A]">DHL Geschäftskunden-API (Pakete)</h2>
        <FieldInput label="API User" value={dhl.api_user || ""} onChange={(v) => setDhl(p => ({ ...p, api_user: v }))} />
        <FieldInput label="API Password" value={dhl.api_password || ""} onChange={(v) => setDhl(p => ({ ...p, api_password: v }))} secret />
        <FieldInput label="EKP (Kundennummer)" value={dhl.ekp || ""} onChange={(v) => setDhl(p => ({ ...p, ekp: v }))} />
        <FieldInput label="Absender-Name" value={dhl.sender_name || ""} onChange={(v) => setDhl(p => ({ ...p, sender_name: v }))} />
        <FieldInput label="Absender-Adresse" value={dhl.sender_address || ""} onChange={(v) => setDhl(p => ({ ...p, sender_address: v }))} />
        <FieldInput label="PLZ" value={dhl.sender_zip || ""} onChange={(v) => setDhl(p => ({ ...p, sender_zip: v }))} />
        <FieldInput label="Stadt" value={dhl.sender_city || ""} onChange={(v) => setDhl(p => ({ ...p, sender_city: v }))} />
        <button onClick={saveDHL} disabled={saving} className="h-10 px-6 bg-[#0A0A0A] text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"><Save size={14} /> Speichern</button>
      </div>
      <div className="bg-white border border-[#E5E5E5] p-6 space-y-4">
        <h2 className="text-base font-bold text-[#0A0A0A]">Deutsche Post API (Briefe)</h2>
        <FieldInput label="API Key" value={dp.api_key || ""} onChange={(v) => setDp(p => ({ ...p, api_key: v }))} secret />
        <FieldInput label="Partner ID" value={dp.partner_id || ""} onChange={(v) => setDp(p => ({ ...p, partner_id: v }))} />
        <button onClick={saveDP} disabled={saving} className="h-10 px-6 bg-[#0A0A0A] text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"><Save size={14} /> Speichern</button>
      </div>
    </div>
  );
}


// ─── Messages Moderation ────────────────────────────────
function MessagesModerationSection() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [area, setArea] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/messages/threads${area ? `?area=${area}` : ""}`); setThreads(data.threads || []); } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [area]);
  useEffect(() => { load(); }, [load]);

  const loadDetail = async (threadId) => {
    try { const { data } = await api.get(`/admin/messages/threads/${threadId}`); setDetail(data); } catch { /* ignore */ }
  };

  if (detail) {
    return (
      <div>
        <button onClick={() => setDetail(null)} className="h-8 px-3 border border-[#E5E5E5] text-xs text-[#666] mb-4 flex items-center gap-1"><ChevronLeft size={14} /> Zurück</button>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <p className="text-xs text-[#999] uppercase tracking-wider mb-3">Thread: {detail.area} – {detail.thread?.thread_id?.slice(0, 8)}</p>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(detail.messages || []).map((m, i) => (
              <div key={m.message_id || i} className="p-2 bg-[#FAFAFA] border border-[#F0F0F0]">
                <p className="text-[10px] text-[#999]">{m.sender_id?.slice(0, 8)} · {m.role || ""} · {m.created_at ? new Date(m.created_at).toLocaleString("de-DE") : ""}</p>
                <p className="text-sm text-[#0A0A0A] mt-1">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-messages-mod">
      <h1 className="text-xl font-bold text-[#0A0A0A] mb-4">Nachrichten-Moderation</h1>
      <div className="flex gap-2 mb-4">
        {[["", "Alle"], ["social", "Social DMs"], ["marketplace", "Marketplace"]].map(([v, l]) => (
          <button key={v} onClick={() => setArea(v)} className={`h-8 px-3 text-xs border ${area === v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5]"}`}>{l}</button>
        ))}
      </div>
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-[#FAFAFA]">
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Bereich</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Teilnehmer</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Letzte Nachricht</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Aktualisiert</th>
            <th className="text-right px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Aktion</th>
          </tr></thead>
          <tbody className="divide-y divide-[#F0F0F0]">
            {loading ? <tr><td colSpan={5} className="py-8 text-center"><Spinner /></td></tr> :
             threads.length === 0 ? <tr><td colSpan={5} className="py-8 text-center text-[#999]">Keine Threads.</td></tr> :
             threads.map((t) => (
              <tr key={t.thread_id} className="hover:bg-[#FAFAFA]">
                <td className="px-3 py-2"><span className="px-2 py-0.5 text-[10px] font-medium border bg-[#F0F0F0] border-[#E5E5E5]">{t.area}</span></td>
                <td className="px-3 py-2 text-xs text-[#666]">
                  {t.participants ? t.participants.map(p => p.display_name || "—").join(" ↔ ") : `${t.buyer_profile_id?.slice(0,6)} ↔ ${t.seller_profile_id?.slice(0,6)}`}
                </td>
                <td className="px-3 py-2 text-xs text-[#666] max-w-xs truncate">{t.last_message || "..."}</td>
                <td className="px-3 py-2 text-xs text-[#CCC]">{t.updated_at ? new Date(t.updated_at).toLocaleDateString("de-DE") : ""}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => loadDetail(t.thread_id)} className="h-7 px-2 bg-[#0A0A0A] text-white text-[10px]">Einsehen</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Message Reports (Sub-Batch 4c.5.3) ─────────────────
function MessageReportsSection() {
  const [reports, setReports] = useState([]);
  const [counts, setCounts] = useState({ open_count: 0, total_count: 0 });
  const [filters, setFilters] = useState({ status: "OPEN", shop_area: "", reason_code: "" });
  const [loading, setLoading] = useState(true);
  const [detailReportId, setDetailReportId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.shop_area) params.shop_area = filters.shop_area;
      if (filters.reason_code) params.reason_code = filters.reason_code;
      const { data } = await api.get("/modul-d/admin/reports", { params });
      setReports(data.items || []);
      setCounts({ open_count: data.open_count || 0, total_count: data.total_count || 0 });
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const REASON_LABELS = {
    HARASSMENT_THREAT: "Belästigung / Drohung",
    SEXUAL_INAPPROPRIATE: "Sexuell unangemessen",
    CHILD_SAFETY: "Minderjährigenschutz",
    CIRCUMVENTION_ATTEMPT: "Plattform-Umgehung",
    CONTACT_DATA: "Kontaktdaten",
    SPAM: "Spam",
    OTHER: "Sonstiges",
  };
  const STATUS_LABELS = {
    OPEN: { label: "Offen", color: "bg-amber-100 text-amber-800 border-amber-300" },
    UNDER_REVIEW: { label: "In Prüfung", color: "bg-blue-100 text-blue-800 border-blue-300" },
    ACTION_TAKEN: { label: "Maßnahme ergriffen", color: "bg-red-100 text-red-800 border-red-300" },
    DISMISSED: { label: "Verworfen", color: "bg-gray-100 text-gray-600 border-gray-300" },
  };
  const AREA_LABELS = { SOCIAL: "Social", MARKETPLACE: "Marketplace", STILLTOUCHEDX: "StX" };

  if (detailReportId) {
    return <MessageReportDetail
      reportId={detailReportId}
      onBack={() => { setDetailReportId(null); load(); }}
      reasonLabels={REASON_LABELS}
      statusLabels={STATUS_LABELS}
      areaLabels={AREA_LABELS}
    />;
  }

  return (
    <div data-testid="admin-message-reports">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-[#0A0A0A]">Gemeldete Nachrichten</h1>
          <p className="text-xs text-[#666] mt-1">
            {counts.open_count} offen · {counts.total_count} insgesamt
          </p>
        </div>
        <button onClick={load} className="h-8 px-3 text-xs border border-[#E5E5E5] text-[#666] hover:bg-[#FAFAFA]">
          Aktualisieren
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E5E5] p-3 mb-4 flex flex-wrap gap-3">
        <div>
          <label htmlFor="filter-status" className="text-[10px] font-bold text-[#999] uppercase block mb-1">Status</label>
          <select id="filter-status" data-testid="filter-status" value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            className="h-8 px-2 text-xs border border-[#E5E5E5]">
            <option value="">Alle</option>
            <option value="OPEN">Offen</option>
            <option value="UNDER_REVIEW">In Prüfung</option>
            <option value="ACTION_TAKEN">Maßnahme</option>
            <option value="DISMISSED">Verworfen</option>
          </select>
        </div>
        <div>
          <label htmlFor="filter-area" className="text-[10px] font-bold text-[#999] uppercase block mb-1">Bereich</label>
          <select id="filter-area" data-testid="filter-area" value={filters.shop_area} onChange={(e) => setFilters(f => ({ ...f, shop_area: e.target.value }))}
            className="h-8 px-2 text-xs border border-[#E5E5E5]">
            <option value="">Alle</option>
            <option value="SOCIAL">Social</option>
            <option value="MARKETPLACE">Marketplace</option>
            <option value="STILLTOUCHEDX">StX</option>
          </select>
        </div>
        <div>
          <label htmlFor="filter-reason" className="text-[10px] font-bold text-[#999] uppercase block mb-1">Grund</label>
          <select id="filter-reason" data-testid="filter-reason" value={filters.reason_code} onChange={(e) => setFilters(f => ({ ...f, reason_code: e.target.value }))}
            className="h-8 px-2 text-xs border border-[#E5E5E5]">
            <option value="">Alle</option>
            {Object.entries(REASON_LABELS).map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-[#FAFAFA]">
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Datum</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Bereich</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Grund</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Reporter</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Gemeldeter User</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Auszug</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Status</th>
            <th className="text-right px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Aktion</th>
          </tr></thead>
          <tbody className="divide-y divide-[#F0F0F0]">
            {loading ? (
              <tr><td colSpan={8} className="py-8 text-center"><Spinner /></td></tr>
            ) : reports.length === 0 ? (
              <tr><td colSpan={8} className="py-8 text-center text-[#999]">Keine Meldungen.</td></tr>
            ) : reports.map((r) => {
              const status = STATUS_LABELS[r.status] || { label: r.status, color: "bg-gray-100 text-gray-600" };
              return (
                <tr key={r.report_id} className="hover:bg-[#FAFAFA]">
                  <td className="px-3 py-2 text-[11px] text-[#666] whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "—"}
                  </td>
                  <td className="px-3 py-2"><span className="px-2 py-0.5 text-[10px] font-medium border bg-[#F0F0F0] border-[#E5E5E5]">{AREA_LABELS[r.shop_area] || r.shop_area}</span></td>
                  <td className="px-3 py-2 text-xs text-[#0A0A0A]">{REASON_LABELS[r.reason_code] || r.reason_code}</td>
                  <td className="px-3 py-2 text-[11px] text-[#666] max-w-[140px] truncate">{r.reporter_email || r.reporter_user_id?.slice(0, 8)}</td>
                  <td className="px-3 py-2 text-[11px] text-[#666] max-w-[140px] truncate">{r.reported_email || r.reported_user_id?.slice(0, 8)}</td>
                  <td className="px-3 py-2 text-xs text-[#666] max-w-xs truncate italic">{r.content_snapshot}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 text-[10px] font-medium border ${status.color}`}>{status.label}</span></td>
                  <td className="px-3 py-2 text-right">
                    <button data-testid={`open-report-${r.report_id}`}
                      onClick={() => setDetailReportId(r.report_id)}
                      className="h-7 px-2 bg-[#0A0A0A] text-white text-[10px]">
                      Prüfen
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Single Report Detail + Review (Sub-Batch 4c.5.3) ───
function MessageReportDetail({ reportId, onBack, reasonLabels, statusLabels, areaLabels }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Review form state
  const [reviewStatus, setReviewStatus] = useState(""); // ACTION_TAKEN | DISMISSED
  const [adminNote, setAdminNote] = useState("");
  const [closeConv, setCloseConv] = useState(false);
  const [suspendEnabled, setSuspendEnabled] = useState(false);
  const [suspendDays, setSuspendDays] = useState(7);
  const [suspendReason, setSuspendReason] = useState("Verstoß gegen Plattform-Richtlinien");
  const [dmEnabled, setDmEnabled] = useState(false);
  const [dmText, setDmText] = useState("");
  const [notifyReporter, setNotifyReporter] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/modul-d/admin/reports/${reportId}`);
      setReport(data);
    } catch (e) {
      setError(e?.response?.data?.detail?.code || "Fehler beim Laden");
    } finally { setLoading(false); }
  }, [reportId]);
  useEffect(() => { load(); }, [load]);

  const submitReview = async () => {
    if (!reviewStatus) { setError("Bitte Status wählen"); return; }
    setReviewing(true);
    setError(null);
    setSuccess(null);
    try {
      const body = {
        status: reviewStatus,
        admin_note: adminNote.trim() || null,
        close_conversation: closeConv,
        notify_reporter: notifyReporter,
      };
      if (suspendEnabled) {
        body.suspend_reported_user = {
          duration_days: Math.max(1, parseInt(suspendDays, 10) || 7),
          reason: suspendReason.trim() || "Verstoß gegen Plattform-Richtlinien",
        };
      }
      if (dmEnabled && dmText.trim()) {
        body.admin_direct_message = { text: dmText.trim() };
      }
      const { data } = await api.post(`/modul-d/admin/reports/${reportId}/review`, body);
      setReport(data.report);
      setSuccess("Review erfolgreich abgeschlossen.");
      // Reset form
      setReviewStatus(""); setAdminNote(""); setCloseConv(false);
      setSuspendEnabled(false); setDmEnabled(false); setDmText("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (detail?.code || "Review fehlgeschlagen"));
    } finally {
      setReviewing(false);
    }
  };

  if (loading) return <div className="py-12 flex justify-center"><Spinner /></div>;
  if (!report) return (
    <div>
      <button onClick={onBack} className="h-8 px-3 border border-[#E5E5E5] text-xs text-[#666] mb-4 flex items-center gap-1"><ChevronLeft size={14} /> Zurück</button>
      <p className="text-sm text-red-600">Report nicht gefunden.</p>
    </div>
  );

  const status = statusLabels[report.status] || { label: report.status, color: "bg-gray-100 text-gray-600" };
  const isClosed = report.status === "ACTION_TAKEN" || report.status === "DISMISSED";

  return (
    <div>
      <button data-testid="back-to-list" onClick={onBack} className="h-8 px-3 border border-[#E5E5E5] text-xs text-[#666] mb-4 flex items-center gap-1">
        <ChevronLeft size={14} /> Zurück zur Liste
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT: Report Detail */}
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-[10px] text-[#999] uppercase tracking-wider">Report</p>
              <p className="text-xs font-mono text-[#0A0A0A]">{report.report_id?.slice(0, 12)}…</p>
            </div>
            <span className={`px-2 py-1 text-[10px] font-medium border ${status.color}`}>{status.label}</span>
          </div>

          <div className="space-y-3 text-sm">
            <div>
              <p className="text-[10px] text-[#999] uppercase tracking-wider">Bereich · Grund</p>
              <p className="text-[#0A0A0A]">
                <span className="px-2 py-0.5 text-[10px] font-medium border bg-[#F0F0F0] border-[#E5E5E5] mr-2">{areaLabels[report.shop_area] || report.shop_area}</span>
                <span className="font-semibold">{reasonLabels[report.reason_code] || report.reason_code}</span>
              </p>
            </div>

            <div>
              <p className="text-[10px] text-[#999] uppercase tracking-wider">Reporter</p>
              <p className="text-[#0A0A0A] text-xs">{report.reporter_email || report.reporter_user_id}</p>
            </div>
            <div>
              <p className="text-[10px] text-[#999] uppercase tracking-wider">Gemeldeter User</p>
              <p className="text-[#0A0A0A] text-xs">{report.reported_email || report.reported_user_id}</p>
            </div>

            <div>
              <p className="text-[10px] text-[#999] uppercase tracking-wider">Erstellt</p>
              <p className="text-[#0A0A0A] text-xs">{report.created_at ? new Date(report.created_at).toLocaleString("de-DE") : "—"}</p>
            </div>

            <div>
              <p className="text-[10px] text-[#999] uppercase tracking-wider">Gemeldete Nachricht (Original)</p>
              <div className="mt-1 p-3 bg-amber-50 border border-amber-200">
                <p className="text-xs text-amber-900 whitespace-pre-wrap leading-relaxed">
                  {report.content_original || report.content_snapshot || "—"}
                </p>
                {report.content_original && report.content_snapshot && report.content_original !== report.content_snapshot && (
                  <p className="text-[10px] text-amber-700 italic mt-2">
                    Snapshot zum Zeitpunkt des Reports: &bdquo;{report.content_snapshot}&ldquo;
                  </p>
                )}
              </div>
              {!report.message_still_available && (
                <p className="text-[10px] text-[#999] italic mt-1">Original-Nachricht nicht mehr im System verfügbar (gelöscht / Conv geschlossen).</p>
              )}
            </div>

            {report.comment && (
              <div>
                <p className="text-[10px] text-[#999] uppercase tracking-wider">Reporter-Kommentar</p>
                <p className="text-xs text-[#666] italic mt-1">&bdquo;{report.comment}&ldquo;</p>
              </div>
            )}

            {isClosed && (
              <div className="mt-3 pt-3 border-t border-[#E5E5E5]">
                <p className="text-[10px] text-[#999] uppercase tracking-wider">Review-Ergebnis</p>
                <p className="text-xs text-[#0A0A0A] mt-1">Reviewer: {report.reviewed_by}</p>
                <p className="text-xs text-[#999]">Am: {report.reviewed_at ? new Date(report.reviewed_at).toLocaleString("de-DE") : "—"}</p>
                {report.admin_note && <p className="text-xs text-[#666] mt-1">Notiz: {report.admin_note}</p>}
                {report.actions_taken && report.actions_taken.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {report.actions_taken.map((a) => (
                      <span key={a} className="px-2 py-0.5 text-[10px] bg-red-50 text-red-700 border border-red-200">{a}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Review Form */}
        <div className="bg-white border border-[#E5E5E5] p-4">
          <h3 className="text-sm font-bold text-[#0A0A0A] mb-3">Review-Aktion</h3>
          {isClosed ? (
            <div className="p-3 bg-gray-50 border border-gray-200">
              <p className="text-xs text-gray-600">Dieser Report wurde bereits abgeschlossen.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Status */}
              <div>
                <label htmlFor="review-status" className="text-[10px] font-bold text-[#0A0A0A] uppercase block mb-1">
                  Status <span className="text-red-600">*</span>
                </label>
                <select id="review-status" data-testid="review-status" value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value)} disabled={reviewing}
                  className="w-full h-9 px-2 text-sm border border-[#E5E5E5]">
                  <option value="">Bitte wählen…</option>
                  <option value="ACTION_TAKEN">Maßnahme ergriffen</option>
                  <option value="DISMISSED">Verworfen (kein Verstoß)</option>
                </select>
              </div>

              {/* Admin Note */}
              <div>
                <label htmlFor="admin-note" className="text-[10px] font-bold text-[#0A0A0A] uppercase block mb-1">Interne Notiz</label>
                <textarea id="admin-note" data-testid="admin-note" value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
                  rows={2} disabled={reviewing} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] resize-none"
                  placeholder="Begründung der Entscheidung..." />
              </div>

              {/* Close Conversation */}
              {(report.conversation_id || report.thread_id || report.presale_id) && (
                <label className="flex items-start gap-2 cursor-pointer">
                  <input data-testid="action-close-conv" type="checkbox" checked={closeConv} onChange={(e) => setCloseConv(e.target.checked)} disabled={reviewing} className="mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-[#0A0A0A]">Konversation schließen</p>
                    <p className="text-[10px] text-[#999]">Beide Teilnehmer können dann nicht mehr antworten.</p>
                  </div>
                </label>
              )}

              {/* Suspend User */}
              <div className="p-3 border border-[#E5E5E5]">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input data-testid="action-suspend" type="checkbox" checked={suspendEnabled} onChange={(e) => setSuspendEnabled(e.target.checked)} disabled={reviewing} className="mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-red-700">User sperren</p>
                    <p className="text-[10px] text-[#999]">Sperre für n Tage – User kann nicht mehr senden.</p>
                  </div>
                </label>
                {suspendEnabled && (
                  <div className="mt-2 ml-5 space-y-2">
                    <div className="flex items-center gap-2">
                      <input data-testid="suspend-days" type="number" min={1} max={3650} value={suspendDays} onChange={(e) => setSuspendDays(e.target.value)} disabled={reviewing}
                        className="w-20 h-8 px-2 text-xs border border-[#E5E5E5]" />
                      <span className="text-xs text-[#666]">Tage</span>
                    </div>
                    <input data-testid="suspend-reason" type="text" value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} disabled={reviewing}
                      className="w-full h-8 px-2 text-xs border border-[#E5E5E5]" placeholder="Grund der Sperre" />
                  </div>
                )}
              </div>

              {/* Admin Direct Message */}
              <div className="p-3 border border-[#E5E5E5]">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input data-testid="action-dm" type="checkbox" checked={dmEnabled} onChange={(e) => setDmEnabled(e.target.checked)} disabled={reviewing} className="mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-[#0A0A0A]">Direkt-Nachricht an User</p>
                    <p className="text-[10px] text-[#999]">Schreibe den gemeldeten User vom StillTouched-Team aus an.</p>
                  </div>
                </label>
                {dmEnabled && (
                  <textarea data-testid="dm-text" value={dmText} onChange={(e) => setDmText(e.target.value)} disabled={reviewing}
                    rows={3} maxLength={2000}
                    className="mt-2 w-full px-2 py-1.5 text-sm border border-[#E5E5E5] resize-none"
                    placeholder="Z.B. Verwarnung, Aufklärung über Richtlinien..." />
                )}
              </div>

              {/* Notify reporter */}
              <label className="flex items-start gap-2 cursor-pointer">
                <input data-testid="notify-reporter" type="checkbox" checked={notifyReporter} onChange={(e) => setNotifyReporter(e.target.checked)} disabled={reviewing} className="mt-0.5" />
                <p className="text-xs text-[#0A0A0A]">Reporter über Ergebnis benachrichtigen</p>
              </label>

              {error && (
                <div className="p-2 bg-red-50 border border-red-200 flex items-start gap-2">
                  <AlertTriangle size={14} className="text-red-600 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-red-700">{error}</p>
                </div>
              )}
              {success && (
                <div className="p-2 bg-green-50 border border-green-200 flex items-start gap-2">
                  <Check size={14} className="text-green-600 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-green-700">{success}</p>
                </div>
              )}

              <button data-testid="submit-review"
                onClick={submitReview} disabled={reviewing || !reviewStatus}
                className="w-full h-10 bg-[#0A0A0A] text-white text-xs font-semibold hover:bg-[#222] disabled:opacity-40 flex items-center justify-center gap-1.5">
                <Send size={12} />
                {reviewing ? "Wird abgeschickt..." : "Review abschließen"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Modul D · Policy / Analytics / Feature-Flags / Scheduler (Sub-Batch 4c.4) ──
function ModulDPolicySection() {
  const [tab, setTab] = useState("policy"); // policy | analytics | flags | scheduler
  const TABS = [
    { key: "policy", label: "Policy & Test" },
    { key: "analytics", label: "Analytics" },
    { key: "flags", label: "Feature-Flags" },
    { key: "scheduler", label: "Scheduler" },
  ];
  return (
    <div data-testid="admin-modul-d-policy">
      <h1 className="text-xl font-bold text-[#0A0A0A] mb-4">Modul D · Konfiguration & Monitoring</h1>
      <div className="flex border-b border-[#E5E5E5] mb-4 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} data-testid={`modul-d-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-xs font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#999] hover:text-[#0A0A0A]"
            }`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "policy" && <PolicyEditorPanel />}
      {tab === "analytics" && <PolicyAnalyticsPanel />}
      {tab === "flags" && <FeatureFlagsPanel />}
      {tab === "scheduler" && <SchedulerPanel />}
    </div>
  );
}

// ─── Policy Editor + Test Tool ──────────────────────────
function PolicyEditorPanel() {
  const [policies, setPolicies] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [savedMsg, setSavedMsg] = useState(null);
  const [error, setError] = useState(null);

  const RULES = [
    { key: "contact_data", label: "Kontaktdaten", desc: "Telefon, E-Mail, Adresse, Social-Handles" },
    { key: "sexual_content", label: "Sexueller Inhalt", desc: "Explizite/vulgäre Begriffe" },
    { key: "circumvention", label: "Plattform-Umgehung", desc: "Treffen, Cash, F&F, Direkt-Kontakt" },
  ];
  const MODE_OPTIONS = ["ENFORCE", "ADVISORY", "OFF"];
  const ACTION_OPTIONS = ["BLOCK", "REDACT"];
  // Pattern-Optionen für sexual_content (Sub-Batch 4c.4-fix-3 Option B)
  const SEXUAL_PATTERN_OPTIONS = [
    { value: "HARD",  label: "HARD (nur extreme Begriffe)" },
    { value: "MP",    label: "MP (Marketplace – broader inkl. vulgär)" },
    { value: "STX",   label: "STX (stilltouchedX – broader inkl. vulgär)" },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/modul-d/admin/policy");
      setPolicies(data.areas || {});
      setError(null);
    } catch (e) {
      setError("Konnte Policy-Konfiguration nicht laden");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateRule = (area, ruleKey, field, value) => {
    setPolicies(prev => ({
      ...prev,
      [area]: {
        ...prev[area],
        [ruleKey]: { ...(prev[area]?.[ruleKey] || {}), [field]: value },
      },
    }));
  };

  const saveArea = async (area) => {
    setSaving(area);
    setSavedMsg(null);
    try {
      const body = {
        contact_data: policies[area]?.contact_data,
        sexual_content: policies[area]?.sexual_content,  // includes pattern field
        circumvention: policies[area]?.circumvention,
        global_mode: policies[area]?.global_mode || "ENFORCE",
      };
      await api.put(`/modul-d/admin/policy/${area}`, body);
      setSavedMsg(`${area} gespeichert.`);
      setTimeout(() => setSavedMsg(null), 3000);
    } catch (e) {
      setError(`Fehler beim Speichern (${area}): ${e?.response?.data?.detail || e?.message}`);
    } finally { setSaving(null); }
  };

  if (loading) return <Spinner />;
  return (
    <div className="space-y-4">
      {error && <div className="p-3 bg-red-50 border border-red-200 text-xs text-red-700">{error}</div>}
      {savedMsg && <div className="p-3 bg-green-50 border border-green-200 text-xs text-green-700">{savedMsg}</div>}

      {["MARKETPLACE", "STILLTOUCHEDX", "SOCIAL"].map(area => {
        const cfg = policies[area] || {};
        return (
          <div key={area} className="bg-white border border-[#E5E5E5] p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-bold text-[#0A0A0A]">{area}</h3>
                <p className="text-[10px] text-[#999]">Global Mode: {cfg.global_mode || "ENFORCE"}</p>
              </div>
              <button data-testid={`policy-save-${area}`}
                onClick={() => saveArea(area)} disabled={saving === area}
                className="h-8 px-3 bg-[#0A0A0A] text-white text-xs font-semibold disabled:opacity-50">
                {saving === area ? "Speichert..." : "Speichern"}
              </button>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#E5E5E5]">
                <th className="text-left py-2 text-[10px] font-bold text-[#999] uppercase">Regel</th>
                <th className="text-left py-2 text-[10px] font-bold text-[#999] uppercase">Mode</th>
                <th className="text-left py-2 text-[10px] font-bold text-[#999] uppercase">Action</th>
                <th className="text-left py-2 text-[10px] font-bold text-[#999] uppercase">Pattern</th>
              </tr></thead>
              <tbody>
                {RULES.map(rule => {
                  const r = cfg[rule.key] || {};
                  const isSexual = rule.key === "sexual_content";
                  return (
                    <tr key={rule.key} className="border-b border-[#F0F0F0]">
                      <td className="py-2 pr-3">
                        <p className="text-xs font-medium text-[#0A0A0A]">{rule.label}</p>
                        <p className="text-[10px] text-[#999]">{rule.desc}</p>
                      </td>
                      <td className="py-2 pr-3">
                        <select data-testid={`policy-${area}-${rule.key}-mode`}
                          value={r.mode || "ENFORCE"}
                          onChange={(e) => updateRule(area, rule.key, "mode", e.target.value)}
                          className="h-8 px-2 text-xs border border-[#E5E5E5]">
                          {MODE_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </td>
                      <td className="py-2 pr-3">
                        <select data-testid={`policy-${area}-${rule.key}-action`}
                          value={r.action || "BLOCK"}
                          onChange={(e) => updateRule(area, rule.key, "action", e.target.value)}
                          className="h-8 px-2 text-xs border border-[#E5E5E5]">
                          {ACTION_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </td>
                      <td className="py-2">
                        {isSexual ? (
                          <select data-testid={`policy-${area}-${rule.key}-pattern`}
                            value={r.pattern || "HARD"}
                            onChange={(e) => updateRule(area, rule.key, "pattern", e.target.value)}
                            className="h-8 px-2 text-xs border border-[#E5E5E5]">
                            {SEXUAL_PATTERN_OPTIONS.map(p => (
                              <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-[10px] text-[#CCC]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}

      <PolicyTestTool />
    </div>
  );
}

function PolicyTestTool() {
  const [text, setText] = useState("");
  const [area, setArea] = useState("MARKETPLACE");
  // 4 Sender-Profile (Spec-konform mit AVS-Awareness §2.3a)
  const SENDER_PROFILES = {
    SELLER_AVS:    { label: "Seller (AVS)",      role: "SELLER", sender_avs: true,  desc: "Verkäufer*in, altersverifiziert (Standard im STX)" },
    BUYER_AVS:     { label: "Buyer (AVS)",       role: "BUYER",  sender_avs: true,  desc: "Käufer*in mit Altersverifizierung" },
    BUYER_NON_AVS: { label: "Buyer (non-AVS)",   role: "BUYER",  sender_avs: false, desc: "Käufer*in OHNE Altersverifizierung" },
    USER:          { label: "User (Social)",     role: "USER",   sender_avs: false, desc: "Social-Nutzer (DM, ohne AVS)" },
  };
  const [senderProfile, setSenderProfile] = useState("BUYER_AVS");
  const [recipientAvs, setRecipientAvs] = useState(true); // Default: STX-Seller hat immer AVS
  const [result, setResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const runTest = async () => {
    if (!text.trim()) return;
    const profile = SENDER_PROFILES[senderProfile];
    setTesting(true);
    setResult(null); // Reset altes Result, damit der neue Test ein frisches Display bekommt
    try {
      const { data } = await api.post("/modul-d/admin/policy/test", {
        text: text.trim(),
        area,
        sender_role: profile.role,
        sender_avs_verified: profile.sender_avs,
        recipient_avs_verified: recipientAvs,
      });
      setResult(data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Fehler" });
    } finally { setTesting(false); }
  };

  const decisionColor = (d) => ({
    ALLOWED: "bg-green-50 border-green-300 text-green-800",
    REDACTED: "bg-amber-50 border-amber-300 text-amber-800",
    BLOCKED: "bg-red-50 border-red-300 text-red-800",
  }[d] || "bg-gray-50 border-gray-200 text-gray-600");

  return (
    <div className="bg-white border border-[#E5E5E5] p-4">
      <h3 className="text-sm font-bold text-[#0A0A0A] mb-3">Policy-Test-Tool</h3>
      <div className="space-y-3">
        <textarea data-testid="policy-test-text" rows={3}
          value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Test-Nachricht eingeben…"
          className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] resize-none" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Area */}
          <div>
            <label htmlFor="test-area" className="text-[10px] font-bold text-[#999] uppercase block mb-1">Bereich</label>
            <select id="test-area" data-testid="test-area" value={area} onChange={(e) => setArea(e.target.value)}
              className="w-full h-9 px-2 text-sm border border-[#E5E5E5]">
              <option value="MARKETPLACE">Marketplace</option>
              <option value="STILLTOUCHEDX">stilltouchedX</option>
              <option value="SOCIAL">Social</option>
            </select>
          </div>

          {/* Sender Profile */}
          <div>
            <label htmlFor="test-sender" className="text-[10px] font-bold text-[#999] uppercase block mb-1">
              Sender (Rolle + AVS-Status)
            </label>
            <select id="test-sender" data-testid="test-sender" value={senderProfile} onChange={(e) => setSenderProfile(e.target.value)}
              className="w-full h-9 px-2 text-sm border border-[#E5E5E5]">
              {Object.entries(SENDER_PROFILES).map(([k, p]) => (
                <option key={k} value={k}>{p.label}</option>
              ))}
            </select>
            <p className="text-[10px] text-[#999] mt-1 italic">
              {SENDER_PROFILES[senderProfile].desc}
            </p>
          </div>
        </div>

        {/* Recipient AVS Toggle */}
        <div className="p-3 bg-[#FAFAFA] border border-[#E5E5E5]">
          <label className="flex items-start gap-2 cursor-pointer">
            <input data-testid="test-recipient-avs" type="checkbox"
              checked={recipientAvs} onChange={(e) => setRecipientAvs(e.target.checked)}
              className="mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-[#0A0A0A]">Empfänger ist altersverifiziert (AVS)</p>
              <p className="text-[10px] text-[#999] mt-0.5">
                Relevant für STX-Sexual-Content-Bypass (§2.3a). In STX sind Seller standardmäßig AVS-verifiziert.
              </p>
            </div>
          </label>
        </div>

        <button data-testid="policy-test-run" onClick={runTest} disabled={testing || !text.trim()}
          className="h-9 px-4 bg-[#0A0A0A] text-white text-xs font-semibold disabled:opacity-50">
          {testing ? "Test läuft..." : "Testen"}
        </button>

        {result && (
          <div className={`p-3 border ${decisionColor(result.decision)}`}>
            {result.error ? (
              <p className="text-xs">{result.error}</p>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] font-bold uppercase">Entscheidung:</span>
                  <span className="text-sm font-bold">{result.decision}</span>
                </div>
                {/* Show context that was used */}
                <p className="text-[10px] mb-2 opacity-75">
                  Kontext: {area} · {SENDER_PROFILES[senderProfile].label} · Empfänger-AVS: {recipientAvs ? "✓" : "✗"}
                </p>
                {result.primary_reason_code && (
                  <p className="text-xs"><strong>Reason:</strong> {result.primary_reason_code}</p>
                )}
                {result.primary_reason_text && (
                  <p className="text-xs mt-1 italic">&bdquo;{result.primary_reason_text}&ldquo;</p>
                )}
                <p className="text-xs mt-1"><strong>Display-Text:</strong> {result.content_display}</p>
                {result.redaction_count > 0 && (
                  <p className="text-xs mt-1">Redactions: {result.redaction_count} ({(result.redaction_types || []).join(", ")})</p>
                )}
                {result.rule_groups_triggered && result.rule_groups_triggered.length > 0 && (
                  <p className="text-xs mt-1">Trigger: {result.rule_groups_triggered.join(", ")}</p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Analytics Panel ────────────────────────────────────
function PolicyAnalyticsPanel() {
  const [days, setDays] = useState(7);
  const [filterArea, setFilterArea] = useState("");
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (filterArea) params.area = filterArea;
      const [statsRes, eventsRes] = await Promise.all([
        api.get("/modul-d/admin/analytics/blocks", { params }),
        api.get("/modul-d/admin/analytics/events", { params: { limit: 50, ...(filterArea ? { area: filterArea } : {}) } }),
      ]);
      setStats(statsRes.data);
      setEvents(eventsRes.data.events || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [days, filterArea]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2 flex-wrap">
        <div>
          <label htmlFor="ana-days" className="text-[10px] font-bold text-[#999] uppercase block mb-1">Zeitraum</label>
          <select id="ana-days" value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="h-8 px-2 text-xs border border-[#E5E5E5]">
            <option value={1}>Letzte 24h</option>
            <option value={7}>Letzte 7 Tage</option>
            <option value={30}>Letzte 30 Tage</option>
            <option value={90}>Letzte 90 Tage</option>
          </select>
        </div>
        <div>
          <label htmlFor="ana-area" className="text-[10px] font-bold text-[#999] uppercase block mb-1">Bereich</label>
          <select id="ana-area" value={filterArea} onChange={(e) => setFilterArea(e.target.value)}
            className="h-8 px-2 text-xs border border-[#E5E5E5]">
            <option value="">Alle</option>
            <option value="MARKETPLACE">Marketplace</option>
            <option value="STILLTOUCHEDX">StX</option>
            <option value="SOCIAL">Social</option>
          </select>
        </div>
        <button onClick={load} className="h-8 px-3 text-xs border border-[#E5E5E5] text-[#666] hover:bg-[#FAFAFA]">
          Aktualisieren
        </button>
      </div>

      {loading ? <Spinner /> : stats && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <KPICard label="Events gesamt" value={stats.total_events || 0} />
            <KPICard label="BLOCKED" value={stats.blocked_total || 0} hint="Komplett blockiert" />
            <KPICard label="REDACTED" value={stats.redacted_total || 0} hint="Geredacted" />
          </div>

          {/* By Reason */}
          <div className="bg-white border border-[#E5E5E5] p-4">
            <h3 className="text-sm font-bold text-[#0A0A0A] mb-3">Verteilung nach Grund (Top)</h3>
            {(stats.by_reason || []).length === 0 ? (
              <p className="text-xs text-[#999]">Keine Events im Zeitraum.</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-[#E5E5E5]">
                  <th className="text-left py-2 text-[10px] font-bold text-[#999] uppercase">Bereich</th>
                  <th className="text-left py-2 text-[10px] font-bold text-[#999] uppercase">Grund</th>
                  <th className="text-left py-2 text-[10px] font-bold text-[#999] uppercase">Decision</th>
                  <th className="text-right py-2 text-[10px] font-bold text-[#999] uppercase">Anzahl</th>
                  <th className="w-32"></th>
                </tr></thead>
                <tbody>
                  {(stats.by_reason || []).map((r, i) => {
                    const max = (stats.by_reason || []).reduce((m, x) => Math.max(m, x.count), 1);
                    const pct = Math.round((r.count / max) * 100);
                    return (
                      <tr key={i} className="border-b border-[#F0F0F0]">
                        <td className="py-2 text-xs">{r.area || "—"}</td>
                        <td className="py-2 text-xs">{r.reason || "—"}</td>
                        <td className="py-2 text-xs">{r.decision}</td>
                        <td className="py-2 text-xs text-right font-bold">{r.count}</td>
                        <td className="py-2">
                          <div className="bg-[#F0F0F0] h-2 w-full">
                            <div className="bg-[#0A0A0A] h-2" style={{ width: `${pct}%` }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Recent Events */}
          <div className="bg-white border border-[#E5E5E5] p-4">
            <h3 className="text-sm font-bold text-[#0A0A0A] mb-3">Letzte Events ({events.length})</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-[#E5E5E5]">
                  <th className="text-left py-2 text-[10px] font-bold text-[#999] uppercase">Datum</th>
                  <th className="text-left py-2 text-[10px] font-bold text-[#999] uppercase">Bereich</th>
                  <th className="text-left py-2 text-[10px] font-bold text-[#999] uppercase">Decision</th>
                  <th className="text-left py-2 text-[10px] font-bold text-[#999] uppercase">Reason</th>
                  <th className="text-left py-2 text-[10px] font-bold text-[#999] uppercase">Sender</th>
                </tr></thead>
                <tbody>
                  {events.length === 0 ? (
                    <tr><td colSpan={5} className="py-4 text-center text-[#999] text-xs">Keine Events.</td></tr>
                  ) : events.map((ev, i) => (
                    <tr key={ev.event_id || i} className="border-b border-[#F0F0F0]">
                      <td className="py-2 text-[11px] text-[#666] whitespace-nowrap">
                        {ev.created_at ? new Date(ev.created_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "—"}
                      </td>
                      <td className="py-2 text-[11px]">{ev.area}</td>
                      <td className="py-2 text-[11px]">
                        <span className={`px-1.5 py-0.5 text-[10px] ${
                          ev.decision === "BLOCKED" ? "bg-red-50 text-red-700" :
                          ev.decision === "REDACTED" ? "bg-amber-50 text-amber-700" :
                          "bg-green-50 text-green-700"
                        }`}>{ev.decision}</span>
                      </td>
                      <td className="py-2 text-[11px]">{ev.primary_reason_code || "—"}</td>
                      <td className="py-2 text-[11px]">{ev.sender_role || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Feature Flags Panel ────────────────────────────────
function FeatureFlagsPanel() {
  const [flags, setFlags] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/modul-d/feature-flags");
      setFlags(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setSavedMsg(null);
    try {
      await api.put("/modul-d/admin/feature-flags", flags);
      setSavedMsg("Feature-Flags gespeichert.");
      setTimeout(() => setSavedMsg(null), 3000);
    } catch (e) {
      setSavedMsg(`Fehler: ${e?.response?.data?.detail || e?.message}`);
    } finally { setSaving(false); }
  };

  const toggleEnabled = (area) => {
    setFlags(prev => ({
      ...prev,
      enabled: { ...prev.enabled, [area]: !prev.enabled?.[area] },
    }));
  };
  const toggleAi = (area) => {
    setFlags(prev => ({
      ...prev,
      ai_suggestions: { ...prev.ai_suggestions, [area]: !prev.ai_suggestions?.[area] },
    }));
  };

  if (loading || !flags) return <Spinner />;
  const AREAS = ["MARKETPLACE", "STILLTOUCHEDX"];
  return (
    <div className="bg-white border border-[#E5E5E5] p-4">
      <h3 className="text-sm font-bold text-[#0A0A0A] mb-3">Feature-Flags (Modul D)</h3>
      {savedMsg && <div className="mb-3 p-2 bg-green-50 border border-green-200 text-xs text-green-700">{savedMsg}</div>}
      <table className="w-full text-sm">
        <thead><tr className="border-b border-[#E5E5E5]">
          <th className="text-left py-2 text-[10px] font-bold text-[#999] uppercase">Bereich</th>
          <th className="text-center py-2 text-[10px] font-bold text-[#999] uppercase">Modul aktiv</th>
          <th className="text-center py-2 text-[10px] font-bold text-[#999] uppercase">AI-Suggestions</th>
        </tr></thead>
        <tbody>
          {AREAS.map(area => (
            <tr key={area} className="border-b border-[#F0F0F0]">
              <td className="py-2 text-xs font-medium">{area}</td>
              <td className="py-2 text-center">
                <input type="checkbox" data-testid={`flag-enabled-${area}`}
                  checked={!!flags.enabled?.[area]} onChange={() => toggleEnabled(area)}
                  disabled={saving} />
              </td>
              <td className="py-2 text-center">
                <input type="checkbox" data-testid={`flag-ai-${area}`}
                  checked={!!flags.ai_suggestions?.[area]} onChange={() => toggleAi(area)}
                  disabled={saving} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button data-testid="flags-save" onClick={save} disabled={saving}
        className="mt-3 h-9 px-4 bg-[#0A0A0A] text-white text-xs font-semibold disabled:opacity-50">
        {saving ? "Speichert..." : "Speichern"}
      </button>
    </div>
  );
}

// ─── Scheduler Manual Trigger ───────────────────────────
function SchedulerPanel() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const tick = async () => {
    setRunning(true);
    setResult(null);
    setError(null);
    try {
      const { data } = await api.post("/modul-d/admin/scheduler/tick");
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message);
    } finally { setRunning(false); }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] p-4">
      <h3 className="text-sm font-bold text-[#0A0A0A] mb-1">Modul-D-Scheduler</h3>
      <p className="text-xs text-[#666] mb-3">
        Manuell die periodischen Jobs auslösen: After-Sale-Chat-Close (14d), 48h-Reminder, Listing-Cascade.
      </p>
      <button data-testid="scheduler-tick" onClick={tick} disabled={running}
        className="h-9 px-4 bg-[#0A0A0A] text-white text-xs font-semibold disabled:opacity-50">
        {running ? "Läuft..." : "Scheduler jetzt ausführen"}
      </button>
      {error && <div className="mt-3 p-3 bg-red-50 border border-red-200 text-xs text-red-700">{error}</div>}
      {result && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200">
          <p className="text-xs font-bold text-green-800 mb-1">Ergebnis:</p>
          <ul className="text-xs text-green-700 space-y-0.5">
            <li>• Geschlossene Chats (14d): {result.closed ?? 0}</li>
            <li>• Reminder verschickt (48h): {result.reminders ?? 0}</li>
            <li>• Listing-Cascades: {result.cascades ?? 0}</li>
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Hub-Eingang (Sub-Batch A-3, Modul A + Rescue-Bag intake) ─────
function HubIntakeSection() {
  const [params] = useSearchParams();
  const initialTab = params.get("hub_tab") || "queue";
  const [tab, setTab] = useState(initialTab); // queue | hold | scan | rescue
  const TABS = [
    { key: "scan", label: "QR scannen", icon: ScanLine },
    { key: "rescue", label: "Rescue-Bag Intake", icon: Boxes },
    { key: "queue", label: "Eingangs-Queue", icon: Package },
    { key: "hold", label: "Hold-Cases", icon: AlertTriangle },
  ];
  const [detailSid, setDetailSid] = useState(null);

  if (detailSid) {
    return <HubShipmentDetail sid={detailSid} onBack={() => setDetailSid(null)} />;
  }

  return (
    <div data-testid="admin-hub-intake">
      <h1 className="text-xl font-bold text-[#0A0A0A] mb-1">Hub-Eingang</h1>
      <p className="text-xs text-[#666] mb-4">QR-Scan (Pakete + Rescue-Bags), Item-Prüfung, Checkliste, Outbound-Versand.</p>

      <div className="flex border-b border-[#E5E5E5] mb-4 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} data-testid={`hub-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-xs font-semibold whitespace-nowrap border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${
                tab === t.key ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#999] hover:text-[#0A0A0A]"
              }`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "scan" && <HubScanPanel onOpenShipment={setDetailSid} onSwitchToRescue={() => setTab("rescue")} />}
      {tab === "rescue" && (
        <div data-testid="hub-rescue-intake-tab" className="bg-white border border-[#E5E5E5] p-4">
          <p className="text-xs text-[#666] mb-3">
            Wareneingang für aktivierte Rescue-Bags (STX). Fotos + Zustand direkt hier erfassen.
          </p>
          <RescueBagIntakePanel />
        </div>
      )}
      {tab === "queue" && <HubQueuePanel onOpenShipment={setDetailSid} />}
      {tab === "hold" && <HubHoldPanel onOpenShipment={setDetailSid} />}
    </div>
  );
}

// ─── QR Scan Panel ──────────────────────────────────────
function CameraScanModal({ onDetect, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function start() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setReady(true);
          scanLoop();
        }
      } catch (e) {
        setError(
          e?.name === "NotAllowedError"
            ? "Kamera-Zugriff wurde verweigert. Bitte im Browser erlauben und erneut versuchen."
            : "Kamera konnte nicht gestartet werden."
        );
      }
    }

    async function scanLoop() {
      const jsQR = (await import("jsqr")).default;
      const tick = () => {
        if (cancelled) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (video && video.readyState === video.HAVE_ENOUGH_DATA && canvas) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(imgData.data, imgData.width, imgData.height, { inversionAttempts: "dontInvert" });
          if (code && code.data) {
            onDetect(code.data.trim());
            return;
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    }

    start();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, [onDetect]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="camera-scan-modal">
      <div className="bg-white rounded-lg w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
          <p className="text-sm font-bold text-[#0A0A0A]">QR-Code scannen</p>
          <button data-testid="camera-scan-close" onClick={onClose} className="text-[#666]"><X size={18} /></button>
        </div>
        <div className="relative bg-black" style={{ aspectRatio: "4 / 3" }}>
          <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" />
          {ready && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-56 h-56 border-2 border-emerald-400/80 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
            </div>
          )}
          {!ready && !error && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-xs">
              <div className="w-6 h-6 border-2 border-white/40 border-t-white rounded-full animate-spin mr-2" /> Kamera wird gestartet…
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 p-4">
              <p className="text-xs text-white text-center">{error}</p>
            </div>
          )}
          <canvas ref={canvasRef} className="hidden" />
        </div>
        <div className="px-4 py-3 border-t border-[#E5E5E5]">
          <p className="text-[10px] text-[#666]">
            Halte den QR-Code des Pakets vor die Kamera. Erkennung erfolgt automatisch.
          </p>
        </div>
      </div>
    </div>
  );
}

function HubScanPanel({ onOpenShipment, onSwitchToRescue }) {
  const [token, setToken] = useState("");
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [rescueCode, setRescueCode] = useState(null);
  const [showCamera, setShowCamera] = useState(false);

  const doScan = async (overrideToken) => {
    const q = (overrideToken ?? token).trim();
    if (!q) return;
    setScanning(true);
    setError(null);
    setResult(null);
    setRescueCode(null);
    try {
      const { data } = await api.post("/hub/inbound/scan", { qr_token: q });
      setResult(data);
    } catch (e) {
      if (q.length >= 8) {
        try {
          await api.get(`/rescue/warehouse/bag-lookup?qr=${encodeURIComponent(q)}`);
          setRescueCode(q);
          return;
        } catch (_) { /* fall through */ }
      }
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (detail?.message || detail?.code || "Scan fehlgeschlagen"));
    } finally { setScanning(false); }
  };

  const handleCameraDetect = (decoded) => {
    setShowCamera(false);
    setToken(decoded);
    doScan(decoded);
  };

  // If a rescue bag was detected, render the inline intake panel
  if (rescueCode) {
    return (
      <div className="bg-white border border-[#E5E5E5] p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold text-amber-900">Rescue-Bag erkannt</p>
            <p className="text-[11px] text-[#666] mt-0.5">Der Code <span className="font-mono">{rescueCode.toUpperCase()}</span> gehört zu einem aktivierten Rescue-Beutel. Erfasse Zustand & Fotos direkt hier.</p>
          </div>
          <button
            data-testid="hub-scan-rescue-cancel"
            onClick={() => { setRescueCode(null); setToken(""); }}
            className="text-[10px] text-[#666] underline shrink-0">
            Abbrechen
          </button>
        </div>
        <RescueBagIntakePanel initialQr={rescueCode} onSuccess={() => { setRescueCode(null); setToken(""); }} />
        {onSwitchToRescue && (
          <p className="text-[10px] text-[#999] pt-2 border-t border-[#F0F0F0]">
            Tipp: Für mehrere Rescue-Bags in Folge nutze den Tab &bdquo;Rescue-Bag Intake&ldquo; &ndash;{" "}
            <button data-testid="hub-scan-rescue-switch-tab" onClick={onSwitchToRescue} className="underline">jetzt öffnen</button>.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E5E5E5] p-4">
      <p className="text-xs text-[#666] mb-3">
        QR-Code des Pakets scannen oder Fallback-Code (ST-XXXX) eingeben. Re-Scans aus HOLD_REVIEW sind erlaubt.
        <br />
        <span className="text-[#999]">Rescue-Bag-Codes (8-stellige Bag-ID) werden automatisch erkannt und direkt hier bearbeitet.</span>
      </p>
      <div className="flex gap-2">
        <button data-testid="hub-scan-camera-btn"
          onClick={() => setShowCamera(true)}
          className="h-10 px-3 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-[#0A0A0A] hover:text-white transition-colors">
          <Camera size={14} /> Kamera
        </button>
        <input data-testid="hub-scan-input"
          value={token} onChange={(e) => setToken(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && doScan()}
          placeholder="QR-Token, ST-XXXX oder Rescue-Bag-ID"
          className="flex-1 h-10 px-3 text-sm border border-[#E5E5E5] font-mono"
          autoFocus />
        <button data-testid="hub-scan-btn"
          onClick={() => doScan()} disabled={scanning || !token.trim()}
          className="h-10 px-4 bg-[#0A0A0A] text-white text-xs font-semibold disabled:opacity-50">
          {scanning ? "Scanning…" : "Scannen"}
        </button>
      </div>

      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 text-xs text-red-700">{String(error)}</div>
      )}

      {result && result.shipment && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200">
          <p className="text-xs font-bold text-green-800 mb-1">✓ Shipment gefunden</p>
          <p className="text-xs text-green-700">
            {result.order?.title ? <>Bestellung: <strong>{result.order.title}</strong> · </> : null}
            Status: <strong>{adminStatusLabel(result.shipment.status)}</strong> · Items erwartet: <strong>{(result.items || []).length}</strong>
          </p>
          <button data-testid="open-scanned-shipment"
            onClick={() => onOpenShipment(result.shipment.inbound_shipment_id)}
            className="mt-2 h-8 px-3 bg-[#0A0A0A] text-white text-xs">
            Öffnen
          </button>
        </div>
      )}

      {showCamera && (
        <CameraScanModal
          onDetect={handleCameraDetect}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
}

// ─── Queue Panel ────────────────────────────────────────
function HubQueuePanel({ onOpenShipment }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hub/inbound/queue");
      setItems(data.items || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;
  return (
    <div className="bg-white border border-[#E5E5E5]">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E5E5]">
        <p className="text-xs font-bold text-[#0A0A0A]">In Bearbeitung ({items.length})</p>
        <button onClick={load} className="text-xs text-[#666] hover:text-[#0A0A0A]">Aktualisieren</button>
      </div>
      {items.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-[#999]">Keine Shipments in der Queue.</p>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#E5E5E5] bg-[#FAFAFA]">
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Status</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Eingang</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Order</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Items</th>
            <th className="text-right px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Aktion</th>
          </tr></thead>
          <tbody className="divide-y divide-[#F0F0F0]">
            {items.map(s => (
              <tr key={s.inbound_shipment_id} className="hover:bg-[#FAFAFA]">
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 text-[10px] font-medium border ${
                    s.status === "HOLD_REVIEW" ? "bg-red-50 border-red-200 text-red-700" :
                    s.status === "INBOUND_PARTIAL" ? "bg-amber-50 border-amber-200 text-amber-700" :
                    "bg-blue-50 border-blue-200 text-blue-700"
                  }`}>{adminStatusLabel(s.status)}</span>
                </td>
                <td className="px-3 py-2 text-[11px] text-[#666] whitespace-nowrap">
                  {s.scanned_at ? new Date(s.scanned_at).toLocaleString("de-DE", {dateStyle:"short",timeStyle:"short"}) : "—"}
                </td>
                <td className="px-3 py-2 text-xs font-mono text-[#666]">{s.order_id?.slice(0,12)}…</td>
                <td className="px-3 py-2 text-xs">{s.expected_item_count || 0}</td>
                <td className="px-3 py-2 text-right">
                  <button data-testid={`open-shipment-${s.inbound_shipment_id}`}
                    onClick={() => onOpenShipment(s.inbound_shipment_id)}
                    className="h-7 px-2 bg-[#0A0A0A] text-white text-[10px]">Prüfen</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Hold-Cases Panel ───────────────────────────────────
function HubHoldPanel({ onOpenShipment }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hub/hold-cases");
      setCases(data.cases || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Spinner />;
  return (
    <div className="bg-white border border-[#E5E5E5]">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E5E5]">
        <p className="text-xs font-bold text-[#0A0A0A]">Offene Probleme ({cases.length})</p>
        <button onClick={load} className="text-xs text-[#666] hover:text-[#0A0A0A]">Aktualisieren</button>
      </div>
      {cases.length === 0 ? (
        <p className="px-3 py-6 text-center text-xs text-[#999]">Keine offenen Probleme.</p>
      ) : (
        <div className="divide-y divide-[#F0F0F0]">
          {cases.map(c => (
            <div key={c.case_id} className="px-3 py-2 hover:bg-[#FAFAFA] flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-red-700">{c.reason}</p>
                <p className="text-[11px] text-[#666]">{c.notes || "—"}</p>
                <p className="text-[10px] text-[#999]">
                  Shipment: {c.inbound_shipment_id?.slice(0,12)}… · {c.created_at ? new Date(c.created_at).toLocaleString("de-DE", {dateStyle:"short",timeStyle:"short"}) : "—"}
                </p>
              </div>
              <button onClick={() => onOpenShipment(c.inbound_shipment_id)}
                className="h-7 px-2 bg-[#0A0A0A] text-white text-[10px]">Shipment öffnen</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Shipment Detail + Item-Confirm + Final-Confirm ─────
function HubShipmentDetail({ sid, onBack }) {
  const [shipment, setShipment] = useState(null);
  const [items, setItems] = useState([]);
  const [order, setOrder] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: q1 }, { data: q2 }] = await Promise.all([
        api.get("/hub/inbound/queue").catch(() => ({ data: { items: [] } })),
        api.get(`/hub/inbound/items/${sid}`).catch(() => ({ data: null })),
      ]);
      const allItems = q1.items || [];
      const ship = allItems.find(s => s.inbound_shipment_id === sid);
      if (ship) {
        setShipment(ship);
        try {
          const { data: o } = await api.get(`/admin/orders/${ship.order_id}`);
          setOrder(o?.order || o);
        } catch { /* Order-Kontext optional */ }
      }
      if (q2 && q2.items) {
        setItems(q2.items);
      } else if (ship) {
        setItems(ship.items || []);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Fehler beim Laden");
    } finally { setLoading(false); }
  }, [sid]);
  useEffect(() => { load(); }, [load]);

  const confirmItem = async (itemId, present, condition) => {
    setError(null);
    try {
      await api.post("/hub/inbound/confirm-item", {
        order_item_id: itemId,
        item_present: present,
        condition: present ? condition : null,
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Item-Bestätigung fehlgeschlagen");
    }
  };

  if (loading) return <div className="py-8 flex justify-center"><Spinner /></div>;
  if (!shipment) return (
    <div>
      <button onClick={onBack} className="h-8 px-3 border border-[#E5E5E5] text-xs text-[#666] mb-4 flex items-center gap-1"><ChevronLeft size={14}/> Zurück</button>
      <p className="text-sm text-red-600">Shipment nicht gefunden oder nicht mehr in der Queue.</p>
    </div>
  );

  const allChecked = items.length > 0 && items.every(i => i.item_present !== undefined && i.item_present !== null);
  const canFinalConfirm = ["SCANNED_AT_HUB", "INBOUND_PARTIAL", "HOLD_REVIEW"].includes(shipment.status);

  return (
    <div data-testid="hub-shipment-detail">
      <button data-testid="hub-back" onClick={onBack} className="h-8 px-3 border border-[#E5E5E5] text-xs text-[#666] mb-4 flex items-center gap-1"><ChevronLeft size={14}/> Zurück</button>

      <div className="bg-white border border-[#E5E5E5] p-4 mb-4">
        <div className="flex items-start justify-between mb-3 gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] text-[#999] uppercase">Shipment-ID</p>
            <p className="text-xs font-mono text-[#0A0A0A] break-all">{sid}</p>
            <div className="mt-2 flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] text-[#999] uppercase">Bestellnummer</p>
                <p data-testid="hub-detail-order-id" className="text-xs font-mono text-[#0A0A0A] break-all">{shipment.order_id}</p>
              </div>
              <button
                data-testid="hub-detail-copy-order"
                onClick={() => {
                  navigator.clipboard.writeText(shipment.order_id || "");
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1200);
                }}
                className="h-6 px-2 border border-[#E5E5E5] text-[10px] text-[#666] hover:bg-[#F5F5F5]"
                title="Bestellnummer kopieren"
              >
                {copied ? "Kopiert!" : "Kopieren"}
              </button>
            </div>
            {order && (
              <div className="mt-3 pt-3 border-t border-[#F0F0F0] flex items-center gap-3">
                {order.title_photo_url && (
                  <img
                    src={order.title_photo_url}
                    alt=""
                    className="w-12 h-12 object-cover rounded border border-[#E5E5E5] shrink-0"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-[#999] uppercase">Artikel im Paket</p>
                  <p className="text-sm font-semibold text-[#0A0A0A] truncate">{order.title || "—"}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {order.area_scope && (
                      <span className="text-[9px] uppercase tracking-wider bg-[#F5F5F5] border border-[#E5E5E5] text-[#666] px-1.5 py-0.5">
                        {order.area_scope === "STILLTOUCHEDX" ? "STX" : "MP"}
                      </span>
                    )}
                    {order.listing_id && (
                      <a
                        data-testid="hub-detail-listing-link"
                        href={order.area_scope === "STILLTOUCHEDX" ? `/stilltouchedx/${order.listing_id}` : `/listing/${order.listing_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] text-[#0A0A0A] underline underline-offset-2 inline-flex items-center gap-1"
                      >
                        Artikel öffnen <ExternalLink size={10} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <span className={`px-2 py-1 text-[10px] font-medium border shrink-0 ${
            shipment.status === "HOLD_REVIEW" ? "bg-red-50 border-red-200 text-red-700" :
            shipment.status === "RECEIPT_CONFIRMED" ? "bg-green-50 border-green-200 text-green-700" :
            "bg-blue-50 border-blue-200 text-blue-700"
          }`}>{adminStatusLabel(shipment.status)}</span>
        </div>
        {error && <div className="mb-2 p-2 bg-red-50 border border-red-200 text-xs text-red-700">{String(error)}</div>}
        {success && <div className="mb-2 p-2 bg-green-50 border border-green-200 text-xs text-green-700">{success}</div>}
      </div>

      <h3 className="text-sm font-bold text-[#0A0A0A] mb-2">Artikel ({items.length})</h3>
      <div className="space-y-2 mb-4">
        {items.length === 0 ? <p className="text-xs text-[#999]">Keine Artikel.</p> :
          items.map(item => <HubItemRow key={item.order_item_id} item={item} areaScope={order?.area_scope} onConfirm={confirmItem} disabled={!canFinalConfirm} />)
        }
      </div>

      {canFinalConfirm && (
        <div className="flex gap-2">
          <button data-testid="hub-confirm-shipment"
            onClick={() => setShowConfirmModal(true)}
            disabled={!allChecked}
            className="h-10 px-4 bg-green-600 text-white text-xs font-semibold disabled:opacity-40">
            {allChecked ? "Shipment bestätigen" : "Bitte alle Artikel prüfen"}
          </button>
          <button data-testid="hub-report-problem"
            onClick={() => setShowProblemModal(true)}
            className="h-10 px-4 border border-red-300 text-red-700 text-xs font-semibold hover:bg-red-50">
            Problem melden
          </button>
        </div>
      )}

      {shipment.status === "RECEIPT_CONFIRMED" && (
        <HubReadyForOutbound sid={sid} onDone={load} />
      )}
      {shipment.status === "READY_FOR_OUTBOUND" && (
        <HubOutboundShip sid={sid} onDone={load} />
      )}

      {showConfirmModal && (
        <HubConfirmModal sid={sid}
          onClose={() => setShowConfirmModal(false)}
          onSuccess={() => { setShowConfirmModal(false); setSuccess("Shipment bestätigt. Ledger-Credit erstellt."); load(); }}
        />
      )}
      {showProblemModal && (
        <HubProblemModal sid={sid}
          onClose={() => setShowProblemModal(false)}
          onSuccess={() => { setShowProblemModal(false); setSuccess("Problem gemeldet. Status: HOLD_REVIEW"); load(); }}
        />
      )}
    </div>
  );
}

function HubItemRow({ item, areaScope, onConfirm, disabled }) {
  const [condition, setCondition] = useState(item.receipt_condition || "ok");
  const checked = item.item_present !== undefined && item.item_present !== null;
  const isPresent = item.item_present === true;
  const isMissing = item.item_present === false;
  const isDamaged = isPresent && item.receipt_condition !== "ok";
  const listingPath = item.listing_id
    ? (areaScope === "STILLTOUCHEDX" ? `/stilltouchedx/${item.listing_id}` : `/listing/${item.listing_id}`)
    : null;

  return (
    <div data-testid={`hub-item-${item.order_item_id}`} className={`p-3 border ${
      isMissing ? "border-red-200 bg-red-50" :
      isDamaged ? "border-amber-200 bg-amber-50" :
      checked ? "border-green-200 bg-green-50" :
      "border-[#E5E5E5] bg-white"
    }`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          {listingPath ? (
            <a
              data-testid={`hub-item-link-${item.order_item_id}`}
              href={listingPath}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-[#0A0A0A] hover:underline inline-flex items-center gap-1"
            >
              {item.title || item.product_name || "Artikel"}
              <ExternalLink size={10} className="text-[#999]" />
            </a>
          ) : (
            <p className="text-xs font-medium text-[#0A0A0A]">{item.title || item.product_name || "Artikel"}</p>
          )}
          <p className="text-[10px] text-[#666]">Item: {item.order_item_id?.slice(0,12)}…</p>
          {checked && (
            <p className="text-[10px] mt-0.5">
              Status: <strong>{item.receipt_status}</strong>
              {item.receipt_condition && ` · ${item.receipt_condition}`}
            </p>
          )}
        </div>
        {!disabled && !checked && (
          <div className="flex items-center gap-2 shrink-0">
            <select value={condition} onChange={(e) => setCondition(e.target.value)}
              className="h-8 px-2 text-xs border border-[#E5E5E5]">
              <option value="ok">OK</option>
              <option value="damaged">Beschädigt</option>
              <option value="unclear">Unklar</option>
            </select>
            <button data-testid={`item-present-${item.order_item_id}`}
              onClick={() => onConfirm(item.order_item_id, true, condition)}
              className="h-8 px-2 bg-green-600 text-white text-[10px]">Da</button>
            <button data-testid={`item-missing-${item.order_item_id}`}
              onClick={() => onConfirm(item.order_item_id, false, null)}
              className="h-8 px-2 bg-red-600 text-white text-[10px]">Fehlt</button>
          </div>
        )}
      </div>
    </div>
  );
}

function HubConfirmModal({ sid, onClose, onSuccess }) {
  const [cl1, setCl1] = useState(false);
  const [cl2, setCl2] = useState(false);
  const [cl3, setCl3] = useState(false);
  const [condition, setCondition] = useState("ok");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/hub/inbound/${sid}/confirm`, {
        checklist_not_empty: cl1,
        checklist_items_plausible: cl2,
        checklist_no_leakage: cl3,
        shipment_condition: condition,
        notes: notes.trim() || null,
      });
      onSuccess();
    } catch (e) {
      setError(e?.response?.data?.detail || "Bestätigung fehlgeschlagen");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white border border-[#E5E5E5] shadow-xl" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
          <h3 className="text-sm font-bold text-[#0A0A0A]">Shipment final bestätigen</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="px-4 py-3 space-y-3">
          <p className="text-xs text-[#666]">Pflichtcheckliste – bitte sorgfältig prüfen.</p>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" data-testid="cl-not-empty" checked={cl1} onChange={(e)=>setCl1(e.target.checked)} className="mt-0.5" />
            <span className="text-xs">Paket ist nicht leer</span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" data-testid="cl-plausible" checked={cl2} onChange={(e)=>setCl2(e.target.checked)} className="mt-0.5" />
            <span className="text-xs">Inhalt plausibel zur Bestellung</span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" data-testid="cl-no-leakage" checked={cl3} onChange={(e)=>setCl3(e.target.checked)} className="mt-0.5" />
            <span className="text-xs">Keine Leckage / Verunreinigung</span>
          </label>
          <div>
            <label htmlFor="ship-cond" className="text-[10px] font-bold text-[#999] uppercase block mb-1">Gesamt-Zustand</label>
            <select id="ship-cond" data-testid="ship-condition" value={condition} onChange={(e)=>setCondition(e.target.value)} className="w-full h-9 px-2 text-sm border border-[#E5E5E5]">
              <option value="ok">OK</option>
              <option value="damaged">Beschädigt</option>
              <option value="unclear">Unklar</option>
            </select>
          </div>
          <div>
            <label htmlFor="conf-notes" className="text-[10px] font-bold text-[#999] uppercase block mb-1">Notizen (optional)</label>
            <textarea id="conf-notes" data-testid="conf-notes" value={notes} onChange={(e)=>setNotes(e.target.value)} rows={2} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] resize-none" />
          </div>
          {error && <div className="p-2 bg-red-50 border border-red-200 text-[11px] text-red-700">{String(error)}</div>}
          <button data-testid="confirm-submit" onClick={submit} disabled={submitting || !cl1 || !cl2 || !cl3}
            className="w-full h-10 bg-green-600 text-white text-xs font-semibold disabled:opacity-40">
            {submitting ? "Wird bestätigt…" : "Bestätigen"}
          </button>
          <p className="text-[10px] text-[#999]">
            Hinweis: Bei nicht-plausibler Checkliste oder beschädigtem Zustand wechselt das Shipment zu HOLD_REVIEW.
          </p>
        </div>
      </div>
    </div>
  );
}

function HubProblemModal({ sid, onClose, onSuccess }) {
  const [reason, setReason] = useState("DAMAGED");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/hub/inbound/${sid}/report-problem`, {
        reason, notes: notes.trim() || null,
      });
      onSuccess();
    } catch (e) {
      setError(e?.response?.data?.detail || "Meldung fehlgeschlagen");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white border border-[#E5E5E5] shadow-xl" onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
          <h3 className="text-sm font-bold text-red-700">Problem melden</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div>
            <label htmlFor="prob-reason" className="text-[10px] font-bold text-[#999] uppercase block mb-1">Grund</label>
            <select id="prob-reason" data-testid="problem-reason" value={reason} onChange={(e)=>setReason(e.target.value)} className="w-full h-9 px-2 text-sm border border-[#E5E5E5]">
              <option value="DAMAGED">Paket/Inhalt beschädigt</option>
              <option value="WRONG_ITEM">Falscher Artikel</option>
              <option value="MISSING_ITEMS">Artikel fehlen</option>
              <option value="LEAKAGE">Leckage / Verunreinigung</option>
              <option value="UNCLEAR">Unklar / Manuelle Prüfung</option>
              <option value="OTHER">Sonstiges</option>
            </select>
          </div>
          <div>
            <label htmlFor="prob-notes" className="text-[10px] font-bold text-[#999] uppercase block mb-1">Details</label>
            <textarea id="prob-notes" data-testid="problem-notes" value={notes} onChange={(e)=>setNotes(e.target.value)} rows={3} className="w-full px-2 py-1.5 text-sm border border-[#E5E5E5] resize-none" />
          </div>
          {error && <div className="p-2 bg-red-50 border border-red-200 text-[11px] text-red-700">{String(error)}</div>}
          <button data-testid="problem-submit" onClick={submit} disabled={submitting}
            className="w-full h-10 bg-red-600 text-white text-xs font-semibold disabled:opacity-40">
            {submitting ? "Wird gemeldet…" : "Problem melden"}
          </button>
          <p className="text-[10px] text-[#999]">Shipment wechselt zu HOLD_REVIEW. Re-Scan möglich nach Klärung.</p>
        </div>
      </div>
    </div>
  );
}

function HubReadyForOutbound({ sid, onDone }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const trigger = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/hub/inbound/${sid}/ready-for-outbound`);
      onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || "Fehler");
    } finally { setSubmitting(false); }
  };
  return (
    <div className="mt-4 p-3 bg-blue-50 border border-blue-200">
      <p className="text-xs font-bold text-blue-800 mb-2">Bereit für Outbound?</p>
      <p className="text-[11px] text-blue-700 mb-2">Markiert das Shipment als versandbereit. Outbound-Shipment wird automatisch erstellt.</p>
      {error && <div className="mb-2 p-2 bg-red-50 border border-red-200 text-[11px] text-red-700">{String(error)}</div>}
      <button data-testid="ready-for-outbound" onClick={trigger} disabled={submitting}
        className="h-9 px-4 bg-blue-600 text-white text-xs font-semibold disabled:opacity-50">
        {submitting ? "Markiere…" : "Für Outbound vorbereiten"}
      </button>
    </div>
  );
}

function HubOutboundShip({ sid, onDone }) {
  const [outboundId, setOutboundId] = useState("");
  const [outbound, setOutbound] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [carrier, setCarrier] = useState("DHL");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/hub/outbound/by-inbound/${sid}`);
        if (data?.outbound_shipment_id) {
          setOutboundId(data.outbound_shipment_id);
          setOutbound(data);
          if (data.carrier) setCarrier(data.carrier);
          if (data.tracking_number) setTrackingNumber(data.tracking_number);
        }
      } catch { /* Outbound existiert evtl. noch nicht */ }
      finally { setLoading(false); }
    })();
  }, [sid]);

  const trigger = async () => {
    if (!outboundId) { setError("Outbound-Shipment nicht gefunden. Bitte zuerst 'Bereit für Outbound' klicken."); return; }
    if (!trackingNumber.trim()) { setError("Bitte Tracking-Nummer eintragen."); return; }
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/hub/outbound/${outboundId}/ship`, {
        tracking_number: trackingNumber.trim(),
        carrier,
      });
      onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || "Fehler beim Versenden");
    } finally { setSubmitting(false); }
  };
  if (loading) return <div className="mt-4 p-3 bg-blue-50 border border-blue-200"><Spinner /></div>;

  // Bereits versendet — nur Read-only anzeigen (falls Backend-Transition auf CLOSED nicht griff)
  if (outbound && outbound.status === "SHIPPED") {
    return (
      <div className="mt-4 p-3 bg-green-50 border border-green-200">
        <p className="text-xs font-bold text-green-800 mb-1">Bereits versendet</p>
        <p className="text-[11px] text-green-800">
          {outbound.carrier || "—"} · <span className="font-mono">{outbound.tracking_number || "—"}</span>
        </p>
        {outbound.shipped_at && (
          <p className="text-[10px] text-green-700 mt-0.5">
            Versendet: {new Date(outbound.shipped_at).toLocaleString("de-DE")}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mt-4 p-3 bg-blue-50 border border-blue-200">
      <p className="text-xs font-bold text-blue-800 mb-2">Outbound versenden</p>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div>
          <label htmlFor="ob-carrier" className="text-[10px] font-bold text-blue-700 uppercase block mb-1">Versender</label>
          <select id="ob-carrier" data-testid="ob-carrier" value={carrier} onChange={(e)=>setCarrier(e.target.value)} className="w-full h-9 px-2 text-sm border border-[#E5E5E5]">
            <option value="DHL">DHL</option>
            <option value="DEUTSCHE_POST">Deutsche Post</option>
            <option value="DPD">DPD</option>
            <option value="HERMES">Hermes</option>
            <option value="UPS">UPS</option>
            <option value="GLS">GLS</option>
            <option value="OTHER">Sonstiges</option>
          </select>
        </div>
        <div>
          <label htmlFor="ob-tracking" className="text-[10px] font-bold text-blue-700 uppercase block mb-1">Tracking-Nr.</label>
          <input id="ob-tracking" data-testid="ob-tracking" value={trackingNumber} onChange={(e)=>setTrackingNumber(e.target.value)}
            placeholder="z.B. 35.764.529397 0"
            className="w-full h-9 px-2 text-sm border border-[#E5E5E5] font-mono" />
        </div>
      </div>
      <p className="text-[9px] text-blue-700 mb-2">Punkte / Leerzeichen werden automatisch entfernt.</p>
      {error && <div className="mb-2 p-2 bg-red-50 border border-red-200 text-[11px] text-red-700">{String(error)}</div>}
      <button data-testid="outbound-ship" onClick={trigger} disabled={submitting}
        className="h-9 px-4 bg-blue-600 text-white text-xs font-semibold disabled:opacity-50 hover:bg-blue-700">
        {submitting ? "Sende…" : "Als versendet markieren"}
      </button>
    </div>
  );
}

// ─── Email Roles ────────────────────────────────────────
function EmailRolesSection() {
  const [roles, setRoles] = useState([]);
  const [smtpAccounts, setSmtpAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [showNewRole, setShowNewRole] = useState(false);
  const [newRole, setNewRole] = useState({ role_code: "", label: "", description: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([api.get("/admin/email-roles"), api.get("/admin/smtp-accounts")]);
      setRoles(r.data.roles || []);
      setSmtpAccounts(s.data.accounts || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const assignSMTP = async (roleCode, smtpAccountId) => {
    setSaving(roleCode);
    try { await api.post(`/admin/email-roles/${roleCode}`, { smtp_account_id: smtpAccountId || null }); load(); } catch { /* ignore */ }
    finally { setSaving(null); }
  };

  const toggleActive = async (roleCode, isActive) => {
    try { await api.post(`/admin/email-roles/${roleCode}`, { is_active: isActive }); load(); } catch { /* ignore */ }
  };

  const createRole = async () => {
    if (!newRole.role_code || !newRole.label) return;
    try { await api.post("/admin/email-roles", newRole); setShowNewRole(false); setNewRole({ role_code: "", label: "", description: "" }); load(); } catch { /* ignore */ }
  };

  if (loading) return <Spinner />;

  return (
    <div data-testid="admin-email-roles">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-[#0A0A0A] flex items-center gap-2"><FileText size={20} /> E-Mail-Rollen</h1>
          <p className="text-sm text-[#999] mt-0.5">Weise jeder Rolle ein SMTP-Konto zu</p>
        </div>
        <button onClick={() => setShowNewRole(true)} className="h-8 px-3 bg-[#0A0A0A] text-white text-xs font-medium flex items-center gap-1"><Plus size={12} /> Neue Rolle</button>
      </div>

      {showNewRole && (
        <div className="bg-white border border-[#E5E5E5] p-4 mb-4 space-y-3">
          <h3 className="text-sm font-bold">Neue E-Mail-Rolle</h3>
          <FieldInput label="Rollen-Code" value={newRole.role_code} onChange={(v) => setNewRole(p => ({ ...p, role_code: v.toUpperCase().replace(/[^A-Z0-9_]/g, "") }))} placeholder="z.B. CUSTOM_ALERT" />
          <FieldInput label="Bezeichnung" value={newRole.label} onChange={(v) => setNewRole(p => ({ ...p, label: v }))} placeholder="z.B. Individuelle Benachrichtigung" />
          <FieldInput label="Beschreibung" value={newRole.description} onChange={(v) => setNewRole(p => ({ ...p, description: v }))} />
          <div className="flex gap-2">
            <button onClick={() => setShowNewRole(false)} className="h-9 px-4 border text-xs text-[#666]">Abbrechen</button>
            <button onClick={createRole} className="h-9 px-4 bg-[#0A0A0A] text-white text-xs font-medium">Erstellen</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-[#FAFAFA]">
            <th className="text-left px-3 py-2.5 text-[10px] font-bold text-[#999] uppercase">Rolle</th>
            <th className="text-left px-3 py-2.5 text-[10px] font-bold text-[#999] uppercase">Beschreibung</th>
            <th className="text-left px-3 py-2.5 text-[10px] font-bold text-[#999] uppercase">SMTP-Konto</th>
            <th className="text-center px-3 py-2.5 text-[10px] font-bold text-[#999] uppercase">Aktiv</th>
          </tr></thead>
          <tbody className="divide-y divide-[#F0F0F0]">
            {roles.map((r) => (
              <tr key={r.role_code} className="hover:bg-[#FAFAFA]">
                <td className="px-3 py-2.5">
                  <p className="text-sm font-medium text-[#0A0A0A]">{r.label}</p>
                  <p className="text-[10px] font-mono text-[#999]">{r.role_code}</p>
                </td>
                <td className="px-3 py-2.5 text-xs text-[#666] max-w-xs">{r.description}</td>
                <td className="px-3 py-2.5">
                  <select
                    value={r.smtp_account_id || ""}
                    onChange={(e) => assignSMTP(r.role_code, e.target.value)}
                    disabled={saving === r.role_code}
                    className="h-8 px-2 border border-[#E5E5E5] text-xs bg-white w-full max-w-[200px]"
                  >
                    <option value="">— Nicht zugewiesen —</option>
                    {smtpAccounts.map((a) => (
                      <option key={a.account_id} value={a.account_id}>{a.label} ({a.from_email})</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-2.5 text-center">
                  <input type="checkbox" checked={r.is_active !== false} onChange={(e) => toggleActive(r.role_code, e.target.checked)}
                    className="w-4 h-4 accent-[#0A0A0A]" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}



// ─── Shipments Admin ────────────────────────────────────
function ShipmentsSection() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/shipments?limit=50"); setShipments(data.shipments || []); } catch { /* ignore */ } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const retry = async (id) => { try { await api.post(`/admin/shipments/${id}/retry`); load(); } catch { /* ignore */ } };

  if (loading) return <Spinner />;
  return (
    <div data-testid="admin-shipments">
      <h1 className="text-xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><Truck size={20} /> Versand / Labels</h1>
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-[#FAFAFA]">
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">ID</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Order</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Carrier</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Tracking</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Status</th>
            <th className="text-right px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Aktion</th>
          </tr></thead>
          <tbody className="divide-y divide-[#F0F0F0]">
            {shipments.length === 0 ? <tr><td colSpan={6} className="py-8 text-center text-[#999]">Keine Versandlabels.</td></tr> :
             shipments.map((s) => (
              <tr key={s.shipment_id} className="hover:bg-[#FAFAFA]">
                <td className="px-3 py-2 text-[11px] font-mono text-[#666]">{s.shipment_id?.slice(0,8)}</td>
                <td className="px-3 py-2 text-[11px] font-mono text-[#666]">{s.order_id?.slice(0,8)}</td>
                <td className="px-3 py-2 text-xs">{s.carrier}</td>
                <td className="px-3 py-2 text-xs font-mono">{s.tracking_number || "—"}</td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 text-[10px] font-medium border ${s.status === "LABEL_CREATED" ? "text-green-600 border-green-200 bg-green-50" : "text-red-600 border-red-200 bg-red-50"}`}>{s.status}</span></td>
                <td className="px-3 py-2 text-right">
                  {s.status === "LABEL_FAILED" && <button onClick={() => retry(s.shipment_id)} className="h-7 px-2 bg-[#0A0A0A] text-white text-[10px]">Retry</button>}
                  {s.label_url && <a href={s.label_url} target="_blank" rel="noreferrer" className="h-7 px-2 border border-[#E5E5E5] text-[10px] text-[#666] ml-1">Label</a>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Hub Queue Admin ────────────────────────────────────
function HubQueueSection() {
  const [tab, setTab] = useState("normal"); // normal | rescue
  const TABS = [
    { key: "normal", label: "Normale Sendungen", icon: Package },
    { key: "rescue", label: "Rescue-Bags", icon: Boxes },
  ];
  return (
    <div data-testid="admin-hub-warehouse">
      <h1 className="text-xl font-bold text-[#0A0A0A] mb-1 flex items-center gap-2"><Database size={20} /> Hub / Warehouse</h1>
      <p className="text-xs text-[#666] mb-4">Zentrale Übersicht aller physischen Wareneingänge — Normal-Sendungen (Modul A) und Rescue-Bags (Modul F).</p>
      <div className="flex border-b border-[#E5E5E5] mb-4">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} data-testid={`hub-warehouse-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-xs font-semibold flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#999] hover:text-[#0A0A0A]"}`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "normal" && <HubQueueNormalPanel />}
      {tab === "rescue" && <HubQueueRescuePanel />}
    </div>
  );
}

// ─── Normale Sendungen: eingegangen, aber noch nicht weiterversendet ─────
function HubQueueNormalPanel() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/hub/inbound/queue");
      setItems(data.items || []);
    } catch { setItems([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <Spinner />;
  return (
    <div data-testid="admin-hub-queue">
      <p className="text-xs text-[#666] mb-3">
        Sendungen, die im Hub eingegangen, aber noch <strong>nicht weiterversendet</strong> wurden.
        Sobald eine Sendung ausgeht, verschwindet sie automatisch aus dieser Liste.
        Wareneingang & Weiterversand laufen über <button onClick={() => navigate("/admin?section=hub_intake")} className="underline">Hub-Eingang</button>.
      </p>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-[#0A0A0A]">Im Hub ({items.length})</p>
        <button onClick={load} className="text-xs text-[#666] hover:text-[#0A0A0A]">Aktualisieren</button>
      </div>
      {items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E5] px-4 py-8 text-center text-xs text-[#999]">
          Aktuell keine Sendungen im Hub, die auf Weiterversand warten.
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#E5E5E5] bg-[#FAFAFA]">
              <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Bestellung</th>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Verkäufer</th>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Eingang</th>
              <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Status</th>
              <th className="text-right px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Aktion</th>
            </tr></thead>
            <tbody className="divide-y divide-[#F0F0F0]">
              {items.map((s) => (
                <tr key={s.inbound_shipment_id} data-testid={`hub-normal-row-${s.inbound_shipment_id}`} className="hover:bg-[#FAFAFA]">
                  <td className="px-3 py-2">
                    <p className="text-xs font-medium text-[#0A0A0A]">{s.order_title || s.order?.title || "—"}</p>
                    <p className="text-[10px] text-[#999] font-mono">
                      {s.order_public_no ? `Nr. ${s.order_public_no} · ` : ""}{(s.order_id || "").slice(0, 12)}…
                    </p>
                  </td>
                  <td className="px-3 py-2 text-xs text-[#666]">{s.seller_display || "—"}</td>
                  <td className="px-3 py-2 text-[11px] text-[#666] whitespace-nowrap">
                    {s.scanned_at ? new Date(s.scanned_at).toLocaleString("de-DE", {dateStyle:"short",timeStyle:"short"}) : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 text-[10px] font-medium border bg-blue-50 border-blue-200 text-blue-700">{adminStatusLabel(s.status)}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button data-testid={`hub-normal-open-${s.inbound_shipment_id}`}
                      onClick={() => navigate("/admin?section=hub_intake&hub_tab=queue")}
                      className="h-7 px-2 bg-[#0A0A0A] text-white text-[10px]">Bearbeiten</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Rescue-Bags Inventar (Modul F) ────────────────────
function HubQueueRescuePanel() {
  const navigate = useNavigate();
  const SUB_TABS = [
    { key: "RECEIVED", label: "Eingegangen" },
    { key: "DAMAGED", label: "Beschädigt" },
    { key: "ACTIVATED", label: "Unterwegs" },
  ];
  const [sub, setSub] = useState("RECEIVED");
  const [bags, setBags] = useState([]);
  const [itemsByBag, setItemsByBag] = useState({});
  const [loading, setLoading] = useState(false);
  const [ticking, setTicking] = useState(false);
  const [tickResult, setTickResult] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: bagsData }, itemsRes] = await Promise.all([
        api.get(`/rescue/admin/bags?status=${sub}&limit=200`),
        api.get(`/rescue/admin/items?limit=500`).catch(() => ({ data: { items: [] } })),
      ]);
      setBags(bagsData.bags || []);
      const map = {};
      (itemsRes.data.items || []).forEach(it => { if (it.bag_id) map[it.bag_id] = it; });
      setItemsByBag(map);
    } catch { setBags([]); setItemsByBag({}); } finally { setLoading(false); }
  }, [sub]);
  useEffect(() => { load(); }, [load]);

  const runLifecycleTick = async () => {
    setTicking(true);
    setTickResult(null);
    try {
      const { data } = await api.post(`/rescue/admin/lifecycle/tick`);
      setTickResult(data.transitions || {});
      load();
    } catch { setTickResult({ error: true }); } finally { setTicking(false); }
  };

  const renderBadge = (rs) => {
    const cfg = {
      PENDING: "bg-gray-100 text-gray-600",
      IN_PROGRESS: "bg-amber-50 text-amber-700",
      COMPLETED: "bg-green-50 text-green-700",
      FAILED: "bg-red-50 text-red-700",
    };
    const cls = cfg[rs] || "bg-gray-50 text-gray-500";
    return <span data-testid={`rescue-render-badge-${rs || "none"}`} className={`px-1.5 py-0.5 rounded text-[10px] ${cls}`}>{rs || "—"}</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-3">
          {SUB_TABS.map(t => (
            <button key={t.key} data-testid={`rescue-inv-tab-${t.key}`}
              onClick={() => setSub(t.key)}
              className={`text-xs pb-1 border-b-2 ${sub === t.key ? "border-[#0A0A0A] text-[#0A0A0A] font-semibold" : "border-transparent text-[#999]"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="rescue-lifecycle-tick-btn"
            onClick={runLifecycleTick}
            disabled={ticking}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#E5E5E5] bg-white text-[#0A0A0A] rounded text-[11px] font-medium disabled:opacity-50">
            <RefreshCw size={12} className={ticking ? "animate-spin" : ""} /> Lifecycle-Tick
          </button>
          <button
            data-testid="rescue-open-warehouse-scanner"
            onClick={() => navigate("/admin?section=hub_intake&hub_tab=rescue")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0A0A0A] text-white rounded text-[11px] font-medium">
            <ScanLine size={12} /> Rescue-Intake öffnen
          </button>
        </div>
      </div>
      {tickResult && !tickResult.error && (
        <div data-testid="rescue-tick-result" className="mb-3 text-[10px] text-[#666] bg-[#FAFAFA] border border-[#E5E5E5] px-3 py-1.5 rounded">
          Tick: {tickResult.promoted_to_rescue || 0} → RESCUE · {tickResult.disposed || 0} entsorgt · {tickResult.damaged_auto_disposed || 0} beschädigt auto-entsorgt
        </div>
      )}
      {loading ? <p className="text-[11px] text-[#999] py-4 text-center">Lade …</p> : bags.length === 0 ? (
        <p data-testid="rescue-inv-empty" className="text-[11px] text-[#999] py-6 text-center border border-dashed border-[#E5E5E5] rounded">
          {sub === "RECEIVED" && "Noch keine Bags im Warehouse eingegangen."}
          {sub === "DAMAGED" && "Keine beschädigten Bags."}
          {sub === "ACTIVATED" && "Keine aktivierten Bags in Transit."}
        </p>
      ) : (
        <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-[#FAFAFA] text-[#999] text-left uppercase text-[10px]">
              <tr>
                <th className="px-3 py-2 font-bold">Bag-ID</th>
                <th className="px-3 py-2 font-bold">Kategorie</th>
                <th className="px-3 py-2 font-bold">Tragedauer</th>
                <th className="px-3 py-2 font-bold">Aktiviert</th>
                <th className="px-3 py-2 font-bold">Eingang</th>
                <th className="px-3 py-2 font-bold">Init-Preis</th>
                <th className="px-3 py-2 font-bold">Rescue-Preis</th>
                <th className="px-3 py-2 font-bold">KI-Render</th>
                <th className="px-3 py-2 font-bold">Item-Status</th>
                <th className="px-3 py-2 font-bold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0F0F0]">
              {bags.map(b => {
                const act = b.activation || {};
                const it = itemsByBag[b.bag_id] || {};
                return (
                  <tr key={b.bag_id} data-testid={`hub-rescue-row-${b.bag_id.slice(0,8)}`} className="hover:bg-[#FAFAFA]">
                    <td className="px-3 py-2 font-mono">{b.bag_id.slice(0,8).toUpperCase()}</td>
                    <td className="px-3 py-2">{act.category || "—"}</td>
                    <td className="px-3 py-2">{act.wear_duration_days ? `${act.wear_duration_days}T` : "—"}</td>
                    <td className="px-3 py-2 text-[#666]">{act.activated_at ? act.activated_at.slice(0,10) : "—"}</td>
                    <td className="px-3 py-2 text-[#666]">{b.received_at ? b.received_at.slice(0,10) : "—"}</td>
                    <td className="px-3 py-2 font-mono">{it.initial_price != null ? `${Number(it.initial_price).toFixed(2)} €` : "—"}</td>
                    <td className="px-3 py-2 font-mono">{it.rescue_price != null ? `${Number(it.rescue_price).toFixed(2)} €` : "—"}</td>
                    <td className="px-3 py-2">{renderBadge(it.render_status)}</td>
                    <td className="px-3 py-2 text-[#666]">{it.status || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${b.status === "RECEIVED" ? "bg-green-50 text-green-700" : b.status === "DAMAGED" ? "bg-red-50 text-red-700" : "bg-blue-50 text-blue-700"}`}>
                        {b.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Physical Labels Admin ──────────────────────────────
function PhysicalLabelsSection() {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ order_id: "", sender_info: "", carrier: "DHL", tracking_number: "", notes: "" });

  const load = async () => { setLoading(true); try { const { data } = await api.get("/shipping/physical-labels"); setLabels(data.labels || []); } catch { /* ignore */ } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const add = async () => {
    try { await api.post("/shipping/physical-labels", form); setShowAdd(false); setForm({ order_id: "", sender_info: "", carrier: "DHL", tracking_number: "", notes: "" }); load(); } catch { /* ignore */ }
  };
  const updateStatus = async (id, status) => {
    try { await api.post(`/shipping/physical-labels/${id}/status`, { status }); load(); } catch { /* ignore */ }
  };

  if (loading) return <Spinner />;
  return (
    <div data-testid="admin-physical-labels">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-[#0A0A0A] flex items-center gap-2"><FileText size={20} /> Eingesandte Labels</h1>
        <button onClick={() => setShowAdd(true)} className="h-8 px-3 bg-[#0A0A0A] text-white text-xs font-medium flex items-center gap-1"><Plus size={12} /> Erfassen</button>
      </div>
      {showAdd && (
        <div className="bg-white border border-[#E5E5E5] p-4 mb-4 space-y-3">
          <FieldInput label="Order-ID (optional)" value={form.order_id} onChange={(v) => setForm(p => ({ ...p, order_id: v }))} />
          <FieldInput label="Absender-Info" value={form.sender_info} onChange={(v) => setForm(p => ({ ...p, sender_info: v }))} />
          <FieldInput label="Carrier" value={form.carrier} onChange={(v) => setForm(p => ({ ...p, carrier: v }))} />
          <FieldInput label="Tracking-Nummer" value={form.tracking_number} onChange={(v) => setForm(p => ({ ...p, tracking_number: v }))} />
          <FieldInput label="Notizen" value={form.notes} onChange={(v) => setForm(p => ({ ...p, notes: v }))} />
          <div className="flex gap-2">
            <button onClick={() => setShowAdd(false)} className="h-9 px-4 border text-xs text-[#666]">Abbrechen</button>
            <button onClick={add} className="h-9 px-4 bg-[#0A0A0A] text-white text-xs font-medium">Erfassen</button>
          </div>
        </div>
      )}
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-[#FAFAFA]">
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">ID</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Order</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Absender</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Carrier</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Status</th>
            <th className="text-right px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Aktion</th>
          </tr></thead>
          <tbody className="divide-y divide-[#F0F0F0]">
            {labels.length === 0 ? <tr><td colSpan={6} className="py-8 text-center text-[#999]">Keine eingesandten Labels.</td></tr> :
             labels.map((l) => (
              <tr key={l.label_id} className="hover:bg-[#FAFAFA]">
                <td className="px-3 py-2 text-[11px] font-mono text-[#666]">{l.label_id?.slice(0,8)}</td>
                <td className="px-3 py-2 text-[11px] font-mono text-[#666]">{l.order_id?.slice(0,8) || "—"}</td>
                <td className="px-3 py-2 text-xs">{l.sender_info || "—"}</td>
                <td className="px-3 py-2 text-xs">{l.carrier}</td>
                <td className="px-3 py-2">
                  <select value={l.status} onChange={(e) => updateStatus(l.label_id, e.target.value)} className="h-6 px-1 text-[10px] border border-[#E5E5E5] bg-white">
                    <option value="RECEIVED">Empfangen</option>
                    <option value="INSPECTED">Geprüft</option>
                    <option value="ASSIGNED">Zugeordnet</option>
                    <option value="USED">Verwendet</option>
                    <option value="EXPIRED">Abgelaufen</option>
                    <option value="INVALID">Ungültig</option>
                  </select>
                </td>
                <td className="px-3 py-2 text-right text-xs text-[#999]">{l.notes || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}



// ─── AVS Provider Settings ──────────────────────────────
function AVSProviderSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");

  useEffect(() => { (async () => { try { const { data } = await api.get("/admin/settings/avs_provider"); setSettings(data.value || {}); } catch { /* ignore */ } finally { setLoading(false); } })(); }, []);

  const save = async () => {
    setSaving(true); setSuccess("");
    try { await api.post("/admin/settings/avs_provider", { value: settings }); setSuccess("AVS-Provider gespeichert"); setTimeout(() => setSuccess(""), 3000); } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const u = (path, val) => {
    setSettings(prev => {
      const copy = { ...prev };
      const parts = path.split(".");
      if (parts.length === 1) { copy[parts[0]] = val; }
      else { copy[parts[0]] = { ...(copy[parts[0]] || {}), [parts[1]]: val }; }
      return copy;
    });
  };

  if (loading) return <Spinner />;

  return (
    <div data-testid="avs-provider-settings" className="space-y-4">
      <div className="bg-white border border-[#E5E5E5] p-6 space-y-4">
        <h2 className="text-base font-bold text-[#0A0A0A]">AVS-Provider Konfiguration</h2>
        {success && <div className="px-3 py-2 bg-green-50 border border-green-200 text-green-600 text-sm">{success}</div>}

        <div>
          <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Provider</label>
          <select value={settings.provider || "manual"} onChange={(e) => u("provider", e.target.value)} className="mt-1 w-full h-10 px-3 border border-[#E5E5E5] text-sm bg-white">
            <option value="manual">Manuell (Admin bestätigt)</option>
            <option value="tantumident">TantumIdent (automatisch)</option>
          </select>
        </div>

        {settings.provider === "tantumident" && (
          <div className="space-y-3 p-4 border border-[#E5E5E5] bg-[#FAFAFA]">
            <h3 className="text-sm font-bold text-[#0A0A0A]">TantumIdent Konfiguration</h3>
            <FieldInput label="API URL" value={(settings.tantumident || {}).api_url || ""} onChange={(v) => u("tantumident.api_url", v)} placeholder="https://api.tantumident.com" />
            <FieldInput label="API Key" value={(settings.tantumident || {}).api_key || ""} onChange={(v) => u("tantumident.api_key", v)} secret />
            <FieldInput label="API Secret" value={(settings.tantumident || {}).api_secret || ""} onChange={(v) => u("tantumident.api_secret", v)} secret />
            <FieldInput label="Merchant ID" value={(settings.tantumident || {}).merchant_id || ""} onChange={(v) => u("tantumident.merchant_id", v)} />
            <FieldInput label="Webhook URL" value={(settings.tantumident || {}).webhook_url || ""} onChange={(v) => u("tantumident.webhook_url", v)} placeholder="https://yourdomain.com/api/avs/webhook" />
            <FieldInput label="Success Redirect URL" value={(settings.tantumident || {}).redirect_success_url || ""} onChange={(v) => u("tantumident.redirect_success_url", v)} />
            <FieldInput label="Cancel Redirect URL" value={(settings.tantumident || {}).redirect_cancel_url || ""} onChange={(v) => u("tantumident.redirect_cancel_url", v)} />
            <div>
              <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Modus</label>
              <select value={(settings.tantumident || {}).mode || "sandbox"} onChange={(e) => u("tantumident.mode", e.target.value)} className="mt-1 w-full h-10 px-3 border border-[#E5E5E5] text-sm bg-white">
                <option value="sandbox">Sandbox</option>
                <option value="production">Produktion</option>
              </select>
            </div>
          </div>
        )}

        <button onClick={save} disabled={saving} className="h-10 px-6 bg-[#0A0A0A] text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"><Save size={14} /> Speichern</button>
      </div>

      <div className="bg-white border border-[#E5E5E5] p-6">
        <h3 className="text-sm font-bold text-[#0A0A0A] mb-2">Hinweis</h3>
        <p className="text-xs text-[#666] leading-relaxed">
          Bei Auswahl &quot;TantumIdent&quot; werden User automatisch zum Provider weitergeleitet.
          Das Ergebnis kommt per Webhook zurück und aktualisiert den AVS-Status automatisch.
          Bei &quot;Manuell&quot; muss ein Admin jeden AVS-Antrag unter &quot;AVS-Anträge&quot; bestätigen oder ablehnen.
          Die manuelle Bestätigung ist auch bei aktivem Provider als Fallback verfügbar.
        </p>
      </div>
    </div>
  );
}

// ─── Orders Admin ───────────────────────────────────────
function OrdersAdminSection() {
  // iter222 · 4-Tab Split: mp (MARKETPLACE-orders), stx (STILLTOUCHEDX-orders),
  // social (payment_transactions social/paid-media), system (payment_transactions abos).
  const [areaTab, setAreaTab] = useState("mp");
  const [orders, setOrders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [errorFilter, setErrorFilter] = useState(false);
  const [detailOrderId, setDetailOrderId] = useState(null);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (areaTab === "mp" || areaTab === "stx") {
        const area = areaTab === "mp" ? "MARKETPLACE" : "STILLTOUCHEDX";
        let url = `/admin/orders?page=${page}&limit=20&area=${area}`;
        if (statusFilter) url += `&status=${statusFilter}`;
        if (searchDebounced) url += `&search=${encodeURIComponent(searchDebounced)}`;
        const { data } = await api.get(url);
        setOrders(data.orders || []);
        setTransactions([]);
        setTotal(data.total || 0);
      } else {
        let url = `/admin/transactions?page=${page}&limit=20&group=${areaTab}`;
        if (statusFilter) url += `&payment_status=${statusFilter}`;
        if (errorFilter) url += `&has_error=1`;
        if (searchDebounced) url += `&search=${encodeURIComponent(searchDebounced)}`;
        const { data } = await api.get(url);
        setTransactions(data.transactions || []);
        setOrders([]);
        setTotal(data.total || 0);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [areaTab, page, statusFilter, errorFilter, searchDebounced]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [areaTab, statusFilter, errorFilter, searchDebounced]);
  useEffect(() => { setStatusFilter(""); setErrorFilter(false); }, [areaTab]);

  const changeStatus = async (orderId, newStatus) => {
    try { await api.post(`/admin/orders/${orderId}/status`, { status: newStatus }); load(); } catch { /* ignore */ }
  };

  const changePaymentStatus = async (orderId, newPS) => {
    try { await api.post(`/admin/orders/${orderId}/payment-status`, { payment_status: newPS }); load(); } catch { /* ignore */ }
  };

  // iter222 · Inline-Status + Clear-Error für payment_transactions.
  const changeTxPaymentStatus = async (txId, newPS) => {
    try { await api.post(`/admin/transactions/${txId}/payment-status`, { payment_status: newPS }); load(); } catch { /* ignore */ }
  };
  const clearTxError = async (txId) => {
    try { await api.post(`/admin/transactions/${txId}/clear-error`, {}); load(); } catch { /* ignore */ }
  };
  // iter226 · Retry Grant + Detail-Modal.
  const [txDetail, setTxDetail] = useState(null);
  const [txTab, setTxTab] = useState("overview"); // iter271 · unified tabs für Abo/Social-Tx
  const [retrying, setRetrying] = useState(null);
  const retryGrant = async (txId) => {
    setRetrying(txId);
    try {
      await api.post(`/admin/transactions/${txId}/retry-grant`, {});
      load();
      if (txDetail?.transaction?.tx_id === txId) openTxDetail(txId);
    } catch (e) {
      const d = e?.response?.data?.detail;
      alert(typeof d === "string" ? d : d?.message || "Retry fehlgeschlagen");
    } finally { setRetrying(null); }
  };
  const openTxDetail = async (txId) => {
    try {
      const { data } = await api.get(`/admin/transactions/${txId}`);
      setTxDetail(data);
    } catch { /* ignore */ }
  };
  // iter267 · Einheitliche Detail-Karte: Social/Abo öffnen dieselbe OrderDetailModal
  // wie MP/STX (über die verknüpfte Bestellung). Fallback aufs tx-Modal, wenn keine
  // Order existiert (z.B. Alt-/Abo-Transaktionen ohne orders-Zeile).
  const openSocialDetail = async (txId) => {
    try {
      const { data } = await api.get(`/admin/transactions/${txId}`);
      const orders = data?.orders || [];
      if (orders.length >= 1) { setDetailOrderId(orders[0].order_id); return; }
      setTxTab("overview");
      setTxDetail(data);
    } catch { /* ignore */ }
  };
  // iter266 · Dokument-Download impersonation-sicher (axios-Blob, trägt Bearer/Cookie).
  const downloadTxDoc = async (docId, filename) => {
    try {
      const res = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || `beleg_${docId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 5000);
    } catch { alert("Dokument konnte nicht geladen werden."); }
  };
  const resendTxDoc = async (docId) => {
    try { await api.post(`/documents/${docId}/resend`); alert("E-Mail erneut versendet."); }
    catch (e) { alert("Versand fehlgeschlagen: " + (e?.response?.data?.detail || e.message)); }
  };
  // iter227 · Bulk + Single Delete für Orders + Transactions.
  const [selected, setSelected] = useState(new Set());
  const toggleSel = (id) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const clearSel = () => setSelected(new Set());
  useEffect(() => { clearSel(); }, [areaTab]);
  const deleteRow = async (id) => {
    if (!window.confirm(`Diesen Eintrag inkl. Dokumente unwiderruflich löschen?\n${id}`)) return;
    try {
      if (isTxTab) await api.delete(`/admin/transactions/${id}`);
      else await api.delete(`/admin/orders/${id}`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };
  const deleteBulk = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} Einträge inkl. aller Dokumente unwiderruflich löschen?`)) return;
    try {
      const url = isTxTab ? "/admin/transactions/bulk-delete" : "/admin/orders/bulk-delete";
      const key = isTxTab ? "tx_ids" : "order_ids";
      await api.post(url, { [key]: Array.from(selected) });
      clearSel();
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Bulk-Löschen fehlgeschlagen"); }
  };

  const AREA_TABS = [
    { key: "mp", label: "Marketplace" },
    { key: "stx", label: "stilltouchedX" },
    { key: "social", label: "Social/Paid-Media" },
    { key: "system", label: "System/Creator-Abos" },
  ];
  const KIND_LABEL = {
    PAID_MEDIA: "Paid Media", PAID_MEDIA_CART: "Paid-Media Warenkorb",
    TIP: "Tip", MESSAGE_PACKAGE: "Nachrichten-Paket",
    SUBSCRIPTION: "Creator-Abo", TIER_SUBSCRIPTION: "Tier-Abo",
    HELPER_SUBSCRIPTION: "Helper-Abo", SUBSCRIPTION_RENEWAL: "Abo-Verlängerung",
  };
  // iter225 · DE-Labels für Payment-Status (auch in tx-Tabs).
  const PS_LABEL = { pending: "Zahlung ausstehend", paid: "Bezahlt", failed: "Zahlung fehlgeschlagen", refunded: "Erstattet" };
  const PS_STYLE = {
    paid: "bg-green-50 text-green-700 border-green-300",
    pending: "bg-yellow-50 text-yellow-800 border-yellow-300",
    failed: "bg-red-50 text-red-700 border-red-300",
    refunded: "bg-gray-50 text-gray-700 border-gray-300",
  };
  // iter225 · Content-Status je kind — abgeleitet aus tx-Feldern.
  const deriveTxContentStatus = (t) => {
    const kind = t.kind;
    const ps = t.payment_status || "pending";
    if (["PAID_MEDIA", "PAID_MEDIA_CART"].includes(kind)) {
      if (ps !== "paid") return { label: "Ausstehend", cls: "bg-yellow-50 text-yellow-800 border-yellow-300" };
      if ((t.metadata || {}).downloaded_at || (t.metadata || {}).download_count > 0) return { label: "Heruntergeladen", cls: "bg-blue-50 text-blue-700 border-blue-300" };
      return { label: "Freigeschaltet", cls: "bg-green-50 text-green-700 border-green-300" };
    }
    if (["SUBSCRIPTION", "TIER_SUBSCRIPTION", "HELPER_SUBSCRIPTION"].includes(kind)) {
      if (ps === "refunded") return { label: "Erstattet", cls: "bg-gray-50 text-gray-700 border-gray-300" };
      if (ps !== "paid") return { label: "Wartet auf Zahlung", cls: "bg-yellow-50 text-yellow-800 border-yellow-300" };
      const end = (t.metadata || {}).current_period_end || t.period_end;
      if (end && new Date(end) < new Date()) return { label: "Abgelaufen", cls: "bg-gray-50 text-gray-700 border-gray-300" };
      return { label: "Aktiv", cls: "bg-green-50 text-green-700 border-green-300" };
    }
    if (kind === "SUBSCRIPTION_RENEWAL") {
      return ps === "paid"
        ? { label: "Verlängert", cls: "bg-green-50 text-green-700 border-green-300" }
        : { label: "Verlängerung ausstehend", cls: "bg-yellow-50 text-yellow-800 border-yellow-300" };
    }
    if (kind === "TIP") {
      return ps === "paid"
        ? { label: "Empfangen", cls: "bg-green-50 text-green-700 border-green-300" }
        : { label: "Ausstehend", cls: "bg-yellow-50 text-yellow-800 border-yellow-300" };
    }
    if (kind === "MESSAGE_PACKAGE") {
      if (ps !== "paid") return { label: "Ausstehend", cls: "bg-yellow-50 text-yellow-800 border-yellow-300" };
      const rem = (t.metadata || {}).remaining;
      if (typeof rem === "number" && rem <= 0) return { label: "Verbraucht", cls: "bg-gray-50 text-gray-700 border-gray-300" };
      return { label: "Aktiv", cls: "bg-green-50 text-green-700 border-green-300" };
    }
    return { label: "—", cls: "bg-gray-50 text-gray-600 border-gray-300" };
  };
  const isTxTab = areaTab === "social" || areaTab === "system";

  const ORDER_STATUSES = ["CREATED", "PAID", "AWAITING_SHIPMENT", "SHIPPED", "DELIVERED", "COMPLETED", "CANCELLED", "REFUNDED"];
  const PAYMENT_STATUSES = ["PAYMENT_PENDING", "PAYMENT_CONFIRMED", "PAID", "PAYMENT_CANCELLED", "PAYMENT_FAILED", "REFUND_PENDING", "REFUNDED"];

  return (
    <div data-testid="admin-orders">
      <h1 className="text-xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><CreditCard size={20} /> Bestellungen</h1>
      {/* iter242 · Fingerprint-Detektor (Variante B §17) — nur im Social-Tab. */}
      {areaTab === "social" && (
        <div className="mb-4">
          <AdminFingerprintPanel />
        </div>
      )}
      {/* iter222 · 4-Tab Split */}
      <div className="flex gap-2 mb-3 border-b border-[#E5E5E5]" data-testid="admin-orders-area-tabs">
        {AREA_TABS.map((tab) => (
          <button
            key={tab.key}
            data-testid={`admin-orders-tab-${tab.key}`}
            onClick={() => setAreaTab(tab.key)}
            className={`h-9 px-4 text-xs font-medium border-b-2 transition ${
              areaTab === tab.key
                ? "border-[#0A0A0A] text-[#0A0A0A]"
                : "border-transparent text-[#666] hover:text-[#0A0A0A]"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {/* Suchfeld */}
      <div className="mb-3 relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suche nach Bestellnummer, Titel, Käufer oder Verkäufer …"
          data-testid="admin-orders-search"
          className="w-full h-10 pl-9 pr-24 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
        />
        {search && (
          <button onClick={() => setSearch("")}
            data-testid="admin-orders-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 px-2 text-[10px] text-[#666] hover:bg-[#F5F5F5]">
            Zurücksetzen
          </button>
        )}
      </div>
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <button onClick={() => { setStatusFilter(""); setPage(1); }}
          data-testid="admin-orders-filter-all"
          className={`h-8 px-3 text-xs border rounded-full transition ${!statusFilter ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}>Alle ({total})</button>
        {(isTxTab ? ["pending", "paid", "failed", "refunded"] : ["CREATED", "PAID", "SHIPPED", "DELIVERED", "COMPLETED", "CANCELLED", "REFUNDED"]).map((s) => (
          <button key={s}
            data-testid={`admin-orders-filter-${s.toLowerCase()}`}
            onClick={() => { setStatusFilter(s); setPage(1); }}
            className={`h-8 px-3 text-xs border rounded-full transition ${statusFilter === s ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}>{isTxTab ? (PS_LABEL[s] || s) : (ORDER_STATUS_LABELS[s] || s)}</button>
        ))}
        {/* iter225 · Fehler-Filter für ALLE Tabs. Bei MP/STX filtert es auf CANCELLED-Orders (Abbruch/Rücktritt). */}
        <button
          data-testid="admin-orders-filter-errors"
          onClick={() => {
            if (isTxTab) setErrorFilter((v) => !v);
            else { setStatusFilter(statusFilter === "CANCELLED" ? "" : "CANCELLED"); setPage(1); }
          }}
          className={`h-8 px-3 text-xs border rounded-full transition ${(isTxTab ? errorFilter : statusFilter === "CANCELLED") ? "bg-red-600 text-white border-red-600" : "bg-white text-red-600 border-red-300 hover:bg-red-50"}`}
        >⚠️ Fehler / Abbrüche</button>
        {/* iter227 · Bulk-Delete-Toolbar */}
        {selected.size > 0 && (
          <div className="ml-auto flex items-center gap-2" data-testid="admin-orders-bulk-toolbar">
            <span className="text-xs text-[#666]">{selected.size} ausgewählt</span>
            <button data-testid="admin-orders-bulk-delete-btn" onClick={deleteBulk} className="h-8 px-3 text-xs font-medium bg-red-600 text-white rounded-full hover:bg-red-700">🗑 Auswahl löschen</button>
            <button onClick={clearSel} className="h-8 px-3 text-xs text-[#666] hover:text-[#0A0A0A]">✕</button>
          </div>
        )}
      </div>
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-[#FAFAFA]">
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Bestellung</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Käufer</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Verkäufer</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Betrag</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Zahlung</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Status</th>
            <th className="text-right px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Aktion</th>
          </tr></thead>
          <tbody className="divide-y divide-[#F0F0F0]">
            {isTxTab ? (
              /* iter222 · payment_transactions Tab (Social/Paid-Media + System/Creator-Abos) */
              loading ? <tr><td colSpan={7} className="py-8 text-center"><Spinner /></td></tr> :
              transactions.length === 0 ? <tr><td colSpan={7} className="py-8 text-center text-[#999]">Keine Transaktionen.</td></tr> :
              transactions.map((t) => (
                <tr key={t.tx_id || t.session_id} data-testid={`tx-row-${t.tx_id}`}
                    className={`hover:bg-[#FAFAFA] ${t.last_webhook_error ? "bg-red-50" : ""}`}>
                  <td className="px-3 py-2">
                    <p className="text-sm font-medium text-[#0A0A0A]">{KIND_LABEL[t.kind] || t.kind}</p>
                    <p className="text-[10px] font-mono text-[#999]">{(t.tx_id || t.session_id || "").slice(0,8)}</p>
                    {t.last_webhook_error && (
                      <span data-testid={`tx-error-badge-${t.tx_id}`} className="mt-0.5 inline-block text-[9px] px-1.5 py-0.5 bg-red-100 text-red-800 border border-red-300 uppercase font-bold" title={t.last_webhook_error.message}>⚠️ Grant-Fehler</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-[#666]">{t.buyer_profile?.display_name || (t.user_id || "").slice(0,8)}</td>
                  <td className="px-3 py-2 text-xs text-[#999]">—</td>
                  <td className="px-3 py-2 text-sm font-medium">{Number(t.amount || 0).toFixed(2)} {t.currency || "EUR"}</td>
                  <td className="px-3 py-2">
                    <span data-testid={`tx-payment-badge-${t.tx_id}`} className={`inline-block mb-1 px-2 py-0.5 text-[9px] font-medium rounded-full border ${PS_STYLE[t.payment_status] || PS_STYLE.pending}`}>{PS_LABEL[t.payment_status] || PS_LABEL.pending}</span>
                    <select
                      data-testid={`tx-payment-select-${t.tx_id}`}
                      value={t.payment_status || "pending"}
                      onChange={(e) => changeTxPaymentStatus(t.tx_id, e.target.value)}
                      className="block h-6 px-1 text-[10px] border border-[#E5E5E5] bg-white rounded"
                    >
                      {["pending","paid","failed","refunded"].map((s) => <option key={s} value={s}>{PS_LABEL[s]}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    {(() => {
                      const cs = deriveTxContentStatus(t);
                      return <span data-testid={`tx-content-status-${t.tx_id}`} className={`inline-block px-2 py-0.5 text-[9px] font-medium rounded-full border ${cs.cls}`}>{cs.label}</span>;
                    })()}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {t.last_webhook_error && (
                      <>
                        <button
                          data-testid={`tx-retry-grant-btn-${t.tx_id}`}
                          onClick={() => retryGrant(t.tx_id)}
                          disabled={retrying === t.tx_id}
                          className="h-7 px-2 text-[10px] font-medium border border-blue-300 text-blue-700 hover:bg-blue-50 rounded mr-1 disabled:opacity-50"
                          title="Grant-Logik erneut ausführen"
                        >{retrying === t.tx_id ? "…" : "🔄 Retry Grant"}</button>
                        <button
                          data-testid={`tx-clear-error-btn-${t.tx_id}`}
                          onClick={() => clearTxError(t.tx_id)}
                          className="h-7 px-2 text-[10px] font-medium border border-red-300 text-red-700 hover:bg-red-100 rounded mr-1"
                          title="Fehler-Flag zurücksetzen (nach manuellem Recover)"
                        >Fehler löschen</button>
                      </>
                    )}
                    <button
                      data-testid={`tx-details-btn-${t.tx_id}`}
                      onClick={() => openSocialDetail(t.tx_id)}
                      className="h-7 px-3 text-[10px] font-medium border border-[#E5E5E5] text-[#666] hover:bg-[#F5F5F5] rounded"
                    >Details</button>
                    <input type="checkbox" data-testid={`tx-select-${t.tx_id}`} checked={selected.has(t.tx_id)} onChange={() => toggleSel(t.tx_id)} className="ml-2 align-middle" title="Für Bulk-Delete markieren" />
                    <button data-testid={`tx-delete-btn-${t.tx_id}`} onClick={() => deleteRow(t.tx_id)} className="ml-1 h-7 px-2 text-[10px] font-medium border border-red-300 text-red-700 hover:bg-red-100 rounded" title="Diese Transaktion inkl. Dokumente löschen">🗑</button>
                  </td>
                </tr>
              ))
            ) : (
              loading ? <tr><td colSpan={7} className="py-8 text-center"><Spinner /></td></tr> :
             orders.length === 0 ? <tr><td colSpan={7} className="py-8 text-center text-[#999]">Keine Bestellungen.</td></tr> :
             orders.map((o) => (
              <tr key={o.order_id} data-testid={`order-row-${o.order_id}`}
                  className="hover:bg-[#FAFAFA]">
                <td className="px-3 py-2" onClick={(e) => { e.stopPropagation(); setDetailOrderId(o.order_id); }} style={{cursor:"pointer"}}>
                  <p className="text-sm font-medium text-[#0A0A0A] truncate max-w-[150px]">{o.title}</p>
                  <p className="text-[10px] font-mono text-[#999]">{o.order_id?.slice(0,8)}</p>
                  {o.special_request && (
                    <span data-testid={`order-row-wish-badge-${o.order_id}`} className="mt-0.5 inline-block text-[9px] px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 uppercase font-bold tracking-wider">Sonderwunsch</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-[#666]">{o.buyer_type === "GUEST" ? `Gast (${o.guest_email || ""})` : (o.buyer_profile?.display_name || o.buyer_id?.slice(0,8))}</td>
                <td className="px-3 py-2 text-xs text-[#666]">{o.seller_profile?.display_name || o.seller_id?.slice(0,8)}</td>
                <td className="px-3 py-2 text-sm font-medium">{o.price?.toFixed(2)} {o.currency}</td>
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <span data-testid={`order-payment-badge-${o.order_id}`} className={`inline-block mb-1 px-2 py-0.5 text-[9px] font-medium rounded-full border ${orderStatusColor(o.payment_status || o.payment_method)}`}>{paymentStatusLabel(o.payment_status) || o.payment_method || "—"}</span>
                  <select
                    data-testid={`order-payment-select-${o.order_id}`}
                    value={o.payment_status || "PAYMENT_PENDING"}
                    onChange={(e) => changePaymentStatus(o.order_id, e.target.value)}
                    className="block h-6 px-1 text-[10px] border border-[#E5E5E5] bg-white rounded"
                  >
                    {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{paymentStatusLabel(s)}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <span data-testid={`order-status-badge-${o.order_id}`} className={`inline-block mb-1 px-2 py-0.5 text-[9px] font-medium rounded-full border ${orderStatusColor(o.status)}`}>{orderStatusLabel(o.status)}</span>
                  <select data-testid={`order-status-select-${o.order_id}`} value={o.status} onChange={(e) => changeStatus(o.order_id, e.target.value)} className="block h-6 px-1 text-[10px] border border-[#E5E5E5] bg-white rounded">
                    {ORDER_STATUSES.map((s) => <option key={s} value={s}>{ORDER_STATUS_LABELS[s] || s}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  <button
                    data-testid={`order-detail-btn-${o.order_id}`}
                    onClick={() => setDetailOrderId(o.order_id)}
                    className="h-7 px-2 border border-[#E5E5E5] text-[10px] text-[#0A0A0A] hover:bg-[#F5F5F5]"
                  >
                    Details
                  </button>
                  <input type="checkbox" data-testid={`order-select-${o.order_id}`} checked={selected.has(o.order_id)} onChange={() => toggleSel(o.order_id)} className="ml-2 align-middle" title="Für Bulk-Delete markieren" />
                  <button data-testid={`order-delete-btn-${o.order_id}`} onClick={() => deleteRow(o.order_id)} className="ml-1 h-7 px-2 text-[10px] font-medium border border-red-300 text-red-700 hover:bg-red-100 rounded" title="Diese Bestellung inkl. Dokumente löschen">🗑</button>
                </td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
      {total > 20 && <div className="flex justify-center gap-2 mt-4">
        <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="h-8 px-3 border text-sm disabled:opacity-30"><ChevronLeft size={14} /></button>
        <span className="text-xs text-[#999] self-center">Seite {page}</span>
        <button onClick={() => setPage(page + 1)} className="h-8 px-3 border text-sm"><ChevronRight size={14} /></button>
      </div>}
      {detailOrderId && (
        <OrderDetailModal
          orderId={detailOrderId}
          onClose={() => setDetailOrderId(null)}
          onReload={() => load()}
        />
      )}
      {/* iter271 · Vereinheitlichte Detail-Karte für Abo/Social-Transaktionen ohne Order.
          Gleiche getabbte Optik (Übersicht · Dokumente · Historie) wie OrderDetailModal. */}
      {txDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setTxDetail(null)} data-testid="abo-detail-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white border border-[#E5E5E5] shadow-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-base font-bold text-[#0A0A0A] flex items-center gap-2">
                  <CreditCard size={16} /> {KIND_LABEL[txDetail.transaction.kind] || txDetail.transaction.kind}
                </h3>
                <p className="text-[10px] font-mono text-[#999]">tx_id: {txDetail.transaction.tx_id}</p>
              </div>
              <button onClick={() => setTxDetail(null)} className="text-[#999]"><X size={18} /></button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-[#E5E5E5] sticky top-[60px] bg-white z-10">
              {[
                ["overview", "Übersicht"],
                ["documents", `Dokumente (${(txDetail.documents || []).length})`],
                ["audit", `Historie (${txDetail.history?.length || 0})`],
              ].map(([key, label]) => (
                <button key={key} data-testid={`abo-tab-${key}`} onClick={() => setTxTab(key)}
                  className={`h-9 px-3 text-xs border-b-2 ${txTab === key ? "border-[#0A0A0A] text-[#0A0A0A] font-medium" : "border-transparent text-[#999]"}`}>
                  {label}
                </button>
              ))}
            </div>

            <div className="px-4 py-3 space-y-3">
              {/* OVERVIEW */}
              {txTab === "overview" && (
                <>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="bg-[#FAFAFA] p-3 rounded"><div className="text-[9px] uppercase text-[#999] mb-1">Käufer</div><div className="font-medium">{txDetail.buyer?.display_name || "—"}</div><div className="text-[#666]">{txDetail.buyer?.email}</div><div className="text-[10px] font-mono text-[#999] mt-1">{txDetail.transaction.user_id}</div></div>
                    <div className="bg-[#FAFAFA] p-3 rounded"><div className="text-[9px] uppercase text-[#999] mb-1">Zahlung</div><div className="font-medium">{Number(txDetail.transaction.amount || 0).toFixed(2)} {txDetail.transaction.currency || "EUR"}</div><div className="text-[#666]">{txDetail.transaction.payment_method} · {PS_LABEL[txDetail.transaction.payment_status] || "—"}</div></div>
                  </div>
                  {txDetail.transaction.last_webhook_error && (
                    <div className="bg-red-50 border border-red-200 rounded p-3 text-xs">
                      <div className="font-bold text-red-800 mb-1">⚠️ Grant-Fehler</div>
                      <div className="text-red-700">{txDetail.transaction.last_webhook_error.message}</div>
                      <button data-testid="abo-detail-retry-btn" onClick={() => retryGrant(txDetail.transaction.tx_id)} disabled={retrying === txDetail.transaction.tx_id} className="mt-2 h-7 px-3 text-[10px] font-medium border border-blue-300 text-blue-700 hover:bg-blue-50 rounded disabled:opacity-50">{retrying === txDetail.transaction.tx_id ? "…" : "🔄 Grant erneut auslösen"}</button>
                    </div>
                  )}
                  {(txDetail.orders || []).length > 0 && (
                    <div data-testid="abo-detail-orders">
                      <div className="text-[9px] uppercase text-[#999] mb-1">Bestellungen ({txDetail.orders.length})</div>
                      <ul className="space-y-1">
                        {txDetail.orders.map((o) => (
                          <li key={o.order_id} className="text-[11px] bg-[#FAFAFA] rounded p-2 flex items-center justify-between gap-2">
                            <div className="min-w-0"><p className="font-medium truncate">{o.title || o.order_id}</p><p className="text-[10px] font-mono text-[#999] truncate">{o.order_id}</p></div>
                            <div className="text-right shrink-0"><p className="font-semibold">{Number(o.price || 0).toFixed(2)} {o.currency}</p><p className="text-[10px] text-[#999]">{o.area_scope}</p></div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div>
                    <div className="text-[9px] uppercase text-[#999] mb-1">Metadata</div>
                    <pre className="bg-[#F5F5F5] rounded p-2 text-[10px] overflow-x-auto">{JSON.stringify(txDetail.transaction.metadata || {}, null, 2)}</pre>
                  </div>
                </>
              )}

              {/* DOCUMENTS */}
              {txTab === "documents" && (
                <div data-testid="abo-detail-documents">
                  {(txDetail.documents || []).length === 0 ? (
                    <p className="text-xs text-[#999]" data-testid="abo-detail-documents-empty">Noch keine Dokumente zu dieser Transaktion.</p>
                  ) : (
                    <div className="divide-y divide-[#F0F0F0] bg-white border border-[#E5E5E5] rounded">
                      {txDetail.documents.map((d) => (
                        <div key={d.document_id} className="py-1.5 px-2 flex items-center gap-2" data-testid={`abo-document-${d.document_id}`}>
                          <div className="flex-1 min-w-0"><p className="text-xs font-semibold truncate">{d.document_type}</p><p className="text-[10px] text-[#888] truncate">{d.document_number} · {d.filename}</p></div>
                          <button data-testid={`abo-document-download-${d.document_id}`} onClick={() => downloadTxDoc(d.document_id, d.filename)} className="h-7 px-2 text-[10px] border border-[#0A0A0A] hover:bg-[#F5F5F5]">PDF</button>
                          <button data-testid={`abo-document-resend-${d.document_id}`} onClick={() => resendTxDoc(d.document_id)} className="h-7 px-2 text-[10px] bg-[#F5F5F5] border border-[#DDD] hover:bg-[#E5E5E5]">Re-Send</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* AUDIT */}
              {txTab === "audit" && (
                <div data-testid="abo-detail-audit">
                  {(txDetail.history || []).length === 0 ? <p className="text-xs text-[#999]">Keine Einträge.</p> : (
                    <ul className="space-y-1">
                      {txDetail.history.map((h, i) => (
                        <li key={i} className="text-[11px] bg-[#FAFAFA] rounded p-2"><span className="font-mono text-[#999]">{(h.created_at || "").slice(0,19)}</span> · <span className="font-medium">{h.event}</span> {h.admin_id && <span className="text-[#999]">by {String(h.admin_id).slice(0,8)}</span>}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── O-1: Admin Order Detail Modal ──────────────────────
function OrderDetailModal({ orderId, onClose, onReload }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview"); // overview | items | refunds | notes | audit
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Item-cancel state
  const [cancelItem, setCancelItem] = useState(null); // {item_id, price}
  const [cancelReason, setCancelReason] = useState("");
  const [cancelAmount, setCancelAmount] = useState("");

  // Refund state
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");

  // Note state
  const [noteText, setNoteText] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/orders/${orderId}`);
      setData(d);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [orderId]);
  useEffect(() => { load(); }, [load]);

  const submitItemCancel = async () => {
    if (!cancelItem || !cancelReason.trim()) {
      setError("Grund pflichtfeld."); return;
    }
    setBusy(true); setError("");
    try {
      const payload = { reason: cancelReason.trim() };
      if (cancelAmount !== "") payload.refund_amount = parseFloat(cancelAmount);
      await api.post(`/admin/orders/${orderId}/items/${cancelItem.item_id}/cancel`, payload);
      setCancelItem(null); setCancelReason(""); setCancelAmount("");
      await load(); onReload?.();
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  const submitRefund = async () => {
    const amt = parseFloat(refundAmount);
    if (!Number.isFinite(amt) || amt <= 0) { setError("Betrag muss > 0 sein."); return; }
    if (!refundReason.trim()) { setError("Grund pflichtfeld."); return; }
    setBusy(true); setError("");
    try {
      await api.post(`/admin/orders/${orderId}/refund`, { amount: amt, reason: refundReason.trim() });
      setRefundAmount(""); setRefundReason("");
      await load(); onReload?.();
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  const submitNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true); setError("");
    try {
      await api.post(`/admin/orders/${orderId}/note`, { text: noteText.trim() });
      setNoteText("");
      await load();
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  if (loading || !data) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
        <div className="bg-white p-6 w-64 text-center"><Spinner /></div>
      </div>
    );
  }

  const o = data.order;
  const items = data.items || [];
  const credits = data.credit_notes || [];
  const notes = data.admin_notes || [];
  const audit = data.audit || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div data-testid="order-detail-modal" className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white border border-[#E5E5E5] shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-base font-bold text-[#0A0A0A] flex items-center gap-2">
              <CreditCard size={16} /> {o.title}
            </h3>
            <p className="text-[10px] font-mono text-[#999]">{o.order_id}</p>
          </div>
          <button onClick={onClose} className="text-[#999]"><X size={18} /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#E5E5E5] sticky top-[60px] bg-white z-10">
          {[
            ["overview", "Übersicht"],
            ["items", `Items (${items.length})`],
            ["refunds", `Gutschriften (${credits.length})`],
            ["documents", `Dokumente (${(data.documents || []).length})`],
            ["notes", `Notizen (${notes.length})`],
            ["audit", `Audit (${audit.length})`],
          ].map(([key, label]) => (
            <button key={key} data-testid={`order-tab-${key}`} onClick={() => setTab(key)}
              className={`h-9 px-3 text-xs border-b-2 ${tab === key ? "border-[#0A0A0A] text-[#0A0A0A] font-medium" : "border-transparent text-[#999]"}`}>
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mx-4 mt-3 p-2 bg-red-50 border border-red-200">
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}

        <div className="px-4 py-3 space-y-3">
          {/* OVERVIEW */}
          {tab === "overview" && (
            <>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-[#999]">Status:</span> <span data-testid="order-detail-status-badge" className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-medium ${orderStatusColor(o.status)}`}>{orderStatusLabel(o.status)}</span></div>
                <div><span className="text-[#999]">Zahlung:</span> <span data-testid="order-detail-payment-badge" className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-medium ${orderStatusColor(o.payment_status || o.payment_method)}`}>{paymentStatusLabel(o.payment_status) || o.payment_method || "—"}</span></div>
                <div><span className="text-[#999]">Käufer:</span> <strong>{o.buyer_type === "GUEST" ? `Gast (${o.guest_email || ""})` : (data.buyer_profile?.display_name || o.buyer_id?.slice(0,12))}</strong></div>
                <div><span className="text-[#999]">Verkäufer:</span> <strong>{data.seller_profile?.display_name || o.seller_id?.slice(0,12)}</strong></div>
                <div><span className="text-[#999]">Bereich:</span> <strong>{o.area_scope}</strong></div>
                <div><span className="text-[#999]">Versand:</span> <strong>{o.shipping_mode}</strong></div>
                <div><span className="text-[#999]">Erstellt:</span> <strong>{new Date(o.created_at).toLocaleString("de-DE")}</strong></div>
                <div><span className="text-[#999]">Aktualisiert:</span> <strong>{new Date(o.updated_at).toLocaleString("de-DE")}</strong></div>
              </div>

              {/* iter88.31 · Sonderwunsch aus MP-Verhandlung */}
              {o.special_request && (
                <div data-testid="order-detail-special-request" className="p-3 border border-amber-300 bg-amber-50 space-y-1">
                  <p className="text-[10px] font-bold text-amber-900 uppercase">Bestätigter Sonderwunsch</p>
                  <p className="text-sm text-amber-900 whitespace-pre-line">&bdquo;{o.special_request}&ldquo;</p>
                  {o.negotiated_price != null && (
                    <p className="text-[11px] text-amber-800">Vereinbarter Preis: <strong>{Number(o.negotiated_price).toFixed(2)} {o.currency}</strong></p>
                  )}
                </div>
              )}

              {/* iter88.31 · Add-ons + extra Tragetage */}
              {(o.selected_addons?.length > 0 || o.selected_wear_days) && (
                <div data-testid="order-detail-addons" className="p-3 border border-[#E5E5E5] bg-white">
                  <p className="text-[10px] font-bold text-[#999] uppercase mb-2">Extras & Sonderwünsche</p>
                  {(o.selected_addons || []).map((a) => (
                    <div key={a.code} className="flex justify-between text-xs py-0.5">
                      <span>{a.code.replace(/^OPT_/, "").replaceAll("_", " ").toLowerCase()}</span>
                      <span className="font-mono">+{Number(a.price).toFixed(2)}</span>
                    </div>
                  ))}
                  {o.selected_wear_days && (
                    <div className="flex justify-between text-xs py-0.5">
                      <span>+ {o.selected_wear_days.days} Tag(e) extra tragen</span>
                      <span className="font-mono">+{Number(o.selected_wear_days.price).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* iter88.31 · Lieferadresse Käufer (flat fields aus Order) */}
              {(o.shipping_address || o.shipping_name || o.packstation_number) && (
                <div data-testid="order-detail-shipping-address" className="p-3 border border-[#E5E5E5] bg-[#FAFAFA]">
                  <p className="text-[10px] font-bold text-[#999] uppercase mb-1">Lieferadresse Käufer</p>
                  <div className="text-xs text-[#0A0A0A] space-y-0.5">
                    {o.shipping_name && <p><strong>{o.shipping_name}</strong></p>}
                    {o.packstation_number ? (
                      <>
                        <p>Packstation {o.packstation_number}</p>
                        {o.post_number && <p>PostNr {o.post_number}</p>}
                      </>
                    ) : null}
                    {o.shipping_address && <p>{o.shipping_address}</p>}
                    {(o.shipping_zip || o.shipping_city) && (
                      <p>{[o.shipping_zip, o.shipping_city].filter(Boolean).join(" ")}</p>
                    )}
                    {o.shipping_country && <p>{o.shipping_country}</p>}
                    {o.buyer_email && (
                      <p className="text-[10px] text-[#999] pt-1">E-Mail: {o.buyer_email}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Versand-Status */}
              {(o.shipping_status || o.tracking_number) && (
                <div data-testid="order-detail-shipping-status" className="p-3 border border-[#E5E5E5] bg-white text-xs">
                  <p className="text-[10px] font-bold text-[#999] uppercase mb-1">Versand-Status</p>
                  <div className="grid grid-cols-2 gap-2">
                    {o.shipping_status && <div><span className="text-[#999]">Status:</span> <strong>{o.shipping_status}</strong></div>}
                    {o.tracking_number && <div><span className="text-[#999]">Tracking:</span> <strong className="font-mono">{o.tracking_number}</strong></div>}
                    {o.shipped_at && <div><span className="text-[#999]">Versendet:</span> <strong>{new Date(o.shipped_at).toLocaleString("de-DE")}</strong></div>}
                    {o.delivered_at && <div><span className="text-[#999]">Zugestellt:</span> <strong>{new Date(o.delivered_at).toLocaleString("de-DE")}</strong></div>}
                  </div>
                </div>
              )}

              <div className="p-3 bg-[#FAFAFA] border border-[#E5E5E5]">
                <div className="flex justify-between text-sm">
                  <span className="text-[#666]">Bestellwert</span><strong>{o.price?.toFixed(2)} {o.currency}</strong>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-[#666]">Bereits erstattet</span><strong className="text-amber-700">- {data.refunded_total?.toFixed(2)} {o.currency}</strong>
                </div>
                <div className="border-t border-[#E5E5E5] mt-2 pt-2 flex justify-between text-sm font-bold">
                  <span>Verbleibend</span><span data-testid="order-remaining-total">{data.remaining_total?.toFixed(2)} {o.currency}</span>
                </div>
              </div>
            </>
          )}

          {/* ITEMS */}
          {tab === "items" && (
            <div className="space-y-2">
              {items.length === 0 && <p className="text-xs text-[#999]">Keine Items in dieser Bestellung.</p>}
              {items.map((it) => {
                const cancelled = it.cancel_status === "CANCELLED";
                const targetPath = it.listing_id ? (o.area_scope === "STILLTOUCHEDX" ? `/stilltouchedx/${it.listing_id}` : `/listing/${it.listing_id}`) : null;
                return (
                  <div key={it.order_item_id} data-testid={`order-item-${it.order_item_id}`} className={`p-3 border ${cancelled ? "bg-[#FAFAFA] border-[#E5E5E5] opacity-60" : "bg-white border-[#E5E5E5]"}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        {targetPath ? (
                          <a
                            href={targetPath}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`order-item-link-${it.order_item_id}`}
                            className="text-sm font-medium text-[#0A0A0A] flex items-center gap-2 hover:underline"
                          >
                            {it.title}
                            <ExternalLink size={12} className="text-[#999]" />
                            {cancelled && <span className="text-[9px] uppercase bg-red-50 text-red-600 border border-red-200 px-1.5 py-0.5">storniert</span>}
                          </a>
                        ) : (
                          <p className="text-sm font-medium text-[#0A0A0A] flex items-center gap-2">
                            {it.title}
                            {cancelled && <span className="text-[9px] uppercase bg-red-50 text-red-600 border border-red-200 px-1.5 py-0.5">storniert</span>}
                          </p>
                        )}
                        <p className="text-[10px] font-mono text-[#999]">{it.order_item_id.slice(0,12)}</p>
                        {cancelled && it.cancel_reason && <p className="text-[10px] text-red-600 mt-1">Grund: {it.cancel_reason}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold">{it.price?.toFixed(2)} {o.currency}</p>
                        {!cancelled && (o.status !== "CANCELLED" && o.status !== "REFUNDED") && (
                          <button data-testid={`order-item-cancel-${it.order_item_id}`} onClick={() => { setCancelItem({ item_id: it.order_item_id, price: it.price }); setCancelAmount(it.price?.toFixed(2) || ""); setCancelReason(""); }} className="mt-1 h-7 px-2 border border-red-300 text-red-600 text-[10px] hover:bg-red-50">
                            Stornieren
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Cancel-Item Inline-Form */}
              {cancelItem && (
                <div className="p-3 bg-amber-50 border border-amber-300 space-y-2">
                  <p className="text-xs font-bold text-amber-900">Item stornieren – automatische Gutschrift</p>
                  <div>
                    <label className="text-[10px] text-[#999] uppercase block mb-1">Gutschrift-Betrag ({o.currency})</label>
                    <input
                      data-testid="cancel-item-amount"
                      type="number" step="0.01" min="0"
                      value={cancelAmount}
                      onChange={(e) => setCancelAmount(e.target.value)}
                      className="w-full h-9 px-2 border border-amber-300 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#999] uppercase block mb-1">Grund (Pflicht)</label>
                    <textarea
                      data-testid="cancel-item-reason"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      rows={2}
                      placeholder="z.B. Defekt vor Versand, Käufer-Rückzieher"
                      className="w-full px-2 py-1 border border-amber-300 text-sm"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setCancelItem(null); setCancelReason(""); setError(""); }} className="flex-1 h-9 border border-[#E5E5E5] text-xs">Abbrechen</button>
                    <button data-testid="cancel-item-confirm" onClick={submitItemCancel} disabled={busy || !cancelReason.trim()} className="flex-1 h-9 bg-amber-600 text-white text-xs disabled:opacity-50">
                      {busy ? "..." : "Stornieren + Gutschrift erstellen"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* DOKUMENTE (iter265 · eigener Tab, alle Bestellarten) */}
          {tab === "documents" && (
            <div className="space-y-2" data-testid="admin-order-documents">
              {(!Array.isArray(data.documents) || data.documents.length === 0) ? (
                <p className="text-xs text-[#999]" data-testid="admin-order-documents-empty">Noch keine Dokumente zu dieser Bestellung.</p>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-bold text-[#0A0A0A]">Dokumente ({data.documents.length})</p>
                    <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/orders/${orderId}/documents.zip`}
                       data-testid="admin-order-documents-zip" target="_blank" rel="noreferrer"
                       className="h-7 px-2 text-[10px] font-semibold bg-[#0A0A0A] text-white flex items-center gap-1 hover:bg-[#222]">
                      Alle als ZIP
                    </a>
                  </div>
                  <div className="divide-y divide-[#F0F0F0] bg-white border border-[#E5E5E5]">
                    {data.documents.map((d) => (
                      <div key={d.document_id} className="py-1.5 px-2 flex items-center gap-2"
                           data-testid={`admin-order-document-${d.document_id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold truncate">{d.document_type}</p>
                          <p className="text-[10px] text-[#888] truncate">{d.document_number} · {d.filename}</p>
                        </div>
                        <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/documents/${d.document_id}/download`}
                           target="_blank" rel="noreferrer"
                           data-testid={`admin-order-document-download-${d.document_id}`}
                           className="h-7 px-2 text-[10px] border border-[#0A0A0A] hover:bg-[#F5F5F5]">PDF</a>
                        <button data-testid={`admin-order-document-resend-${d.document_id}`}
                          onClick={async () => { try { await api.post(`/documents/${d.document_id}/resend`); alert("E-Mail erneut versendet."); } catch (e) { alert("Versand fehlgeschlagen: " + (e?.response?.data?.detail || e.message)); } }}
                          className="h-7 px-2 text-[10px] bg-[#F5F5F5] border border-[#DDD] hover:bg-[#E5E5E5]">Re-Send</button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* REFUNDS / CREDIT NOTES */}
          {tab === "refunds" && (
            <div className="space-y-2">
              {credits.length === 0 && <p className="text-xs text-[#999]">Noch keine Gutschriften.</p>}
              {credits.map((c) => (
                <div key={c.credit_note_id} className="p-3 bg-white border border-[#E5E5E5]">
                  <div className="flex justify-between">
                    <div>
                      <p className="text-sm font-medium">{c.kind === "ITEM_CANCEL" ? "Item-Storno" : c.kind === "FULL_REFUND" ? "Voll-Erstattung" : "Teil-Erstattung"}</p>
                      <p className="text-[10px] text-[#999] font-mono">{c.credit_note_id.slice(0,12)}</p>
                      <p className="text-xs text-[#666] mt-1">{c.reason}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-amber-700">- {c.amount?.toFixed(2)} {c.currency}</p>
                      <p className="text-[10px] text-[#999] mt-1">{new Date(c.created_at).toLocaleString("de-DE")}</p>
                    </div>
                  </div>
                </div>
              ))}


              {(o.status !== "CANCELLED" && o.status !== "REFUNDED" && data.remaining_total > 0) && (
                <div className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] space-y-2">
                  <p className="text-xs font-bold text-[#0A0A0A]">Neue Gutschrift / Erstattung</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-[#999] uppercase block mb-1">Betrag ({o.currency})</label>
                      <input data-testid="refund-amount" type="number" step="0.01" min="0.01" max={data.remaining_total}
                        value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)}
                        className="w-full h-9 px-2 border border-[#E5E5E5] text-sm" />
                      <p className="text-[10px] text-[#999] mt-0.5">max {data.remaining_total?.toFixed(2)} {o.currency}</p>
                    </div>
                    <div>
                      <label className="text-[10px] text-[#999] uppercase block mb-1">Grund</label>
                      <input data-testid="refund-reason" type="text" value={refundReason} onChange={(e) => setRefundReason(e.target.value)}
                        placeholder="z.B. Versandkosten-Erstattung"
                        className="w-full h-9 px-2 border border-[#E5E5E5] text-sm" />
                    </div>
                  </div>
                  <button data-testid="refund-submit" onClick={submitRefund} disabled={busy || !refundAmount || !refundReason}
                    className="h-9 px-3 bg-[#0A0A0A] text-white text-xs disabled:opacity-50">
                    Gutschrift erstellen
                  </button>
                </div>
              )}
            </div>
          )}

          {/* NOTES */}
          {tab === "notes" && (
            <div className="space-y-2">
              {notes.length === 0 && <p className="text-xs text-[#999]">Noch keine Notizen.</p>}
              {notes.map((n) => (
                <div key={n.note_id} className="p-3 bg-white border border-[#E5E5E5]">
                  <p className="text-xs text-[#0A0A0A] whitespace-pre-wrap">{n.text}</p>
                  <p className="text-[10px] text-[#999] mt-1">{n.author_email} · {new Date(n.created_at).toLocaleString("de-DE")}</p>
                </div>
              ))}
              <div className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] space-y-2">
                <textarea data-testid="order-note-text" value={noteText} onChange={(e) => setNoteText(e.target.value)}
                  rows={3} maxLength={2000} placeholder="Interne Notiz (sichtbar nur für Admins)"
                  className="w-full px-2 py-1 border border-[#E5E5E5] text-sm" />
                <button data-testid="order-note-submit" onClick={submitNote} disabled={busy || !noteText.trim()}
                  className="h-9 px-3 bg-[#0A0A0A] text-white text-xs disabled:opacity-50">
                  Notiz hinzufügen
                </button>
              </div>
            </div>
          )}

          {/* AUDIT */}
          {tab === "audit" && (
            <div className="space-y-1.5">
              {audit.length === 0 && <p className="text-xs text-[#999]">Keine Audit-Einträge.</p>}
              {audit.map((a, i) => (
                <div key={i} className="p-2 bg-white border border-[#E5E5E5] flex justify-between text-[11px]">
                  <div>
                    <p className="font-mono text-[#0A0A0A]">{a.event}</p>
                    {a.reason && <p className="text-[#666] mt-0.5">{a.reason}</p>}
                    {a.admin_note && <p className="text-[#666] mt-0.5 italic">{a.admin_note}</p>}
                  </div>
                  <p className="text-[#999] whitespace-nowrap">{new Date(a.created_at).toLocaleString("de-DE")}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Q-1: Shipping SLA Queue Section ────────────────────
const CATEGORY_LABELS = {
  MP_STANDARD: { de: "Marketplace · Standard", color: "bg-blue-50 border-blue-200 text-blue-700" },
  STX_DISCRETE: { de: "stilltouchedX · Diskret", color: "bg-purple-50 border-purple-200 text-purple-700" },
  RESCUE: { de: "Rescue (Eilig)", color: "bg-orange-50 border-orange-200 text-orange-700" },
};

function ShippingQueueSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [openCategory, setOpenCategory] = useState(null); // null = all expanded
  const [detailOrderId, setDetailOrderId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = onlyOverdue ? "?only_overdue=true" : "";
      const { data: d } = await api.get(`/admin/shipping-queue${params}`);
      setData(d);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [onlyOverdue]);
  useEffect(() => { load(); }, [load]);

  const totalAll = (data?.groups || []).reduce((acc, g) => acc + g.total, 0);
  const totalOverdue = (data?.groups || []).reduce((acc, g) => acc + g.overdue_count, 0);

  return (
    <div data-testid="admin-shipping-queue">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-[#0A0A0A] flex items-center gap-2"><Clock size={20} /> Lieferzeiten-Queue</h1>
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-1.5 text-xs text-[#666] cursor-pointer">
            <input data-testid="queue-only-overdue" type="checkbox" checked={onlyOverdue} onChange={(e) => setOnlyOverdue(e.target.checked)} className="w-3.5 h-3.5" />
            Nur überfällige
          </label>
          <button onClick={load} className="h-8 px-3 border border-[#E5E5E5] text-xs hover:bg-[#F5F5F5]">Aktualisieren</button>
        </div>
      </div>

      {/* Top-Level Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="p-3 bg-white border border-[#E5E5E5]">
          <p className="text-[10px] text-[#999] uppercase">Gesamt offen</p>
          <p className="text-2xl font-bold text-[#0A0A0A]" data-testid="queue-total">{totalAll}</p>
        </div>
        <div className="p-3 bg-white border border-[#E5E5E5]">
          <p className="text-[10px] text-[#999] uppercase">Überfällig</p>
          <p className="text-2xl font-bold text-red-600" data-testid="queue-overdue-total">{totalOverdue}</p>
        </div>
        {(data?.groups || []).map((g) => (
          <div key={g.category} className={`p-3 border ${g.overdue_count > 0 ? "bg-red-50 border-red-200" : "bg-white border-[#E5E5E5]"}`}>
            <p className="text-[10px] text-[#999] uppercase">{CATEGORY_LABELS[g.category]?.de || g.category}</p>
            <p className="text-2xl font-bold text-[#0A0A0A]">{g.total} <span className={`text-xs ${g.overdue_count ? "text-red-600" : "text-[#999]"}`}>({g.overdue_count} überfällig)</span></p>
            <p className="text-[10px] text-[#999] mt-1">SLA: {g.sla_days} Tage</p>
          </div>
        ))}
      </div>

      {/* Filter by category */}
      <div className="flex gap-2 mb-3 flex-wrap">
        <button onClick={() => setOpenCategory(null)} className={`h-8 px-3 text-xs border ${!openCategory ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5]"}`}>Alle Kategorien</button>
        {(data?.groups || []).map((g) => (
          <button key={g.category} onClick={() => setOpenCategory(g.category)}
            data-testid={`queue-cat-${g.category}`}
            className={`h-8 px-3 text-xs border ${openCategory === g.category ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5]"}`}>
            {CATEGORY_LABELS[g.category]?.de || g.category} ({g.total})
          </button>
        ))}
      </div>

      {loading && <Spinner />}

      {!loading && (data?.groups || []).filter((g) => !openCategory || g.category === openCategory).map((g) => (
        <div key={g.category} className="mb-5">
          <div className={`flex items-center justify-between px-3 py-2 border ${CATEGORY_LABELS[g.category]?.color || "bg-[#FAFAFA] border-[#E5E5E5]"}`}>
            <div className="flex items-center gap-2">
              <p className="text-sm font-bold">{CATEGORY_LABELS[g.category]?.de || g.category}</p>
              <span className="text-[10px]">SLA: {g.sla_days} Tage</span>
            </div>
            <div className="flex gap-3 text-xs">
              <span>{g.total} offen</span>
              {g.overdue_count > 0 && (
                <span className="text-red-700 font-semibold flex items-center gap-1">
                  <AlertTriangle size={11} /> {g.overdue_count} überfällig
                </span>
              )}
            </div>
          </div>
          {g.items.length === 0 ? (
            <p className="px-3 py-4 text-xs text-[#999] bg-white border border-t-0 border-[#E5E5E5]">Keine offenen Bestellungen in dieser Kategorie.</p>
          ) : (
            <div className="bg-white border border-t-0 border-[#E5E5E5] overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Bestellung</th>
                  <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Käufer</th>
                  <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Verkäufer</th>
                  <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Versand</th>
                  <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Status</th>
                  <th className="text-right px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Tage</th>
                  <th className="text-right px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Aktion</th>
                </tr></thead>
                <tbody className="divide-y divide-[#F0F0F0]">
                  {g.items.map((it) => (
                    <tr key={it.order_id} data-testid={`queue-row-${it.order_id}`} className={`hover:bg-[#FAFAFA] ${it.is_overdue ? "bg-red-50/40" : ""}`}>
                      <td className="px-3 py-2">
                        <p className="text-sm font-medium text-[#0A0A0A] truncate max-w-[180px]">{it.title}</p>
                        <p className="text-[10px] font-mono text-[#999]">{it.order_id.slice(0,12)}</p>
                      </td>
                      <td className="px-3 py-2 text-xs text-[#666]">{it.buyer_display}</td>
                      <td className="px-3 py-2 text-xs text-[#666]">{it.seller_display}</td>
                      <td className="px-3 py-2 text-[11px] text-[#666]">{it.shipping_mode === "ANONYMOUS_HUB" ? "Anonymous Hub" : "Direct"}</td>
                      <td className="px-3 py-2"><span className={`px-2 py-0.5 text-[9px] font-medium border rounded-full ${orderStatusColor(it.status)}`}>{adminStatusLabel(it.status)}</span></td>
                      <td className="px-3 py-2 text-right">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold border ${it.is_overdue ? "border-red-300 bg-red-100 text-red-700" : "border-[#E5E5E5] bg-white text-[#666]"}`}>
                          {it.is_overdue && <AlertTriangle size={10} />}
                          {it.days_elapsed}d{it.is_overdue ? ` (+${it.overdue_by_days})` : ""}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          data-testid={`queue-detail-${it.order_id}`}
                          onClick={() => setDetailOrderId(it.order_id)}
                          className="h-7 px-2 border border-[#E5E5E5] text-[10px] text-[#0A0A0A] hover:bg-[#F5F5F5]"
                        >
                          Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {detailOrderId && (
        <OrderDetailModal
          orderId={detailOrderId}
          onClose={() => setDetailOrderId(null)}
          onReload={() => load()}
        />
      )}
    </div>
  );
}

// ─── I-1: STX Inactivity-Sweep Section ──────────────────
// ─── MEDIA-ADMIN: extracted to /app/frontend/src/components/admin/MediaAdminSection.js (iter29) ───


function InactivitySection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmRun, setConfirmRun] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/inactivity/preview");
      setData(d);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runSweep = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/inactivity/run");
      setLastResult(r);
      setConfirmRun(false);
      await load();
    } catch (e) {
      setLastResult({ ok: false, error: formatError(e) });
    } finally {
      setBusy(false);
    }
  };

  if (loading || !data) return <Spinner />;

  const t = data.thresholds_days;
  const stages = [
    { key: "stage_1_pending", label: `Stage 1 – Erinnerung (>${t.reminder_1}d inaktiv)`, items: data.stage_1_pending, color: "border-amber-200 bg-amber-50 text-amber-700" },
    { key: "stage_2_pending", label: `Stage 2 – Letzte Warnung (>${t.reminder_2}d inaktiv)`, items: data.stage_2_pending, color: "border-orange-200 bg-orange-50 text-orange-700" },
    { key: "stage_3_pending", label: `Stage 3 – Auto-Delete (>${t.auto_delete}d inaktiv)`, items: data.stage_3_pending, color: "border-red-300 bg-red-50 text-red-700" },
  ];
  const totalActions = stages.reduce((acc, s) => acc + s.items.length, 0);

  return (
    <div data-testid="admin-inactivity">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-[#0A0A0A] flex items-center gap-2"><AlertTriangle size={20} /> STX Inaktivitäts-Sweep</h1>
        <div className="flex gap-2">
          <button onClick={load} className="h-8 px-3 border border-[#E5E5E5] text-xs hover:bg-[#F5F5F5]">Aktualisieren</button>
          <button
            data-testid="inactivity-run-btn"
            onClick={() => setConfirmRun(true)}
            disabled={totalActions === 0}
            className="h-8 px-3 bg-[#0A0A0A] text-white text-xs disabled:opacity-50"
          >
            Sweep ausführen ({totalActions})
          </button>
        </div>
      </div>

      <div className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] mb-4 text-xs text-[#666] leading-relaxed">
        <p>Der Scheduler läuft automatisch alle 5 Minuten. Hier siehst du nur AVS-verifizierte STX-User
          (<code className="bg-white px-1">age_verification_status = VERIFIED</code>, <code className="bg-white px-1">account_status = ACTIVE</code>),
          die die Inaktivitäts-Schwelle überschritten haben. Email + System-Message werden bei jeder Stage automatisch verschickt.</p>
        <p className="mt-1">Schwellen via ENV konfigurierbar: <code>STX_INACTIVITY_REMINDER_1_DAYS</code> / <code>STX_INACTIVITY_REMINDER_2_DAYS</code> / <code>STX_INACTIVITY_AUTO_DELETE_DAYS</code>.</p>
      </div>

      {lastResult && (
        <div className={`p-3 mb-4 border ${lastResult.ok ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-700"}`}>
          <p className="text-xs">
            {lastResult.ok ? `Sweep ausgeführt: ${lastResult.actions_taken} Aktion(en).` : `Fehler: ${lastResult.error}`}
          </p>
        </div>
      )}

      {stages.map((s) => (
        <div key={s.key} className="mb-5">
          <div className={`px-3 py-2 border ${s.color} flex items-center justify-between`}>
            <p className="text-sm font-bold">{s.label}</p>
            <span className="text-xs">{s.items.length} User</span>
          </div>
          {s.items.length === 0 ? (
            <p className="px-3 py-3 text-xs text-[#999] bg-white border border-t-0 border-[#E5E5E5]">Keine User in dieser Stage.</p>
          ) : (
            <div className="bg-white border border-t-0 border-[#E5E5E5] overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">User-ID</th>
                  <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Email</th>
                  <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Letzter Login</th>
                </tr></thead>
                <tbody className="divide-y divide-[#F0F0F0]">
                  {s.items.map((u) => (
                    <tr key={u.user_id} className="hover:bg-[#FAFAFA]">
                      <td className="px-3 py-2 text-[11px] font-mono text-[#666]">{u.user_id.slice(0,12)}</td>
                      <td className="px-3 py-2 text-xs">{u.email}</td>
                      <td className="px-3 py-2 text-xs text-[#666]">{u.last_login_at ? new Date(u.last_login_at).toLocaleString("de-DE") : "nie"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {/* Confirm Modal */}
      {confirmRun && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setConfirmRun(false)}>
          <div className="w-full max-w-md bg-white border border-[#E5E5E5] shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-[#E5E5E5]">
              <h3 className="text-sm font-bold">Inaktivitäts-Sweep manuell ausführen</h3>
            </div>
            <div className="px-4 py-3 space-y-2">
              <p className="text-xs text-[#666]">
                Es werden insgesamt <strong>{totalActions} Aktion(en)</strong> ausgeführt. Stage-3-User werden dabei <strong>endgültig soft-gelöscht</strong>.
              </p>
              <ul className="text-[11px] text-[#666] space-y-0.5 ml-3 list-disc">
                <li>{data.stage_1_pending.length} Erinnerungs-Email + System-Message (Stage 1)</li>
                <li>{data.stage_2_pending.length} Letzte-Warnung-Email + System-Message (Stage 2)</li>
                <li>{data.stage_3_pending.length} Auto-Soft-Delete (Stage 3)</li>
              </ul>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setConfirmRun(false)} className="flex-1 h-9 border border-[#E5E5E5] text-sm">Abbrechen</button>
                <button data-testid="inactivity-run-confirm" onClick={runSweep} disabled={busy}
                  className="flex-1 h-9 bg-[#0A0A0A] text-white text-sm disabled:opacity-50">
                  {busy ? "..." : "Jetzt ausführen"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── AVS Requests Admin ─────────────────────────────────
function AVSRequestsSection() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/avs-requests"); setRequests(data.requests || []); } catch { /* ignore */ } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const decide = async (reqId, decision) => {
    try { await api.post(`/admin/avs-requests/${reqId}/decide`, { decision }); load(); } catch { /* ignore */ }
  };

  if (loading) return <Spinner />;
  return (
    <div data-testid="admin-avs-requests">
      <h1 className="text-xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-2"><Shield size={20} /> AVS-Anträge</h1>
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-[#FAFAFA]">
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">User</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">E-Mail</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Datum</th>
            <th className="text-left px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Status</th>
            <th className="text-right px-3 py-2 text-[10px] font-bold text-[#999] uppercase">Aktion</th>
          </tr></thead>
          <tbody className="divide-y divide-[#F0F0F0]">
            {requests.length === 0 ? <tr><td colSpan={5} className="py-8 text-center text-[#999]">Keine AVS-Anträge.</td></tr> :
             requests.map((r) => (
              <tr key={r.avs_request_id} className="hover:bg-[#FAFAFA]">
                <td className="px-3 py-2 text-[11px] font-mono text-[#666]">{r.user_id?.slice(0,8)}</td>
                <td className="px-3 py-2 text-xs">{r.email}</td>
                <td className="px-3 py-2 text-xs text-[#999]">{new Date(r.created_at).toLocaleDateString("de-DE")}</td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 text-[10px] font-medium border rounded-full ${orderStatusColor(r.status)}`}>{adminStatusLabel(r.status)}</span></td>
                <td className="px-3 py-2 text-right">
                  {r.status === "PENDING" && <div className="flex gap-1 justify-end">
                    <button onClick={() => decide(r.avs_request_id, "VERIFIED")} className="h-7 px-2 bg-green-600 text-white text-[10px] font-medium">Bestätigen</button>
                    <button onClick={() => decide(r.avs_request_id, "REJECTED")} className="h-7 px-2 bg-red-600 text-white text-[10px] font-medium">Ablehnen</button>
                  </div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Payouts / Gutschriften ─────────────────────────────
function PayoutsSection() {
  const [tab, setTab] = useState("eligible"); // eligible | history
  const [eligible, setEligible] = useState({ entries: [], grouped_by_seller: [] });
  const [history, setHistory] = useState([]);
  const [historyFilter, setHistoryFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [selectedSellers, setSelectedSellers] = useState(new Set());
  const [detailPayout, setDetailPayout] = useState(null);
  const [detailOrders, setDetailOrders] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [eligibleDetail, setEligibleDetail] = useState(null);
  const [search, setSearch] = useState("");
  const [nestedAdminOrderId, setNestedAdminOrderId] = useState(null);  // Öffnet Admin-Order-Modal

  const flash = (type, text, ms = 4000) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), ms);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [elig, hist] = await Promise.all([
        api.get("/admin/payouts/eligible"),
        api.get("/admin/payouts"),
      ]);
      setEligible(elig.data);
      setHistory(hist.data.payouts || []);
    } catch (e) {
      flash("error", formatError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleSelect = (sellerId) => {
    setSelectedSellers(prev => {
      const nx = new Set(prev);
      if (nx.has(sellerId)) nx.delete(sellerId); else nx.add(sellerId);
      return nx;
    });
  };

  const releaseSelected = async () => {
    const groups = (eligible.grouped_by_seller || []).filter(g => selectedSellers.has(g.seller_id));
    if (groups.length === 0) {
      flash("error", "Bitte mindestens einen Verkäufer auswählen");
      return;
    }
    const entryIds = groups.flatMap(g => g.entry_ids);
    if (!window.confirm(`Wirklich ${groups.length} Payout(s) über ${groups.reduce((s, g) => s + g.total_amount, 0).toFixed(2)} EUR freigeben?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payouts/release", { entry_ids: entryIds });
      const paid = (data.results || []).filter(r => r.status === "PAID_OUT").length;
      const manual = (data.results || []).filter(r => r.status === "PENDING_MANUAL").length;
      const failed = (data.results || []).filter(r => r.status === "FAILED").length;
      flash("success", `${paid} ausgezahlt · ${manual} manuell · ${failed} fehlgeschlagen`);
      setSelectedSellers(new Set());
      load();
    } catch (e) {
      flash("error", formatError(e));
    } finally { setBusy(false); }
  };

  const runAuto = async () => {
    if (!window.confirm("Auto-Run: Alle PAYOUT_ELIGIBLE-Einträge freigeben, deren 3-Werktage-Frist bereits vergangen ist?\n(In Produktion: 20:00 CET täglicher Cron)")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payouts/auto-run");
      flash("success", data.message || "Auto-Run abgeschlossen");
      load();
    } catch (e) { flash("error", formatError(e)); }
    finally { setBusy(false); }
  };

  const markPaid = async (payoutId) => {
    const ref = window.prompt("Externe Referenz (optional, z. B. Bank-Buchungs-ID):", "");
    if (ref === null) return;
    setBusy(true);
    try {
      await api.post(`/admin/payouts/${payoutId}/mark-paid`, { external_ref: ref });
      flash("success", "Als PAID_OUT markiert");
      load();
      if (detailPayout) setDetailPayout(null);
    } catch (e) { flash("error", formatError(e)); }
    finally { setBusy(false); }
  };

  const failPayout = async (payoutId) => {
    const reason = window.prompt("Grund des Fehlschlags:", "");
    if (!reason) return;
    setBusy(true);
    try {
      await api.post(`/admin/payouts/${payoutId}/fail`, { reason });
      flash("success", "Als FAILED markiert; Einträge wieder eligible");
      load();
      if (detailPayout) setDetailPayout(null);
    } catch (e) { flash("error", formatError(e)); }
    finally { setBusy(false); }
  };

  const openEligibleDetail = async (group) => {
    setDetailLoading(true);
    setEligibleDetail({ seller_id: group.seller_id, seller: group.seller, entries: [], total_amount: 0 });
    try {
      const { data } = await api.get(`/admin/payouts/eligible-detail?seller_id=${encodeURIComponent(group.seller_id)}`);
      setEligibleDetail({
        seller_id: group.seller_id,
        seller: group.seller,
        entries: data.entries || [],
        total_amount: data.total_amount || 0,
      });
    } catch (e) { flash("error", formatError(e)); }
    finally { setDetailLoading(false); }
  };

  const openPayoutDetail = async (payout) => {
    setDetailPayout(payout);
    setDetailOrders([]);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/payouts/${payout.payout_id}/orders`);
      setDetailOrders(data.entries || []);
    } catch { /* soft-fail — modal still shows */ }
    finally { setDetailLoading(false); }
  };

  if (loading) return <Spinner />;

  // Suche filtert Eligible-Gruppen + History-Einträge
  const q = search.trim().toLowerCase();
  const matchesQ = (haystack) => haystack.some(v => v && String(v).toLowerCase().includes(q));
  const filteredEligible = !q ? (eligible.grouped_by_seller || []) :
    (eligible.grouped_by_seller || []).filter(g => matchesQ([
      g.seller?.display_name, g.seller?.email, g.seller_id,
      ...(g.order_ids || []), ...(g.order_titles || []),
    ]));

  const totalEligible = (eligible.grouped_by_seller || []).reduce((s, g) => s + g.total_amount, 0);
  const historyByStatus = historyFilter === "ALL" ? history : history.filter(p => p.status === historyFilter);
  const filteredHistory = !q ? historyByStatus :
    historyByStatus.filter(p => matchesQ([
      p.seller_display_name, p.seller_email, p.seller_id, p.payout_id, p.external_ref,
      ...(p.order_ids || []),
    ]));

  const statusBadge = {
    PAID_OUT: "bg-green-50 text-green-700 border-green-200",
    PENDING_MANUAL: "bg-amber-50 text-amber-700 border-amber-200",
    PAYING_OUT: "bg-blue-50 text-blue-700 border-blue-200",
    FAILED: "bg-red-50 text-red-700 border-red-200",
  };

  return (
    <div data-testid="admin-payouts-section">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-[#0A0A0A]">Gutschriften</h1>
          <p className="text-xs text-[#666] mt-1">Freigabe & Auszahlung an Verkäufer. Auto-Trigger 20:00 CET · 3 Werktage nach Hub-Eingang oder Zustellung (Direktlieferung).</p>
        </div>
        <button onClick={runAuto} disabled={busy}
          data-testid="payouts-auto-run"
          className="h-9 px-3 border border-[#0A0A0A] text-[#0A0A0A] text-xs font-medium hover:bg-[#0A0A0A] hover:text-white flex items-center gap-1.5 disabled:opacity-50">
          <RefreshCw size={14} /> Auto-Run testen
        </button>
      </div>

      {msg.text && (
        <div className={`mb-4 px-3 py-2 border text-sm ${msg.type === "success" ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-700"}`}>
          {msg.text}
        </div>
      )}

      <div className="flex gap-1.5 mb-4">
        <button onClick={() => setTab("eligible")}
          data-testid="payouts-tab-eligible"
          className={`h-9 px-4 text-xs font-medium border ${tab === "eligible" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] text-[#666]"}`}>
          Freigabe-fähig ({(eligible.grouped_by_seller || []).length})
        </button>
        <button onClick={() => setTab("history")}
          data-testid="payouts-tab-history"
          className={`h-9 px-4 text-xs font-medium border ${tab === "history" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] text-[#666]"}`}>
          Historie ({history.length})
        </button>
      </div>

      {/* Suchfeld */}
      <div className="mb-4 relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suche nach Verkäufer (Name/E-Mail) oder Bestellnummer …"
          data-testid="payouts-search"
          className="w-full h-10 pl-9 pr-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
        />
        {q && (
          <button onClick={() => setSearch("")}
            data-testid="payouts-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 px-2 text-[10px] text-[#666] hover:bg-[#F5F5F5]">
            Zurücksetzen
          </button>
        )}
      </div>

      {/* Eligible Tab */}
      {tab === "eligible" && (
        <>
          <div className="mb-3 flex items-center justify-between p-3 bg-[#FAFAFA] border border-[#E5E5E5] text-xs">
            <div>
              <span className="font-medium">Ausstehendes Volumen:</span>{" "}
              <span className="font-mono">{totalEligible.toFixed(2)} EUR</span>
              {" · "}
              <span className="font-medium">Ausgewählt:</span>{" "}
              <span className="font-mono">{selectedSellers.size}</span>
            </div>
            <button onClick={releaseSelected} disabled={busy || selectedSellers.size === 0}
              data-testid="payouts-release-selected"
              className="h-8 px-3 bg-[#0A0A0A] text-white text-xs font-medium disabled:opacity-50 flex items-center gap-1">
              <Check size={13} /> Auswahl freigeben
            </button>
          </div>

          <div className="bg-white border border-[#E5E5E5]">
            {filteredEligible.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-[#999]">
                {q ? `Keine Treffer für „${search}".` : "Keine freigabe-fähigen Einträge."}
              </div>
            )}
            {filteredEligible.map(g => {
              const selected = selectedSellers.has(g.seller_id);
              const notReady = !g.seller.payout_ready;
              return (
                <div key={g.seller_id}
                  data-testid={`payouts-eligible-row-${g.seller_id}`}
                  className={`flex items-center justify-between px-4 py-3 border-b border-[#F0F0F0] last:border-0 gap-3 ${selected ? "bg-[#F5F5F5]" : "hover:bg-[#FAFAFA]"}`}>
                  <input type="checkbox" checked={selected} onChange={() => toggleSelect(g.seller_id)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`payouts-select-${g.seller_id}`} />
                  <button
                    type="button"
                    onClick={() => openEligibleDetail(g)}
                    data-testid={`payouts-eligible-open-${g.seller_id}`}
                    className="flex-1 min-w-0 text-left cursor-pointer">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-[#0A0A0A] hover:underline">{g.seller.display_name || g.seller_id}</p>
                      {notReady ? (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold border rounded bg-amber-50 border-amber-200 text-amber-700">
                          Auszahlungsmethode fehlt
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold border rounded bg-blue-50 border-blue-200 text-blue-700">
                          {g.seller.payout_method}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-[#999] mt-0.5">
                      {g.count} Einträge · {g.seller.email} · älteste: {g.oldest_eligible_at?.slice(0, 10) || "–"}
                    </p>
                  </button>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-[#0A0A0A] font-mono">{g.total_amount.toFixed(2)} EUR</p>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* History Tab */}
      {tab === "history" && (
        <>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {["ALL", "PAID_OUT", "PENDING_MANUAL", "PAYING_OUT", "FAILED"].map(s => (
              <button key={s} onClick={() => setHistoryFilter(s)}
                data-testid={`payouts-filter-${s}`}
                className={`h-8 px-3 border text-xs font-medium ${historyFilter === s ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] text-[#666] hover:bg-[#F5F5F5]"}`}>
                {s === "ALL" ? "Alle" : s}
                <span className="ml-1.5 opacity-60">
                  {s === "ALL" ? history.length : history.filter(p => p.status === s).length}
                </span>
              </button>
            ))}
          </div>

          <div className="bg-white border border-[#E5E5E5]">
            {filteredHistory.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-[#999]">
                {q ? `Keine Treffer für „${search}".` : "Keine Payouts."}
              </div>
            )}
            {filteredHistory.map(p => (
              <div key={p.payout_id}
                data-testid={`payouts-history-row-${p.payout_id}`}
                onClick={() => openPayoutDetail(p)}
                className="flex items-center justify-between px-4 py-3 border-b border-[#F0F0F0] last:border-0 hover:bg-[#FAFAFA] gap-3 cursor-pointer">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-[#0A0A0A]">{p.seller_display_name || p.seller_id}</p>
                    <span className={`px-1.5 py-0.5 text-[9px] font-bold border rounded ${statusBadge[p.status] || "bg-gray-100 border-gray-200 text-gray-700"}`}>
                      {p.status}
                    </span>
                    <span className="px-1.5 py-0.5 text-[9px] font-medium border rounded bg-white border-[#E5E5E5] text-[#666]">
                      {p.method}
                    </span>
                    {p.trigger === "AUTO" && (
                      <span className="px-1.5 py-0.5 text-[9px] font-medium border rounded bg-purple-50 border-purple-200 text-purple-700">
                        AUTO
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-[#999] mt-0.5 font-mono">
                    {p.payout_id} · {p.order_count} Bestellungen · {p.created_at?.slice(0, 16).replace("T", " ")}
                  </p>
                  {p.message && <p className="text-[10px] text-[#666] mt-0.5 truncate">{p.message}</p>}
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-[#0A0A0A] font-mono">{Number(p.amount).toFixed(2)} {p.currency}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Detail-Modal Payout-Historie */}
      {detailPayout && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setDetailPayout(null); setDetailOrders([]); }}>
          <div onClick={(e) => e.stopPropagation()} data-testid="payout-detail-modal"
            className="bg-white border border-[#E5E5E5] max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5]">
              <h3 className="text-base font-bold text-[#0A0A0A]">Payout Details</h3>
              <button onClick={() => { setDetailPayout(null); setDetailOrders([]); }} className="h-7 w-7 flex items-center justify-center hover:bg-[#F5F5F5]">
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm">
              <DL label="Payout ID" value={detailPayout.payout_id} className="font-mono text-xs" />
              <DL label="Verkäufer" value={`${detailPayout.seller_display_name} (${detailPayout.seller_email})`} />
              <DL label="Betrag" value={`${Number(detailPayout.amount).toFixed(2)} ${detailPayout.currency}`} />
              <DL label="Methode" value={`${detailPayout.method} → ${detailPayout.destination}`} />
              <DL label="Status" value={detailPayout.status} />
              <DL label="Trigger" value={detailPayout.trigger || "MANUAL"} />
              <DL label="Bestellungen" value={detailPayout.order_count} />
              <DL label="Erstellt" value={detailPayout.created_at?.replace("T", " ").slice(0, 19)} />
              {detailPayout.released_at && <DL label="Freigegeben" value={detailPayout.released_at.replace("T", " ").slice(0, 19)} />}
              {detailPayout.external_ref && <DL label="Externe Ref" value={detailPayout.external_ref} className="font-mono text-xs" />}
              {detailPayout.message && <DL label="Meldung" value={detailPayout.message} />}
              {detailPayout.failure_reason && <DL label="Fehler-Grund" value={detailPayout.failure_reason} />}
            </div>
            {/* Order-Übersicht */}
            <div className="px-5 py-3 border-t border-[#E5E5E5]">
              <p className="text-[10px] font-bold text-[#999] uppercase tracking-wider mb-2">Enthaltene Bestellungen</p>
              {detailLoading ? (
                <p className="text-xs text-[#999] py-3">Lade …</p>
              ) : detailOrders.length === 0 ? (
                <p className="text-xs text-[#999] py-3">Keine Bestellungen gefunden.</p>
              ) : (
                <div className="border border-[#E5E5E5] max-h-64 overflow-y-auto">
                  {detailOrders.map((e, i) => (
                    <div key={e.entry_id || i} className="flex items-center justify-between px-3 py-2 border-b border-[#F0F0F0] last:border-0 text-xs">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-[#0A0A0A] truncate">{e.order?.title || "—"}</p>
                        <p className="text-[10px] text-[#999] font-mono">
                          {e.order?.order_id?.slice(0, 8)} · {e.order?.area_scope || "—"} · {e.order?.buyer || "—"}
                        </p>
                        {e.order?.delivered_at && (
                          <p className="text-[10px] text-[#666]">zugestellt: {e.order.delivered_at.slice(0, 10)}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <div className="text-right">
                          <p className="font-mono text-[#0A0A0A]">{Number(e.amount || 0).toFixed(2)} {e.currency || "EUR"}</p>
                          {e.fee_amount && <p className="text-[10px] text-[#999] font-mono">-{Number(e.fee_amount).toFixed(2)} Gebühr</p>}
                        </div>
                        {e.order?.order_id && (
                          <button
                            onClick={() => setNestedAdminOrderId(e.order.order_id)}
                            data-testid={`payout-history-open-order-${e.order.order_id}`}
                            title="Bestellung öffnen"
                            className="h-7 w-7 flex items-center justify-center border border-[#E5E5E5] hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] transition-colors">
                            <ExternalLink size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-[#E5E5E5] flex gap-2 flex-wrap">
              {(detailPayout.status === "PENDING_MANUAL" || detailPayout.status === "FAILED" || detailPayout.status === "PAYING_OUT") && (
                <button onClick={() => markPaid(detailPayout.payout_id)} disabled={busy}
                  data-testid="payout-detail-mark-paid"
                  className="h-9 px-3 bg-[#0A0A0A] text-white text-xs font-medium disabled:opacity-50">
                  Als PAID_OUT markieren
                </button>
              )}
              {detailPayout.status !== "PAID_OUT" && detailPayout.status !== "FAILED" && (
                <button onClick={() => failPayout(detailPayout.payout_id)} disabled={busy}
                  data-testid="payout-detail-fail"
                  className="h-9 px-3 border border-red-500 text-red-500 text-xs font-medium hover:bg-red-50 disabled:opacity-50">
                  Als FAILED markieren
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail-Modal Eligible-Gruppe (klickbar aus Tab 1) */}
      {eligibleDetail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEligibleDetail(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="payout-eligible-detail-modal"
            className="bg-white border border-[#E5E5E5] max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5]">
              <h3 className="text-base font-bold text-[#0A0A0A]">
                Gutschriften – {eligibleDetail.seller?.display_name || eligibleDetail.seller_id}
              </h3>
              <button onClick={() => setEligibleDetail(null)} className="h-7 w-7 flex items-center justify-center hover:bg-[#F5F5F5]">
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between text-sm">
              <div>
                <p className="text-[10px] font-bold text-[#999] uppercase tracking-wider">Summe</p>
                <p className="text-lg font-bold text-[#0A0A0A] font-mono">{eligibleDetail.total_amount.toFixed(2)} EUR</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold text-[#999] uppercase tracking-wider">Auszahlungsmethode</p>
                <p className="text-xs text-[#666]">{eligibleDetail.seller?.payout_method || "—"}</p>
              </div>
            </div>
            <div className="px-5 py-3">
              {detailLoading && eligibleDetail.entries.length === 0 ? (
                <p className="text-xs text-[#999] py-3">Lade Bestellungen …</p>
              ) : eligibleDetail.entries.length === 0 ? (
                <p className="text-xs text-[#999] py-3">Keine Einträge gefunden.</p>
              ) : (
                <div className="border border-[#E5E5E5]">
                  {eligibleDetail.entries.map((e, i) => (
                    <div key={e.entry_id || i}
                      data-testid={`payout-eligible-item-${e.entry_id}`}
                      className="flex items-center justify-between px-3 py-2.5 border-b border-[#F0F0F0] last:border-0 text-xs hover:bg-[#FAFAFA]">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-[#0A0A0A] truncate">{e.order?.title || "—"}</p>
                        <p className="text-[10px] text-[#999] font-mono mt-0.5">
                          {e.order?.order_id?.slice(0, 8) || "—"} · {e.order?.area_scope || "—"} · {e.order?.buyer || "—"}
                        </p>
                        <div className="flex gap-2 mt-0.5">
                          {e.source_flow && (
                            <span className="text-[9px] px-1 py-px border rounded bg-white border-[#E5E5E5] text-[#666]">
                              {e.source_flow === "HUB" ? "Hub" : "Direkt"}
                            </span>
                          )}
                          {e.payout_eligible_at && (
                            <span className="text-[10px] text-[#666]">
                              freigabe: {e.payout_eligible_at.slice(0, 10)}
                            </span>
                          )}
                          {e.order?.delivered_at && (
                            <span className="text-[10px] text-[#999]">
                              zugestellt: {e.order.delivered_at.slice(0, 10)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <div className="text-right">
                          <p className="font-mono text-[#0A0A0A]">{Number(e.amount || 0).toFixed(2)} {e.currency || "EUR"}</p>
                          {e.fee_amount && <p className="text-[10px] text-[#999] font-mono">-{Number(e.fee_amount).toFixed(2)} Gebühr</p>}
                        </div>
                        {e.order?.order_id && (
                          <button
                            onClick={() => setNestedAdminOrderId(e.order.order_id)}
                            data-testid={`payout-eligible-open-order-${e.order.order_id}`}
                            title="Bestellung öffnen"
                            className="h-7 w-7 flex items-center justify-center border border-[#E5E5E5] hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] transition-colors">
                            <ExternalLink size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Nested Admin-Order-Detail-Modal (aus Payout-Detail-Modals) */}
      {nestedAdminOrderId && (
        <OrderDetailModal
          orderId={nestedAdminOrderId}
          onClose={() => setNestedAdminOrderId(null)}
        />
      )}
    </div>
  );
}

// ─── Email Templates ────────────────────────────────────
function EmailTemplatesSection() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template_code | null
  const [detail, setDetail] = useState(null);   // full template doc
  const [form, setForm] = useState({ subject: "", body_html: "", body_text: "", is_active: true });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [category, setCategory] = useState("ALL");
  const [previewHtml, setPreviewHtml] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const flash = (type, text, ms = 3000) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), ms);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-templates");
      setTemplates(data.templates || []);
    } catch {
      flash("error", "Konnte Templates nicht laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openEdit = async (code) => {
    try {
      const { data } = await api.get(`/admin/email-templates/${code}`);
      setDetail(data);
      setForm({
        subject: data.subject || "",
        body_html: data.body_html || "",
        body_text: data.body_text || "",
        is_active: data.is_active !== false,
      });
      setEditing(code);
      setPreviewHtml("");
    } catch (e) {
      flash("error", formatError(e));
    }
  };

  const closeEdit = () => {
    setEditing(null);
    setDetail(null);
    setPreviewHtml("");
    setTestEmail("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/email-templates/${editing}`, form);
      flash("success", "Template gespeichert");
      load();
    } catch (e) {
      flash("error", formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const renderPreview = async () => {
    if (!editing) return;
    try {
      // Erst speichern, dann preview mit sample_variables aus Detail
      const { data } = await api.post(`/admin/email-templates/${editing}/preview`, {
        variables: detail?.sample_variables || {},
      });
      setPreviewHtml(data.body_html || "");
    } catch (e) {
      flash("error", formatError(e));
    }
  };

  const sendTest = async () => {
    if (!editing || !testEmail || !testEmail.includes("@")) {
      flash("error", "Gültige E-Mail-Adresse angeben");
      return;
    }
    setSendingTest(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${editing}/test-send`, {
        to_email: testEmail,
        variables: detail?.sample_variables || {},
      });
      if (data?.result?.sent) {
        flash("success", `Test-E-Mail an ${testEmail} versendet`);
      } else {
        flash("error", `Nicht versendet: ${data?.result?.reason || "unbekannt"}`);
      }
    } catch (e) {
      flash("error", formatError(e));
    } finally {
      setSendingTest(false);
    }
  };

  const reseed = async () => {
    try {
      const { data } = await api.post(`/admin/email-templates/reseed-missing`);
      flash("success", `${data.count} fehlende Templates angelegt`);
      load();
    } catch (e) {
      flash("error", formatError(e));
    }
  };

  if (loading) return <Spinner />;

  // ─── Editor-View ─────────────────────────────────────
  if (editing && detail) {
    const catBadge = {
      TRANSACTIONAL: "bg-blue-50 text-blue-700 border-blue-200",
      ACCOUNT: "bg-emerald-50 text-emerald-700 border-emerald-200",
      SYSTEM: "bg-amber-50 text-amber-700 border-amber-200",
      ADMIN: "bg-purple-50 text-purple-700 border-purple-200",
    }[detail.category] || "bg-gray-50 text-gray-700 border-gray-200";

    return (
      <div className="space-y-4" data-testid="email-template-editor">
        <button onClick={closeEdit} className="h-8 px-3 border border-[#E5E5E5] text-xs text-[#666] flex items-center gap-1"
          data-testid="email-template-back">
          <ChevronLeft size={14} /> Zurück zur Übersicht
        </button>

        {msg.text && (
          <div className={`px-3 py-2 border text-sm ${msg.type === "success" ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-700"}`}>
            {msg.text}
          </div>
        )}

        <div className="bg-white border border-[#E5E5E5] p-5 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-bold text-[#0A0A0A]">{detail.label || detail.template_code}</h3>
                <span className={`px-2 py-0.5 text-[10px] font-medium border rounded ${catBadge}`}>{detail.category || "—"}</span>
                {detail.is_system && (
                  <span className="px-2 py-0.5 text-[10px] font-bold border rounded bg-red-50 border-red-200 text-red-700">SYSTEM</span>
                )}
              </div>
              <p className="text-xs text-[#666] mt-1">{detail.description}</p>
              <p className="text-[10px] text-[#999] mt-1 font-mono">{detail.template_code} · SMTP-Rolle: {detail.role_code}</p>
            </div>
            <label className="inline-flex items-center gap-2 text-xs text-[#666]">
              <input type="checkbox" checked={!!form.is_active}
                onChange={(e) => setForm(p => ({ ...p, is_active: e.target.checked }))}
                data-testid="email-template-active" />
              Aktiv (wird versendet)
            </label>
          </div>

          {(detail.variables || []).length > 0 && (
            <div className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] text-xs">
              <p className="font-medium text-[#666] uppercase tracking-wider mb-1.5">Verfügbare Variablen</p>
              <div className="flex flex-wrap gap-1.5">
                {(detail.variables || []).map(v => (
                  <code key={v} className="px-1.5 py-0.5 bg-white border border-[#E5E5E5] text-[11px] font-mono text-[#0A0A0A]">{`{{${v}}}`}</code>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Betreff</label>
            <input value={form.subject} onChange={(e) => setForm(p => ({ ...p, subject: e.target.value }))}
              data-testid="email-template-subject"
              className="mt-1 w-full h-10 px-3 border border-[#E5E5E5] text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" />
          </div>

          <div>
            <label className="text-xs font-medium text-[#666] uppercase tracking-wider">HTML-Inhalt (WYSIWYG)</label>
            <div className="mt-1">
              <RichTextEditor
                value={form.body_html}
                onChange={(html) => setForm(p => ({ ...p, body_html: html }))}
                placeholder="Text der E-Mail hier eingeben. Variablen wie {{display_name}} werden beim Versand ersetzt."
                testId="email-template-body"
                minHeight="280px"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Text-Inhalt (Fallback, optional)</label>
            <textarea value={form.body_text || ""}
              onChange={(e) => setForm(p => ({ ...p, body_text: e.target.value }))}
              data-testid="email-template-body-text"
              placeholder="Wird generiert falls leer. Nur nötig bei speziellen Plaintext-Anforderungen."
              className="mt-1 w-full h-24 px-3 py-2 border border-[#E5E5E5] text-xs font-mono bg-white focus:outline-none resize-y" />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={save} disabled={saving}
              data-testid="email-template-save"
              className="h-10 px-6 bg-[#0A0A0A] text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
              <Save size={14} /> Speichern
            </button>
            <button onClick={renderPreview}
              data-testid="email-template-preview"
              className="h-10 px-4 border border-[#E5E5E5] text-[#0A0A0A] text-sm font-medium flex items-center gap-1.5 hover:bg-[#F5F5F5]">
              Vorschau (mit Beispieldaten)
            </button>
          </div>
        </div>

        {/* Preview */}
        {previewHtml && (
          <div className="bg-white border border-[#E5E5E5] p-5" data-testid="email-template-preview-panel">
            <p className="text-xs font-medium text-[#666] uppercase tracking-wider mb-3">Vorschau (Rendering mit sample_variables)</p>
            <div className="border border-[#E5E5E5] p-4 text-sm bg-white" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        )}

        {/* Test-Versand */}
        <div className="bg-white border border-[#E5E5E5] p-5 space-y-3">
          <p className="text-xs font-medium text-[#666] uppercase tracking-wider">Test-Versand</p>
          <div className="flex gap-2 flex-wrap items-center">
            <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@example.com"
              type="email"
              data-testid="email-template-test-to"
              className="flex-1 min-w-[220px] h-10 px-3 border border-[#E5E5E5] text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" />
            <button onClick={sendTest} disabled={sendingTest || !testEmail.includes("@")}
              data-testid="email-template-test-send"
              className="h-10 px-4 border border-[#0A0A0A] text-[#0A0A0A] text-sm font-medium flex items-center gap-1.5 hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50">
              {sendingTest ? "Wird gesendet …" : "Test-E-Mail senden"}
            </button>
          </div>
          <p className="text-[10px] text-[#999]">Verwendet Beispieldaten (sample_variables) und den konfigurierten SMTP-Account der Rolle „{detail.role_code}&ldquo;.</p>
        </div>
      </div>
    );
  }

  // ─── Liste ──────────────────────────────────────────
  const CATEGORIES = [
    { key: "ALL", label: "Alle" },
    { key: "TRANSACTIONAL", label: "Transaktional" },
    { key: "ACCOUNT", label: "Account" },
    { key: "SYSTEM", label: "System" },
    { key: "ADMIN", label: "Admin" },
  ];
  const filtered = category === "ALL" ? templates : templates.filter(t => (t.category || "ADMIN") === category);

  return (
    <div data-testid="admin-email-templates">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-[#0A0A0A]">E-Mail-Vorlagen</h1>
          <p className="text-xs text-[#666] mt-1">Verwaltung aller Systemmails. Änderungen greifen sofort für neue E-Mail-Versände.</p>
        </div>
        <button onClick={reseed}
          data-testid="email-templates-reseed"
          className="h-9 px-3 border border-[#E5E5E5] text-xs text-[#666] hover:bg-[#F5F5F5] flex items-center gap-1.5">
          <RefreshCw size={14} /> Fehlende Templates neu anlegen
        </button>
      </div>

      {msg.text && (
        <div className={`mb-4 px-3 py-2 border text-sm ${msg.type === "success" ? "bg-green-50 border-green-200 text-green-700" : "bg-red-50 border-red-200 text-red-700"}`}>
          {msg.text}
        </div>
      )}

      {/* iter276 · Zentrale Marken-Balken-Feineinstellung für E-Mails */}
      <div className="mb-5">
        <BrandBarSettings scope="email" />
      </div>

      <div className="mb-3 flex flex-wrap gap-1.5">
        {CATEGORIES.map(c => (
          <button key={c.key} onClick={() => setCategory(c.key)}
            data-testid={`email-templates-cat-${c.key}`}
            className={`h-8 px-3 border text-xs font-medium transition-colors ${
              category === c.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] text-[#666] hover:bg-[#F5F5F5]"
            }`}>
            {c.label}
            {c.key !== "ALL" && (
              <span className="ml-1.5 opacity-60">
                {templates.filter(t => (t.category || "ADMIN") === c.key).length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="bg-white border border-[#E5E5E5]">
        {filtered.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-[#999]">Keine Templates in dieser Kategorie.</div>
        )}
        {filtered.map((t) => (
          <div key={t.template_code}
            data-testid={`email-template-row-${t.template_code}`}
            className="flex items-center justify-between px-4 py-3 border-b border-[#F0F0F0] last:border-0 hover:bg-[#FAFAFA] gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-medium text-[#0A0A0A]">{t.label || t.template_code}</p>
                {t.is_system && (
                  <span className="px-1.5 py-0.5 text-[9px] font-bold border rounded bg-red-50 border-red-200 text-red-700">SYSTEM</span>
                )}
                {t.is_active === false && (
                  <span className="px-1.5 py-0.5 text-[9px] font-bold border rounded bg-gray-100 border-gray-200 text-gray-500">INAKTIV</span>
                )}
              </div>
              <p className="text-xs text-[#999] mt-0.5 truncate">Betreff: {t.subject || "—"}</p>
              <p className="text-[10px] text-[#bbb] mt-0.5 font-mono">{t.template_code}</p>
            </div>
            <button onClick={() => openEdit(t.template_code)}
              data-testid={`email-template-edit-${t.template_code}`}
              className="h-8 px-3 bg-[#0A0A0A] text-white text-xs font-medium hover:bg-[#222]">
              Bearbeiten
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Shared Components ──────────────────────────────────
function FieldInput({ label, value, onChange, type = "text", placeholder = "", secret = false }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{label}</label>
      <div className="relative mt-1">
        <input type={secret && !show ? "password" : type} value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full h-10 px-3 border border-[#E5E5E5] text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder={placeholder} />
        {secret && <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-2.5 text-[#999]">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>}
      </div>
    </div>
  );
}

function Spinner() {
  return <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;
}

// Phase 3 · Musik-Slot · zeigt aktiven Provider (lokal/Mubert) + Beispiel-Katalog.
function MusicSlotSection() {
  const [status, setStatus] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);
  const [error, setError] = useState("");
  const audioRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, c] = await Promise.all([api.get("/music/status"), api.get("/music/catalog?limit=20")]);
        setStatus(s.data);
        setCatalog(c.data);
      } catch (e) { setError("Musik-Daten konnten nicht geladen werden."); }
      finally { setLoading(false); }
    })();
    return () => { if (audioRef.current) audioRef.current.pause(); };
  }, []);

  const toggle = (track) => {
    if (!track.preview_url) return;
    if (playing === track.id) { audioRef.current?.pause(); setPlaying(null); return; }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(track.preview_url);
    a.volume = 0.6;
    a.play().catch(() => {});
    a.onended = () => setPlaying(null);
    audioRef.current = a;
    setPlaying(track.id);
  };

  const isMubert = status?.provider === "mubert";

  return (
    <div data-testid="admin-music-slot">
      <h1 className="text-xl font-bold text-[#0A0A0A] mb-1 flex items-center gap-2"><Music2 size={20} /> Musik-Slot</h1>
      <p className="text-sm text-[#999] mb-4">Hintergrundmusik für Beiträge &amp; Storys. Ohne Mubert-Key läuft der lokale Beispiel-Katalog.</p>

      {loading ? <Spinner /> : error ? (
        <div data-testid="admin-music-error" className="bg-red-50 border border-red-200 text-red-700 text-sm p-4 rounded">{error}</div>
      ) : (
        <>
          {/* Provider-Status */}
          <div className="bg-white border border-[#E5E5E5] p-4 mb-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] text-[#999] uppercase tracking-wider font-medium">Aktiver Provider</p>
                <p className="text-lg font-bold text-[#0A0A0A] mt-0.5 flex items-center gap-2">
                  {isMubert ? "Mubert" : "Lokaler Beispiel-Katalog"}
                  <span className={`px-2 py-0.5 text-[10px] font-medium border rounded-full ${isMubert ? "text-green-700 border-green-300 bg-green-50" : "text-amber-700 border-amber-300 bg-amber-50"}`}>
                    {isMubert ? "Aktiv" : "Demo"}
                  </span>
                </p>
              </div>
              <div className={`w-11 h-11 rounded-full flex items-center justify-center ${isMubert ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>
                <Music2 size={20} />
              </div>
            </div>
            {!isMubert && (
              <div className="mt-3 p-3 bg-[#FAFAFA] border border-[#F0F0F0] text-[12px] text-[#555] leading-relaxed">
                <p className="font-semibold text-[#0A0A0A] mb-1">Mubert aktivieren (später)</p>
                <p>Trage die Zugangsdaten als Server-Secrets ein — danach schaltet der Slot automatisch auf echte Mubert-Tracks um. Der Browser erhält die Credentials nie.</p>
                <ul className="mt-2 space-y-0.5 list-disc list-inside font-mono text-[11px]">
                  <li>MUBERT_CUSTOMER_ID</li>
                  <li>MUBERT_ACCESS_TOKEN</li>
                </ul>
              </div>
            )}
          </div>

          {/* Katalog-Vorschau */}
          <div className="bg-white border border-[#E5E5E5] p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-[#0A0A0A]">Katalog-Vorschau</h3>
              <span className="text-[11px] text-[#999]">{catalog?.tracks?.length || 0} Tracks</span>
            </div>
            <div className="space-y-2">
              {(catalog?.tracks || []).map((tr) => (
                <div key={tr.id} data-testid={`admin-music-track-${tr.id}`} className="flex items-center gap-3 p-2 bg-[#FAFAFA] border border-[#F0F0F0]">
                  <button onClick={() => toggle(tr)} disabled={!tr.preview_url}
                    data-testid={`admin-music-play-${tr.id}`}
                    aria-label={playing === tr.id ? `Pause ${tr.title}` : `Play ${tr.title}`}
                    title={playing === tr.id ? "Pause" : "Play"}
                    className="w-9 h-9 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center shrink-0 disabled:opacity-30 hover:bg-[#222]">
                    {playing === tr.id ? <span className="text-xs">❚❚</span> : <span className="text-xs ml-0.5">▶</span>}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#0A0A0A] truncate">{tr.title}</p>
                    <p className="text-[11px] text-[#999] truncate">{tr.artist || "—"} · {tr.mood || ""} {tr.genre ? `· ${tr.genre}` : ""}</p>
                  </div>
                  <span className="text-[10px] text-[#999] shrink-0">{tr.duration}s</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}


// iter88.18: STX Konfiguration Wrapper mit 3 Sub-Tabs (Versand · BodyDetection · Add-Ons)
function STXKonfigurationSection() {
  const [tab, setTab] = useState("shipping");
  const TABS = [
    { key: "shipping", label: "Versand" },
    { key: "body_detection", label: "Body-Detection" },
    { key: "addons", label: "Add-Ons" },
    { key: "fan_level", label: "Fan-Level" },
  ];
  return (
    <div data-testid="stx-konfiguration">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-[#0A0A0A]">STX Konfiguration</h2>
        <p className="text-xs text-[#666] mt-0.5">Zentrale Einstellungen für den stilltouchedX-Bereich.</p>
      </div>
      <div className="flex gap-2 mb-6 border-b border-[#E5E5E5]">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`stx-konfig-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 ${
              tab === t.key ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-[#666] hover:text-[#0A0A0A]"
            }`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "shipping" && <STXShippingConfigSection />}
      {tab === "body_detection" && <BodyDetectionSection />}
      {tab === "addons" && <STXShopAddonConfigSection />}
      {tab === "fan_level" && <FanLevelConfigSection />}
    </div>
  );
}

// iter88.18: Versand-Konfiguration pro Kategorie
function STXShippingConfigSection() {
  const [cfg, setCfg] = useState(null);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [{ data: c }, { data: cs }] = await Promise.all([
          api.get("/admin/stx-shipping-config"),
          api.get("/stilltouchedx/catalog/categories"),
        ]);
        setCfg({
          per_category: c.per_category || {},
          default: c.default || { tiers: [], free_from_items: null, free_from_amount_eur: null },
        });
        // Backend returns `tree`, jede Node hat {category_code, label, gender, subcategories}
        const catList = (cs.tree || cs.categories || []).map((t) => ({
          code: t.category_code || t.code,
          label: t.label,
        }));
        setCats(catList);
      } catch (e) {
        setError("Konnte Versand-Config nicht laden");
      }
    })();
  }, []);

  const upsertCategory = (catCode, rule) => {
    setCfg((prev) => ({ ...prev, per_category: { ...prev.per_category, [catCode]: rule } }));
  };

  const save = async () => {
    setSaving(true); setError(""); setFlash("");
    try {
      await api.put("/admin/stx-shipping-config", { ...cfg, merge_per_category: false });
      setFlash("Gespeichert");
      setTimeout(() => setFlash(""), 2500);
    } catch (e) {
      setError(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  if (!cfg) return <div className="text-sm text-[#666]">Lädt…</div>;

  return (
    <div data-testid="stx-shipping-config">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-3">Versand-Standard (fallback für nicht-konfigurierte Kategorien)</p>
      <ShippingRuleEditor
        testIdPrefix="ship-default"
        rule={cfg.default}
        onChange={(rule) => setCfg({ ...cfg, default: rule })} />
      <div className="mt-6">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-3">Pro Kategorie</p>
        <div className="space-y-4">
          {cats.map((cat) => {
            const rule = cfg.per_category?.[cat.code] || { tiers: [], free_from_items: null };
            return (
              <div key={cat.code} className="border border-[#E5E5E5] rounded-xl p-3 bg-[#FAFAFA]">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold">{cat.label} <span className="text-[10px] font-mono text-[#999]">· {cat.code}</span></p>
                  {cfg.per_category?.[cat.code] && (
                    <button
                      data-testid={`ship-cat-remove-${cat.code}`}
                      onClick={() => {
                        const cp = { ...cfg.per_category };
                        delete cp[cat.code];
                        setCfg({ ...cfg, per_category: cp });
                      }}
                      className="text-[10px] text-red-600 hover:underline">
                      Standard nutzen
                    </button>
                  )}
                </div>
                <ShippingRuleEditor
                  testIdPrefix={`ship-cat-${cat.code}`}
                  rule={rule}
                  onChange={(r) => upsertCategory(cat.code, r)} />
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-6 flex items-center gap-3">
        <button data-testid="ship-save" onClick={save} disabled={saving}
          className="h-10 px-5 rounded-lg bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#333] disabled:opacity-40">
          {saving ? "Speichere…" : "Speichern"}
        </button>
        {flash && <span data-testid="ship-flash" className="text-xs text-green-600">{flash}</span>}
        {error && <span data-testid="ship-error" className="text-xs text-red-600">{error}</span>}
      </div>
    </div>
  );
}

function ShippingRuleEditor({ rule, onChange, testIdPrefix }) {
  const addTier = () => {
    const nextMax = ((rule.tiers || []).slice(-1)[0]?.max_items || 0) + 1;
    onChange({ ...rule, tiers: [...(rule.tiers || []), { max_items: nextMax, price_eur: 4.99 }] });
  };
  const updateTier = (i, patch) => {
    const t = [...(rule.tiers || [])];
    t[i] = { ...t[i], ...patch };
    onChange({ ...rule, tiers: t });
  };
  const removeTier = (i) => {
    const t = [...(rule.tiers || [])];
    t.splice(i, 1);
    onChange({ ...rule, tiers: t });
  };
  return (
    <div className="space-y-2">
      {(rule.tiers || []).map((t, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-[10px] text-[#666] w-20">Bis {t.max_items} Artikel:</span>
          <input type="number" min="1" max="999" value={t.max_items}
            data-testid={`${testIdPrefix}-max-${i}`}
            onChange={(e) => updateTier(i, { max_items: parseInt(e.target.value) || 1 })}
            className="w-16 h-8 px-2 rounded border border-[#E5E5E5] text-xs text-center" />
          <span className="text-[10px] text-[#666]">€</span>
          <input type="number" step="0.01" min="0" value={t.price_eur}
            data-testid={`${testIdPrefix}-price-${i}`}
            onChange={(e) => updateTier(i, { price_eur: parseFloat(e.target.value) || 0 })}
            className="w-20 h-8 px-2 rounded border border-[#E5E5E5] text-xs" />
          <button onClick={() => removeTier(i)} data-testid={`${testIdPrefix}-remove-${i}`}
            className="text-red-500 text-xs px-2">✕</button>
        </div>
      ))}
      <button data-testid={`${testIdPrefix}-add`} onClick={addTier}
        className="text-xs text-[#0A0A0A] underline">+ Stufe</button>
      <div className="flex items-center gap-2 pt-1">
        <span className="text-[10px] text-[#666] w-20">Frei ab:</span>
        <input type="number" min="1" placeholder="—" value={rule.free_from_items || ""}
          data-testid={`${testIdPrefix}-free-from`}
          onChange={(e) => onChange({ ...rule, free_from_items: e.target.value ? parseInt(e.target.value) : null })}
          className="w-16 h-8 px-2 rounded border border-[#E5E5E5] text-xs text-center" />
        <span className="text-[10px] text-[#666]">Artikel</span>
        <span className="text-[10px] text-[#999] px-1">oder</span>
        <input type="number" min="1" step="0.01" placeholder="—" value={rule.free_from_amount_eur || ""}
          data-testid={`${testIdPrefix}-free-from-amount`}
          onChange={(e) => onChange({ ...rule, free_from_amount_eur: e.target.value ? parseFloat(e.target.value) : null })}
          className="w-20 h-8 px-2 rounded border border-[#E5E5E5] text-xs" />
        <span className="text-[10px] text-[#666]">EUR</span>
      </div>
    </div>
  );
}

// iter88.18: Fan-Level Config Editor (minimal — Backend-Endpoint bereits vorhanden)
function FanLevelConfigSection() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState("");

  useEffect(() => {
    api.get("/admin/fan-level-config").then(({ data }) => {
      setCfg({
        NEW_FAN: data.NEW_FAN || { max_days_since_follow: 14, max_purchases: 0 },
        ACTIVE_FAN: data.ACTIVE_FAN || { min_purchases: 1, max_purchases: 3, engagement_lookback_days: 30 },
        TOP_FAN: data.TOP_FAN || { min_purchases: 4, min_total_spend_eur: 100 },
      });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true); setFlash("");
    try {
      await api.put("/admin/fan-level-config", cfg);
      setFlash("Gespeichert");
      setTimeout(() => setFlash(""), 2500);
    } finally { setSaving(false); }
  };

  const reset = async () => {
    await api.post("/admin/fan-level-config/reset");
    const { data } = await api.get("/admin/fan-level-config");
    setCfg({
      NEW_FAN: data.NEW_FAN,
      ACTIVE_FAN: data.ACTIVE_FAN,
      TOP_FAN: data.TOP_FAN,
    });
    setFlash("Zurückgesetzt");
    setTimeout(() => setFlash(""), 2500);
  };

  if (!cfg) return <div className="text-sm text-[#666]">Lädt…</div>;

  return (
    <div data-testid="fan-level-config" className="space-y-4">
      <div className="border border-[#E5E5E5] rounded-xl p-3">
        <p className="text-sm font-bold mb-2">🔥 TOP_FAN</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs">Min. Käufe
            <input type="number" min="0" value={cfg.TOP_FAN.min_purchases}
              data-testid="fan-top-purchases"
              onChange={(e) => setCfg({ ...cfg, TOP_FAN: { ...cfg.TOP_FAN, min_purchases: parseInt(e.target.value) || 0 } })}
              className="w-full mt-1 h-9 px-2 rounded border border-[#E5E5E5] text-sm" /></label>
          <label className="text-xs">Min. Ausgabe (EUR)
            <input type="number" step="0.01" min="0" value={cfg.TOP_FAN.min_total_spend_eur}
              data-testid="fan-top-spend"
              onChange={(e) => setCfg({ ...cfg, TOP_FAN: { ...cfg.TOP_FAN, min_total_spend_eur: parseFloat(e.target.value) || 0 } })}
              className="w-full mt-1 h-9 px-2 rounded border border-[#E5E5E5] text-sm" /></label>
        </div>
      </div>
      <div className="border border-[#E5E5E5] rounded-xl p-3">
        <p className="text-sm font-bold mb-2">✨ ACTIVE_FAN</p>
        <div className="grid grid-cols-3 gap-3">
          <label className="text-xs">Min. Käufe
            <input type="number" min="0" value={cfg.ACTIVE_FAN.min_purchases}
              data-testid="fan-active-min-purchases"
              onChange={(e) => setCfg({ ...cfg, ACTIVE_FAN: { ...cfg.ACTIVE_FAN, min_purchases: parseInt(e.target.value) || 0 } })}
              className="w-full mt-1 h-9 px-2 rounded border border-[#E5E5E5] text-sm" /></label>
          <label className="text-xs">Max. Käufe
            <input type="number" min="1" value={cfg.ACTIVE_FAN.max_purchases}
              data-testid="fan-active-max-purchases"
              onChange={(e) => setCfg({ ...cfg, ACTIVE_FAN: { ...cfg.ACTIVE_FAN, max_purchases: parseInt(e.target.value) || 3 } })}
              className="w-full mt-1 h-9 px-2 rounded border border-[#E5E5E5] text-sm" /></label>
          <label className="text-xs">Engag.-Fenster (d)
            <input type="number" min="1" value={cfg.ACTIVE_FAN.engagement_lookback_days || 30}
              data-testid="fan-active-lookback"
              onChange={(e) => setCfg({ ...cfg, ACTIVE_FAN: { ...cfg.ACTIVE_FAN, engagement_lookback_days: parseInt(e.target.value) || 30 } })}
              className="w-full mt-1 h-9 px-2 rounded border border-[#E5E5E5] text-sm" /></label>
        </div>
      </div>
      <div className="border border-[#E5E5E5] rounded-xl p-3">
        <p className="text-sm font-bold mb-2">🌱 NEW_FAN</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs">Max. Tage seit Follow
            <input type="number" min="1" value={cfg.NEW_FAN.max_days_since_follow}
              data-testid="fan-new-days"
              onChange={(e) => setCfg({ ...cfg, NEW_FAN: { ...cfg.NEW_FAN, max_days_since_follow: parseInt(e.target.value) || 14 } })}
              className="w-full mt-1 h-9 px-2 rounded border border-[#E5E5E5] text-sm" /></label>
          <label className="text-xs">Max. Käufe
            <input type="number" min="0" value={cfg.NEW_FAN.max_purchases}
              data-testid="fan-new-max-purchases"
              onChange={(e) => setCfg({ ...cfg, NEW_FAN: { ...cfg.NEW_FAN, max_purchases: parseInt(e.target.value) || 0 } })}
              className="w-full mt-1 h-9 px-2 rounded border border-[#E5E5E5] text-sm" /></label>
        </div>
      </div>
      <div className="flex items-center gap-3 pt-2">
        <button data-testid="fan-save" onClick={save} disabled={saving}
          className="h-10 px-5 rounded-lg bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#333] disabled:opacity-40">
          {saving ? "Speichere…" : "Speichern"}
        </button>
        <button data-testid="fan-reset" onClick={reset}
          className="h-10 px-5 rounded-lg border border-[#E5E5E5] text-sm text-[#666] hover:bg-[#F5F5F5]">
          Auf Default zurück
        </button>
        {flash && <span data-testid="fan-flash" className="text-xs text-green-600">{flash}</span>}
      </div>
    </div>
  );
}

// iter88.10 + .12: Admin STX-Shop Add-On CRUD (Enable/Disable + Rename + Public-Text + Add + Delete)
function STXShopAddonConfigSection() {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [savedFlash, setSavedFlash] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newForm, setNewForm] = useState({ code: "OPT_", label: "", public_text: "", gender: "ANY" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stx-addons");
      setAddons((data.addons || []).map((a) => ({
        ...a,
        _draft_label: a.label_override,
        _draft_public: a.public_text_override,
      })));
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const patch = async (code, payload) => {
    setSaving((s) => ({ ...s, [code]: true }));
    try {
      await api.patch(`/admin/stx-addons/${code}`, payload);
      setAddons((prev) => prev.map((a) => {
        if (a.code !== code) return a;
        const next = { ...a, ...payload };
        next.effective_label = (payload.label_override !== undefined ? payload.label_override : a.label_override) || a.default_label;
        next.effective_public_text = (payload.public_text_override !== undefined ? payload.public_text_override : a.public_text_override) || a.default_public_text;
        return next;
      }));
      setSavedFlash((s) => ({ ...s, [code]: true }));
      setTimeout(() => setSavedFlash((s) => ({ ...s, [code]: false })), 1200);
    } catch (e) { /* noop */ }
    finally { setSaving((s) => ({ ...s, [code]: false })); }
  };

  const remove = async (code, isCustom) => {
    if (!window.confirm(isCustom
      ? "Custom-Add-On endgültig löschen? Bestehende Angebote werden ihren Code weiterhin behalten."
      : "Basis-Add-On deaktivieren? Es wird aus Wizard + Public ausgeblendet, kann aber wiederhergestellt werden.")) return;
    try {
      await api.delete(`/admin/stx-addons/${code}`);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };

  const restore = async (code) => {
    try {
      await api.post(`/admin/stx-addons/${code}/restore`);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Wiederherstellen fehlgeschlagen"); }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/stx-addons", newForm);
      setNewForm({ code: "OPT_", label: "", public_text: "", gender: "ANY" });
      setShowAdd(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || "Anlegen fehlgeschlagen"); }
  };

  return (
    <div data-testid="admin-stx-shop-config" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#0A0A0A]">STX-Shop · Add-On-Verwaltung</h2>
          <p className="text-xs text-[#666] mt-1">
            Verwalte globale Add-Ons: aktivieren/deaktivieren, Label und öffentlichen Beschreibungs-Text (non-AVS) individuell anpassen, neue eigene Add-Ons anlegen oder Basis-Add-Ons entfernen.
          </p>
        </div>
        <button data-testid="admin-stx-addon-add-toggle"
          onClick={() => setShowAdd(!showAdd)}
          className="h-9 px-3 text-xs font-medium border border-[#0A0A0A] bg-[#0A0A0A] text-white hover:bg-[#1A1A1A]">
          {showAdd ? "Abbrechen" : "+ Neu"}
        </button>
      </div>

      {showAdd && (
        <form data-testid="admin-stx-addon-add-form" onSubmit={submitNew}
          className="border border-[#0A0A0A] p-4 space-y-2 bg-[#FAFAFA]">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <input data-testid="new-addon-code"
              value={newForm.code}
              onChange={(e) => setNewForm({ ...newForm, code: e.target.value.toUpperCase() })}
              placeholder="OPT_MEIN_CODE" required
              className="h-9 px-2 border border-[#E5E5E5] text-xs font-mono" />
            <input data-testid="new-addon-label"
              value={newForm.label}
              onChange={(e) => setNewForm({ ...newForm, label: e.target.value })}
              placeholder="Anzeigename" required
              className="h-9 px-2 border border-[#E5E5E5] text-xs" />
            <select data-testid="new-addon-gender"
              value={newForm.gender}
              onChange={(e) => setNewForm({ ...newForm, gender: e.target.value })}
              className="h-9 px-2 border border-[#E5E5E5] text-xs">
              <option value="ANY">Beide (ANY)</option>
              <option value="F">Nur Damen (F)</option>
              <option value="M">Nur Herren (M)</option>
            </select>
            <button type="submit" data-testid="new-addon-submit"
              className="h-9 px-3 text-xs font-medium bg-[#0A0A0A] text-white">
              Anlegen
            </button>
          </div>
          <textarea data-testid="new-addon-public-text"
            value={newForm.public_text}
            onChange={(e) => setNewForm({ ...newForm, public_text: e.target.value })}
            placeholder={'Öffentlicher Beschreibungs-Text (non-AVS) — z.B. „Wurde bei besonderen Anlässen getragen."'}
            rows={2} maxLength={240} required
            className="w-full px-2 py-1.5 border border-[#E5E5E5] text-xs" />
        </form>
      )}

      {loading ? (
        <Spinner />
      ) : (
        <div className="border border-[#E5E5E5] divide-y divide-[#E5E5E5]">
          {addons.map((a) => (
            <div key={a.code} data-testid={`admin-stx-addon-${a.code}`}
              className={`px-3 py-3 space-y-2 ${a.deleted ? "bg-[#FFF7F7]" : ""}`}>
              <div className="flex items-center gap-3">
                <input type="checkbox"
                  data-testid={`admin-stx-addon-enabled-${a.code}`}
                  checked={a.enabled !== false && !a.deleted}
                  disabled={a.deleted}
                  onChange={(e) => patch(a.code, { enabled: e.target.checked })}
                  className="w-4 h-4 accent-[#0A0A0A] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono text-[#999]">{a.code}</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-[#F5F5F5] text-[#666]">{a.gender}</span>
                    {a.is_custom && <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200">Custom</span>}
                    {a.deleted && <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 border border-red-200">Deaktiviert</span>}
                  </div>
                </div>
                {a.deleted ? (
                  <button data-testid={`admin-stx-addon-restore-${a.code}`}
                    onClick={() => restore(a.code)}
                    className="h-8 px-3 text-xs border border-green-500 text-green-700 hover:bg-green-50">
                    Wiederherstellen
                  </button>
                ) : (
                  <>
                    <button data-testid={`admin-stx-addon-save-${a.code}`}
                      onClick={() => patch(a.code, { label_override: a._draft_label || "", public_text_override: a._draft_public || "" })}
                      disabled={!!saving[a.code]}
                      className={`h-8 px-3 text-xs font-medium border transition-colors ${
                        savedFlash[a.code] ? "border-green-500 text-green-600 bg-green-50" : "border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                      }`}>
                      {saving[a.code] ? "…" : (savedFlash[a.code] ? "✓" : "Speichern")}
                    </button>
                    <button data-testid={`admin-stx-addon-delete-${a.code}`}
                      onClick={() => remove(a.code, a.is_custom)}
                      className="h-8 px-3 text-xs border border-red-300 text-red-600 hover:bg-red-50">
                      {a.is_custom ? "Löschen" : "Entfernen"}
                    </button>
                  </>
                )}
              </div>
              {!a.deleted && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pl-7">
                  <div>
                    <label className="text-[10px] text-[#999] uppercase tracking-wider">Anzeigename (Wizard/Artikel)</label>
                    <input
                      data-testid={`admin-stx-addon-label-${a.code}`}
                      value={a._draft_label || ""}
                      onChange={(e) => setAddons((prev) => prev.map((x) => x.code === a.code ? { ...x, _draft_label: e.target.value } : x))}
                      placeholder={a.default_label}
                      className="w-full h-8 px-2 border border-[#E5E5E5] text-xs" />
                    <p className="text-[9px] text-[#BBB] mt-0.5 truncate">Default: &bdquo;{a.default_label}&ldquo;</p>
                  </div>
                  <div>
                    <label className="text-[10px] text-[#999] uppercase tracking-wider">Öffentlicher Beschreibungs-Text (non-AVS)</label>
                    <textarea
                      data-testid={`admin-stx-addon-public-${a.code}`}
                      value={a._draft_public || ""}
                      onChange={(e) => setAddons((prev) => prev.map((x) => x.code === a.code ? { ...x, _draft_public: e.target.value } : x))}
                      placeholder={a.default_public_text}
                      maxLength={240} rows={2}
                      className="w-full px-2 py-1 border border-[#E5E5E5] text-xs" />
                    <p className="text-[9px] text-[#BBB] mt-0.5 truncate">Default: &bdquo;{a.default_public_text}&ldquo;</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// iter88.13: Modul O Phase 2 · Reports-Moderation
function ReportsModerationSection() {
  const [reports, setReports] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("PENDING");

  const load = async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const { data } = await api.get(`/admin/content-reports${q}`);
      setReports(data.reports || []);
      setPendingCount(data.pending_count || 0);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const resolve = async (id, status) => {
    const note = window.prompt(`Notiz (${status}) — optional:`, "") || "";
    try {
      await api.patch(`/admin/content-reports/${id}`, { status, resolution_note: note });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Fehler"); }
  };

  return (
    <div data-testid="admin-reports-section" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#0A0A0A]">Meldungen · Moderation</h2>
          <p className="text-xs text-[#666] mt-1">
            {pendingCount} offene Meldung{pendingCount === 1 ? "" : "en"} · Status: {statusFilter || "alle"}
          </p>
        </div>
        <div className="flex gap-1">
          {["PENDING", "RESOLVED", "DISMISSED", ""].map((s) => (
            <button key={s || "all"} data-testid={`admin-reports-filter-${s || "all"}`}
              onClick={() => setStatusFilter(s)}
              className={`h-8 px-3 text-xs border transition-colors ${
                statusFilter === s ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5]"
              }`}>{s || "Alle"}</button>
          ))}
        </div>
      </div>
      {loading ? (
        <Spinner />
      ) : reports.length === 0 ? (
        <div className="border border-[#E5E5E5] px-4 py-16 text-center text-sm text-[#999]">
          Keine Meldungen mit diesem Status.
        </div>
      ) : (
        <div className="border border-[#E5E5E5] divide-y divide-[#E5E5E5]">
          {reports.map((r) => (
            <div key={r.report_id} data-testid={`admin-report-${r.report_id}`} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold border rounded-full ${orderStatusColor(r.status)}`}>{adminStatusLabel(r.status)}</span>
                    <span className="text-[10px] font-mono text-[#999]">{r.target_type}</span>
                    <span className="text-[10px] font-mono text-[#666]">{r.target_id.slice(0, 12)}…</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200">{r.reason}</span>
                  </div>
                  {r.description && (
                    <p className="text-xs text-[#666] mt-1 whitespace-pre-line">{r.description}</p>
                  )}
                  <p className="text-[10px] text-[#BBB] mt-1">
                    Gemeldet von {r.reporter_id?.slice(0, 8)}… · {new Date(r.created_at).toLocaleString("de-DE")}
                    {r.resolved_at && ` · Bearbeitet ${new Date(r.resolved_at).toLocaleString("de-DE")}`}
                  </p>
                  {r.resolution_note && (
                    <p className="text-[10px] text-[#666] mt-1 italic">Notiz: {r.resolution_note}</p>
                  )}
                </div>
                {r.status === "PENDING" && (
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`admin-report-resolve-${r.report_id}`}
                      onClick={() => resolve(r.report_id, "RESOLVED")}
                      className="h-8 px-2 text-xs border border-green-500 text-green-700 hover:bg-green-50">
                      Erledigt
                    </button>
                    <button data-testid={`admin-report-dismiss-${r.report_id}`}
                      onClick={() => resolve(r.report_id, "DISMISSED")}
                      className="h-8 px-2 text-xs border border-[#E5E5E5] text-[#666] hover:bg-[#F5F5F5]">
                      Verwerfen
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

